import {readPriorCheckInAdvance,priorAdvancePayment} from '../utils/priorCheckInAdvance';
import AdvanceBookingsPanel from '../components/manager/AdvanceBookingsPanel';
import {assertAdvanceForCheckIn} from '../utils/bookingAdvance';
import JanakpuriBookingsPanel from "../components/manager/JanakpuriBookingsPanel";
import {JANAKPURI_HOTEL_ID, janakpuriCall, setJanakpuriClaim} from "../services/janakpuriBookingService";
import {captureCheckInContext, assertCheckInContext, assertNoActiveRoomStays, buildRoomDisplayRows} from "../utils/roomLifecycleGuards";
import {commitLegacyRecovery} from "../utils/legacyStayRecovery";
import FoodOrdersPanel from "../components/manager/FoodOrdersPanel";
import {placeFoodOrder} from "../services/foodOrderService";
import {restoreOccupiedRoom, restoreClosedRoom} from "../utils/restoreOccupiedRoom";
import {allocateAdRegisterSerial} from "../utils/adRegisterSerial";
import {occupyReadyRoom} from "../utils/quickRoomOccupancy";
import {allocateAdvanceCredits} from "../utils/advanceAllocation";
import {runPmsTransaction as runTransaction} from "../utils/pmsTransaction";
import {getFunctions, httpsCallable} from "firebase/functions";
import {app} from "../firebase";
import {applyStayEntry} from "../utils/stayMutation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { planBoardMutation, mergeBoardMutation, removeReadyRoom, roomKey, assertRoomLockAllowsBoard, hydrateOccupiedCards } from "../utils/roomBoardMutation";
import { calculateBill, calculatePaidTotal, roundMoney, financialFields } from "../services/billingService";
import RoomCleaningModal from "../components/manager/RoomCleaningModal";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { sendTelegram } from "../telegram";
import GuestCheckInModal from "../components/manager/GuestCheckInModal";
import OccupiedRoomModal from "../components/manager/OccupiedRoomModal";
import DayBookPanel from "../components/manager/DayBookPanel";
import GuestHistoryPanel from "../components/manager/GuestHistoryPanel";
import CheckoutSuccessModal from "../components/manager/CheckoutSuccessModal";
import MaintenancePanel from "../components/manager/MaintenancePanel";
import HousekeepingPanel from "../components/manager/HousekeepingPanel";
import ExpensePanel from "../components/manager/ExpensePanel";
import ReportsPanel from "../components/manager/ReportsPanel";
import PendingPaymentsPanel from "../components/manager/PendingPaymentsPanel";
import BookingDiaryPanel from "../components/manager/BookingDiaryPanel";
import AuditLogPanel from "../components/manager/AuditLogPanel";
import {
  ManagerDashboardTabPanel,
} from "../components/manager/ManagerDashboardNav";
import { getBusinessDateKey } from "../utils/businessDate";
import {
  loadGuestProfiles,
  saveGuestProfiles,
} from "../services/guestProfileService";
import {
  getCurrentActor,
  logGuestCheckIn,
  logGuestCheckout,
  logPaymentReceived,
  logHousekeepingUpdate,
  logMaintenanceUpdate,
  logRoomBoardUpdate,
  logDiscountUpdate,
} from "../services/auditLogService";

const MANAGER_UID = "qrvQ8zvbtobSxHPkVGgx2c63t9B2";
const ADMIN_UID = "imXv7XiyoYSxJRg9kLzo7jhrbJI3";

function HotelManagerDashboard() {
  const navigate = useNavigate();

  const [authReady, setAuthReady] = useState(false);
  const [authAuthorized, setAuthAuthorized] = useState(false);
  const [currentFirebaseUserId, setCurrentFirebaseUserId] = useState("");
  const [activeDashboardTab, setActiveDashboardTab] = useState("room_board");

  const dashboardRootRef = useRef(null);
  const latestStaysRef = useRef({hotelId: "", records: new Map()});
  const latestBoardRef = useRef({hotelId: "", board: {occupiedRooms: []}});
  const audioContextRef = useRef(null);

  const [ambientNow, setAmbientNow] =
    useState(() => new Date());

  const hotelId = sessionStorage.getItem("managerHotelId") || localStorage.getItem("managerHotelId");
  const hotelName = sessionStorage.getItem("managerHotelName") || localStorage.getItem("managerHotelName");
  const managerLoggedIn = sessionStorage.getItem("managerLoggedIn") === "true" || localStorage.getItem("managerLoggedIn") === "true";

  if (hotelId && managerLoggedIn) {
    sessionStorage.setItem("managerHotelId", hotelId);
    if (hotelName) sessionStorage.setItem("managerHotelName", hotelName);
    sessionStorage.setItem("managerLoggedIn", "true");
  }

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      const uid = String(user?.uid || "");
      const authorized = uid === MANAGER_UID || uid === ADMIN_UID;

      setCurrentFirebaseUserId(uid);
      setAuthAuthorized(authorized);
      setAuthReady(true);

      if (!authorized) {
        navigate("/manager-login", { replace: true });
      }
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  /* ------------------------------------------------------------------------ */
  /*                 AMBIENT THEME + DASHBOARD SOUND FX                      */
  /* ------------------------------------------------------------------------ */

  const ensureAudioContext = () => {
    if (typeof window === "undefined") {
      return null;
    }

    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContextClass) {
      return null;
    }

    if (!audioContextRef.current) {
      audioContextRef.current =
        new AudioContextClass();
    }

    const context =
      audioContextRef.current;

    if (
      context.state === "suspended"
    ) {
      context.resume().catch(() => {});
    }

    return context;
  };

  const playSynthTone = ({
    frequency = 520,
    duration = 0.06,
    volume = 0.025,
    type = "sine",
    delay = 0,
    endFrequency,
  } = {}) => {
    const context =
      ensureAudioContext();

    if (!context) return;

    try {
      const oscillator =
        context.createOscillator();

      const gain =
        context.createGain();

      const startAt =
        context.currentTime +
        Math.max(0, delay);

      const finishAt =
        startAt +
        Math.max(0.03, duration);

      oscillator.type = type;

      oscillator.frequency.setValueAtTime(
        frequency,
        startAt
      );

      if (
        Number.isFinite(
          Number(endFrequency)
        )
      ) {
        oscillator.frequency.exponentialRampToValueAtTime(
          Math.max(
            40,
            Number(endFrequency)
          ),
          finishAt
        );
      }

      gain.gain.setValueAtTime(
        0.0001,
        startAt
      );

      gain.gain.exponentialRampToValueAtTime(
        Math.max(
          0.0002,
          Number(volume) || 0.025
        ),
        startAt + 0.012
      );

      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        finishAt
      );

      oscillator.connect(gain);
      gain.connect(context.destination);

      oscillator.start(startAt);
      oscillator.stop(
        finishAt + 0.02
      );
    } catch (error) {
      console.warn(
        "Dashboard sound unavailable:",
        error
      );
    }
  };

  const playButtonClickSound = () => {
    playSynthTone({
      frequency: 460,
      endFrequency: 620,
      duration: 0.055,
      volume: 0.018,
      type: "sine",
    });
  };

  const playCheckInSuccessSound = () => {
    [
      [523.25, 0],
      [659.25, 0.09],
      [783.99, 0.18],
      [1046.5, 0.29],
    ].forEach(
      ([frequency, delay], index) => {
        playSynthTone({
          frequency,
          duration:
            index === 3
              ? 0.22
              : 0.13,
          volume:
            index === 3
              ? 0.045
              : 0.035,
          type: "sine",
          delay,
        });
      }
    );
  };

  useEffect(() => {
    const timer = window.setInterval(
      () => setAmbientNow(new Date()),
      60 * 1000
    );

    return () =>
      window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const root =
      dashboardRootRef.current;

    if (!root) {
      return undefined;
    }

    const handleDashboardButtonClick =
      (event) => {
        const button =
          event.target?.closest?.(
            "button"
          );

        if (
          !button ||
          !root.contains(button) ||
          button.disabled ||
          button.dataset?.pmsSound ===
            "silent"
        ) {
          return;
        }

        playButtonClickSound();
      };

    root.addEventListener(
      "click",
      handleDashboardButtonClick,
      true
    );

    return () =>
      root.removeEventListener(
        "click",
        handleDashboardButtonClick,
        true
      );
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                  CONTINUOUS DELHI TIME-BASED THEME                       */
  /* ------------------------------------------------------------------------ */

  const delhiClock = useMemo(
    () => {
      try {
        const parts =
          new Intl.DateTimeFormat(
            "en-GB",
            {
              timeZone:
                "Asia/Kolkata",
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
            }
          ).formatToParts(
            ambientNow
          );

        const hour = Number(
          parts.find(
            (part) =>
              part.type === "hour"
          )?.value || 0
        );

        const minute = Number(
          parts.find(
            (part) =>
              part.type === "minute"
          )?.value || 0
        );

        return {
          hour,
          minute,
          minutes:
            hour * 60 + minute,
        };
      } catch {
        const hour =
          ambientNow.getHours();
        const minute =
          ambientNow.getMinutes();

        return {
          hour,
          minute,
          minutes:
            hour * 60 + minute,
        };
      }
    },
    [ambientNow]
  );

  const delhiMinutes =
    delhiClock.minutes;

  const timePhase = useMemo(
    () => {
      if (delhiMinutes < 270) {
        return "late_night";
      }

      if (delhiMinutes < 330) {
        return "pre_dawn";
      }

      if (delhiMinutes < 390) {
        return "dawn";
      }

      if (delhiMinutes < 450) {
        return "sunrise";
      }

      if (delhiMinutes < 630) {
        return "morning";
      }

      if (delhiMinutes < 900) {
        return "midday";
      }

      if (delhiMinutes < 1020) {
        return "afternoon";
      }

      if (delhiMinutes < 1110) {
        return "golden_hour";
      }

      if (delhiMinutes < 1170) {
        return "sunset";
      }

      if (delhiMinutes < 1260) {
        return "dusk";
      }

      return "night";
    },
    [delhiMinutes]
  );

  const isRoomOperations =
    activeDashboardTab ===
    "room_board";

  const roomOpsPalette =
    useMemo(() => {
      /*
       * Ye fixed Light/Dark switch nahi hai.
       * Har anchor ke beech current Delhi minute ke hisaab se colours smoothly
       * interpolate hote hain. Isliye 06:22, 06:23, 06:24... sab me shade
       * gradually sunrise se morning ki taraf move karta rahega.
       */
      const hexToRgb = (hex) => {
        const normalized =
          String(hex || "")
            .replace("#", "")
            .trim();

        if (!/^[0-9a-f]{6}$/i.test(normalized)) {
          return {
            r: 0,
            g: 0,
            b: 0,
          };
        }

        return {
          r: parseInt(
            normalized.slice(0, 2),
            16
          ),
          g: parseInt(
            normalized.slice(2, 4),
            16
          ),
          b: parseInt(
            normalized.slice(4, 6),
            16
          ),
        };
      };

      const rgbToHex = ({ r, g, b }) =>
        `#${[r, g, b]
          .map((value) =>
            Math.max(
              0,
              Math.min(
                255,
                Math.round(value)
              )
            )
              .toString(16)
              .padStart(2, "0")
          )
          .join("")}`;

      const mixHex = (
        from,
        to,
        ratio
      ) => {
        const first =
          hexToRgb(from);
        const second =
          hexToRgb(to);
        const amount = Math.max(
          0,
          Math.min(1, ratio)
        );

        return rgbToHex({
          r:
            first.r +
            (second.r - first.r) *
              amount,
          g:
            first.g +
            (second.g - first.g) *
              amount,
          b:
            first.b +
            (second.b - first.b) *
              amount,
        });
      };

      const withAlpha = (
        hex,
        alpha
      ) => {
        const { r, g, b } =
          hexToRgb(hex);

        return `rgba(${r},${g},${b},${alpha})`;
      };

      const getLuminance = (hex) => {
        const { r, g, b } =
          hexToRgb(hex);

        const normalize = (value) => {
          const channel =
            value / 255;

          return channel <= 0.03928
            ? channel / 12.92
            : Math.pow(
                (channel + 0.055) /
                  1.055,
                2.4
              );
        };

        return (
          0.2126 * normalize(r) +
          0.7152 * normalize(g) +
          0.0722 * normalize(b)
        );
      };


      const anchors = [
        {
          minute: 0,
          top: "#050b18",
          mid: "#0b1730",
          bottom: "#101b2b",
          glowA: "#4338ca",
          glowB: "#0ea5e9",
        },
        {
          minute: 270,
          top: "#0c1230",
          mid: "#202958",
          bottom: "#34405e",
          glowA: "#6366f1",
          glowB: "#64748b",
        },
        {
          minute: 330,
          top: "#343968",
          mid: "#806c8c",
          bottom: "#c88f8e",
          glowA: "#f472b6",
          glowB: "#818cf8",
        },
        {
          minute: 375,
          top: "#7c78a8",
          mid: "#efa178",
          bottom: "#ffd79a",
          glowA: "#fb923c",
          glowB: "#f9a8d4",
        },
        {
          minute: 420,
          top: "#b9daf4",
          mid: "#ffe2ad",
          bottom: "#f8f2e6",
          glowA: "#fbbf24",
          glowB: "#38bdf8",
        },
        {
          minute: 540,
          top: "#d9eefc",
          mid: "#edf7ff",
          bottom: "#f8f5ee",
          glowA: "#fde68a",
          glowB: "#7dd3fc",
        },
        {
          minute: 720,
          top: "#cfeafa",
          mid: "#e9f6ff",
          bottom: "#f8f8f3",
          glowA: "#fef08a",
          glowB: "#67e8f9",
        },
        {
          minute: 900,
          top: "#d9edf6",
          mid: "#eef5f2",
          bottom: "#f7f0e3",
          glowA: "#fde68a",
          glowB: "#93c5fd",
        },
        {
          minute: 1020,
          top: "#f5d7aa",
          mid: "#f2c5a7",
          bottom: "#e7d9dd",
          glowA: "#fb923c",
          glowB: "#c084fc",
        },
        {
          minute: 1110,
          top: "#ef9a75",
          mid: "#bd7d91",
          bottom: "#6b5d89",
          glowA: "#f97316",
          glowB: "#a855f7",
        },
        {
          minute: 1170,
          top: "#67577e",
          mid: "#394365",
          bottom: "#19283e",
          glowA: "#c084fc",
          glowB: "#fb7185",
        },
        {
          minute: 1260,
          top: "#101b2e",
          mid: "#0a1728",
          bottom: "#07111f",
          glowA: "#4f46e5",
          glowB: "#0ea5e9",
        },
        {
          minute: 1440,
          top: "#050b18",
          mid: "#0b1730",
          bottom: "#101b2b",
          glowA: "#4338ca",
          glowB: "#0ea5e9",
        },
      ];

      let from = anchors[0];
      let to = anchors[1];

      for (
        let index = 0;
        index < anchors.length - 1;
        index += 1
      ) {
        if (
          delhiMinutes >=
            anchors[index].minute &&
          delhiMinutes <=
            anchors[index + 1].minute
        ) {
          from = anchors[index];
          to = anchors[index + 1];
          break;
        }
      }

      const span = Math.max(
        1,
        to.minute - from.minute
      );

      const ratio =
        (delhiMinutes -
          from.minute) /
        span;

      const top = mixHex(
        from.top,
        to.top,
        ratio
      );

      const mid = mixHex(
        from.mid,
        to.mid,
        ratio
      );

      const bottom = mixHex(
        from.bottom,
        to.bottom,
        ratio
      );

      const glowA = mixHex(
        from.glowA,
        to.glowA,
        ratio
      );

      const glowB = mixHex(
        from.glowB,
        to.glowB,
        ratio
      );

      const dark =
        getLuminance(mid) < 0.31;

      return {
        dark,
        root: bottom,
        page:
          `linear-gradient(180deg,${top} 0%,${mid} 48%,${bottom} 100%)`,
        title:
          dark
            ? "#f8fafc"
            : "#0f172a",
        muted:
          dark
            ? "#a8b7c9"
            : "#64748b",
        surface:
          dark
            ? "rgba(10,24,40,.82)"
            : "rgba(255,255,255,.90)",
        surfaceSoft:
          dark
            ? "rgba(16,32,50,.78)"
            : "rgba(248,250,252,.90)",
        border:
          dark
            ? "rgba(148,163,184,.24)"
            : "rgba(148,163,184,.30)",
        dateBg:
          dark
            ? "rgba(15,30,48,.82)"
            : "rgba(255,255,255,.78)",
        dateText:
          dark
            ? "#e5edf7"
            : "#475569",
        glowA:
          withAlpha(
            glowA,
            dark ? 0.28 : 0.23
          ),
        glowB:
          withAlpha(
            glowB,
            dark ? 0.20 : 0.16
          ),
      };
    },
    [delhiMinutes]
  );

  const BOARD_KEY = `managerBoard_${hotelId}`;

  /*
   * Room Occupation Board ka source of truth Firestore hoga.
   * localStorage sirf fallback/cache ke liye rahega.
   * Isse kisi bhi laptop/system par same board realtime dikhega.
   */
  const getBoardDocRef = () =>
    doc(
      db,
      "hotelRoomBoards",
      hotelId
    );

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(true);
  const [checkInLoadingId, setCheckInLoadingId] = useState("");
  const [selectedCheckInRoom, setSelectedCheckInRoom] = useState("");
  const [checkInRoomContext, setCheckInRoomContext] = useState(null);
  const [quickCheckInOpen, setQuickCheckInOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [checkInPrefillProfile, setCheckInPrefillProfile] = useState(null);
  const [checkInPrefillName, setCheckInPrefillName] = useState("");
  const [boardGuestProfiles, setBoardGuestProfiles] = useState([]);
  const [boardGuestSearch, setBoardGuestSearch] = useState("");
  const [selectedBoardGuest, setSelectedBoardGuest] = useState(null);
  const [boardGuestRoom, setBoardGuestRoom] = useState("");
  const [boardGuestSearchLoading, setBoardGuestSearchLoading] = useState(false);
  const [walkInSaving, setWalkInSaving] = useState(false);
  const [selectedOccupiedRoom, setSelectedOccupiedRoom] = useState(null);
  const [roomActionSaving, setRoomActionSaving] = useState(false);
  const [selectedCleaningRoom, setSelectedCleaningRoom] = useState(null);
  const [cleaningActionSaving, setCleaningActionSaving] = useState(false);
  const [checkoutSuccessData, setCheckoutSuccessData] = useState(null);

  /*
   * React state update async hota hai. Fast double-click / slow network me
   * same Check-In handler do baar enter na ho, isliye immediate ref lock.
   */
  const checkInSubmitLockRef = useRef(false);

  /*
   * Room Board + hotelStays lifecycle reconciliation ko har realtime snapshot
   * par loop me chalne se rokta hai. First cloud board load par ek safe repair run
   * hota hai; check-in se pehle room lifecycle dobara verify hota hai.
   */
  const lifecycleReconcileStartedRef = useRef(false);

  const [vacantRooms, setVacantRooms] = useState([]);
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [checkoutRooms, setCheckoutRooms] = useState([]);
  const [serviceRooms, setServiceRooms] = useState([]);

  const [singleRoom, setSingleRoom] = useState("");
  const [singleRoomCategory, setSingleRoomCategory] = useState("");
  const [floorLetter, setFloorLetter] = useState("");
  const [floorRoomCount, setFloorRoomCount] = useState("");
  const [floorRoomCategory, setFloorRoomCategory] = useState("");
  const [roomCategoryDrafts, setRoomCategoryDrafts] = useState({});
  const [serviceRoomInput, setServiceRoomInput] = useState("");
  const [roomSetupOpen, setRoomSetupOpen] = useState(false);
  const [servicePanelOpen, setServicePanelOpen] = useState(false);

  const [
    rushTempNameEnabled,
    setRushTempNameEnabled,
  ] = useState(false);

  const normalizeRoom = (room) =>
    String(room || "").trim().toUpperCase();

  const normalizeRoomCategory = (value) =>
    String(value || "").replace(/\s+/g, " ").trim();

  const getRoomValue = (item) =>
    typeof item === "string" ? item : item?.room || "";

  const getRoomCategory = (item) =>
    normalizeRoomCategory(
      typeof item === "object" && item
        ? item.roomCategory || item.category || item.roomType || ""
        : ""
    );

  const getBoardRoomItem = (roomValue) => {
    const room = normalizeRoom(roomValue);
    if (!room) return null;

    return [
      ...vacantRooms,
      ...occupiedRooms,
      ...checkoutRooms,
    ].find(
      (item) =>
        normalizeRoom(getRoomValue(item)) === room
    ) || null;
  };

  const getRoomSetupRows = () => {
    const roomMap = new Map();

    const addRows = (items, status) => {
      (items || []).forEach((item) => {
        const room = normalizeRoom(getRoomValue(item));
        if (!room || roomMap.has(room)) return;

        roomMap.set(room, {
          ...(typeof item === "object" && item ? item : { room }),
          room,
          setupStatus: status,
        });
      });
    };

    addRows(vacantRooms, "Ready");
    addRows(occupiedRooms, "Occupied");
    addRows(checkoutRooms, "Dirty / Checkout");

    return sortRooms([...roomMap.values()]);
  };

  const sortRooms = (rooms) => {
    return [...rooms].sort((a, b) => {
      const roomA = normalizeRoom(getRoomValue(a));
      const roomB = normalizeRoom(getRoomValue(b));

      const matchA = roomA.match(/^([A-Z]+)(\d+)$/);
      const matchB = roomB.match(/^([A-Z]+)(\d+)$/);

      if (!matchA || !matchB) {
        return roomA.localeCompare(roomB);
      }

      if (matchA[1] !== matchB[1]) {
        return matchA[1].localeCompare(matchB[1]);
      }

      return Number(matchA[2]) - Number(matchB[2]);
    });
  };

  const getRoomFloor = (room) => {
    const value = normalizeRoom(room);

    const alphaNumeric = value.match(/^([A-Z]+)(\d+)$/);
    if (alphaNumeric) return alphaNumeric[1];

    // Numeric PMS room numbers: 101-199 => Floor 1, 201-299 => Floor 2...
    const numeric = value.match(/^(\d{3,4})$/);
    if (numeric) {
      const number = Number(numeric[1]);
      const floor = Math.floor(number / 100);
      return floor > 0 ? String(floor) : "GROUND";
    }

    return "OTHER";
  };

  const groupRoomsByFloor = (rooms) => {
    const grouped = {};

    sortRooms(rooms).forEach((item) => {
      const room = getRoomValue(item);
      const floor = getRoomFloor(room);

      if (!grouped[floor]) {
        grouped[floor] = [];
      }

      grouped[floor].push(item);
    });

    return grouped;
  };

  const groupedVacant = useMemo(
    () => groupRoomsByFloor(vacantRooms),
    [vacantRooms]
  );

  const groupedOccupied = useMemo(
    () => groupRoomsByFloor(occupiedRooms),
    [occupiedRooms]
  );

  const groupedCheckout = useMemo(
    () => groupRoomsByFloor(checkoutRooms),
    [checkoutRooms]
  );

  const isCheckedIn = (booking) =>
    booking?.checkedIn === true ||
    booking?.status === "checked_in";

  const isFreeStay = (booking) =>
    booking?.isHotelFree === true ||
    booking?.paymentType === "free_hotel_loyalty";

  const totalBookings = bookings.length;

  const totalCheckedIn = bookings.filter((booking) =>
    isCheckedIn(booking)
  ).length;

  const pendingCheckIn = bookings.filter(
    (booking) => !isCheckedIn(booking)
  ).length;

  const todayDate = new Date().toLocaleDateString("en-CA", {timeZone: "Asia/Kolkata"});

  const todayBookings = bookings.filter(
    (booking) => booking.date === todayDate
  ).length;

  const todayCheckedIn = bookings.filter(
    (booking) =>
      booking.date === todayDate && isCheckedIn(booking)
  ).length;

  const totalBoardRooms = new Set([...vacantRooms, ...occupiedRooms, ...checkoutRooms, ...serviceRooms].map(getRoomValue).map(normalizeRoom).filter(Boolean)).size;

  const normalizeBoardData = (
    boardData = {}
  ) => {
    const safeVacantRooms = (
      boardData.vacantRooms || []
    )
      .map((item) => {
        if (
          typeof item === "string"
        ) {
          return {
            room: item,
            isSir: false,
          };
        }

        return {
          ...item,
          room:
            item?.room || "",
          isSir:
            !!item?.isSir,
        };
      })
      .filter(
        (item) =>
          normalizeRoom(
            item.room
          )
      );

    const safeOccupiedRooms = (
      boardData.occupiedRooms ||
      []
    )
      .map((item) =>
        typeof item === "string"
          ? {
              room: item,
              status:
                "details_pending",
            }
          : item
      )
      .filter(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          )
      );

    const safeCheckoutRooms = (
      boardData.checkoutRooms ||
      []
    )
      .map((item) =>
        typeof item === "string"
          ? {
              room: item,
              status:
                "checkout_pending",
            }
          : item
      )
      .filter(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          )
      );

    const safeServiceRooms = (
      boardData.serviceRooms ||
      []
    ).filter(Boolean);

    return {
      vacantRooms:
        sortRooms(
          safeVacantRooms
        ),

      occupiedRooms:
        sortRooms(
          safeOccupiedRooms
        ),

      checkoutRooms:
        sortRooms(
          safeCheckoutRooms
        ),

      serviceRooms:
        safeServiceRooms,

      rushTempNameEnabled:
        boardData.rushTempNameEnabled === true,
    };
  };

  const applyBoardData = (
    boardData = {}
  ) => {
    const normalized =
      normalizeBoardData(
        boardData
      );

    // Keep the raw board: hydration can temporarily hide cards while the
    // independent stay listener is still delivering the previous room.
    latestBoardRef.current = {hotelId, board: normalized};

    setVacantRooms(
      normalized.vacantRooms
    );

    setOccupiedRooms(
      hydrateOccupiedCards(normalized.occupiedRooms, latestStaysRef.current.hotelId === hotelId ? latestStaysRef.current.records : new Map())
    );

    setCheckoutRooms(
      normalized.checkoutRooms
    );

    setServiceRooms(
      normalized.serviceRooms
    );

    setRushTempNameEnabled(
      normalized.rushTempNameEnabled === true
    );

    return normalized;
  };

  // Capture the calling render's baseline BEFORE the first await. Never write a full stale list.
  const saveBoard = async (nextVacant, nextOccupied, nextCheckout, nextService) => {
    if (!hotelId) return false;
    const operation = planBoardMutation(
      { vacantRooms, occupiedRooms, checkoutRooms, serviceRooms, rushTempNameEnabled },
      { vacantRooms: nextVacant || [], occupiedRooms: nextOccupied || [],
        checkoutRooms: nextCheckout || [], serviceRooms: nextService || [], rushTempNameEnabled }
    );
    try {
      const committed = await runTransaction(db, async transaction => {
        const boardRef = getBoardDocRef();
        const snapshot = await transaction.get(boardRef);
        const current = snapshot.exists() ? snapshot.data() : {};
        const next = mergeBoardMutation({...current, ...normalizeBoardData(current)}, operation);
        const rooms = [...new Set(operation.changes.map(change => change.room))];
        const locks = await Promise.all(rooms.map(room => transaction.get(getRoomLifecycleLockRef(room))));
        rooms.forEach((room, index) => {
          const lock = locks[index].exists() ? locks[index].data() : {};
          assertRoomLockAllowsBoard(room, next, lock);
        });
        // Board-only quick occupancy also reserves the room, atomically with the card.
        rooms.forEach((room, index) => {
          const lock = locks[index].exists() ? locks[index].data() : {};
          const occupied = next.occupiedRooms.find(item => roomKey(item) === room);
          const ready = next.vacantRooms.some(item => roomKey(item) === room);
          const previousCheckout = (current.checkoutRooms || []).find(item => roomKey(item) === room);
          const desiredCheckout = next.checkoutRooms.find(item => roomKey(item) === room);
          if (ready && !lock.activeStayId) {
            if (lock.status === 'maintenance_hold') throw new Error(`Room ${room}: maintenance resolve karein.`);
            if (lock.lastStayId && String(previousCheckout?.stayId || '') !== String(lock.lastStayId)) throw new Error(`Room ${room}: cleaning record badal gaya hai.`);
            if (locks[index].exists()) transaction.delete(getRoomLifecycleLockRef(room));
          } else if (occupied?.detailsPending === true && !lock.activeStayId) {
            transaction.set(getRoomLifecycleLockRef(room), {hotelId, room, status: 'details_pending', quickOccupancyId: occupied.quickOccupancyId || lock.quickOccupancyId || '', updatedAt: serverTimestamp()});
          } else if (desiredCheckout && !lock.activeStayId && (!locks[index].exists() || lock.status === 'details_pending')) {
            transaction.set(getRoomLifecycleLockRef(room), {hotelId, room, activeStayId: '', lastStayId: desiredCheckout.stayId || '', status: desiredCheckout.status === 'maintenance_hold' ? 'maintenance_hold' : 'cleaning', updatedAt: serverTimestamp()});
          } else if (!occupied && lock.status === 'details_pending') {
            transaction.delete(getRoomLifecycleLockRef(room));
          }
        });
        transaction.set(boardRef, {...next, schemaVersion: 2, revision: Number(current.revision || 0) + 1, hotelId, hotelName: hotelName || '', updatedAt: serverTimestamp()}, {merge: true});
        return next;
      });
      applyBoardData(committed);
      try { localStorage.setItem(BOARD_KEY, JSON.stringify(committed)); } catch (_) {}
      return true;
    } catch (error) {
      console.error('Room board transaction failed:', error);
      // Roll back optimistic UI to authoritative cloud state, never to the stale render.
      try { const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data()); } catch (_) {}
      alert(error?.message || 'Room board save failed. Connection aur Firestore permissions check karein.');
      return false;
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                    PHASE 1 - ROOM LIFECYCLE LOCK                         */
  /* ------------------------------------------------------------------------ */

  const getRoomLifecycleLockId = (roomValue) => {
    const room = normalizeRoom(roomValue);
    const safeHotel = String(hotelId || "hotel")
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "_");
    const safeRoom = room.replace(/[^A-Z0-9_-]/g, "_");
    return `${safeHotel}__${safeRoom}`;
  };

  const getRoomLifecycleLockRef = (roomValue) =>
    doc(
      db,
      "activeRoomStays",
      getRoomLifecycleLockId(roomValue)
    );

  const isStayActive = (stay = {}) => {
    const status = String(stay?.status || "")
      .trim()
      .toLowerCase();

    return (
      status === "checked_in" &&
      stay?.checkedOut !== true &&
      stay?.checkoutCompleted !== true
    );
  };

  const lifecycleMillis = (value) => {
    if (!value) return 0;

    if (typeof value?.toDate === "function") {
      return value.toDate().getTime();
    }

    if (value?.seconds) {
      return Number(value.seconds) * 1000;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const getStayCheckInMillis = (stay = {}) =>
    lifecycleMillis(
      stay.checkInAt ||
        stay.createdAtIso ||
        stay.createdAt
    );

  const getStayCheckoutMillis = (stay = {}) =>
    lifecycleMillis(
      stay.actualCheckOutAt ||
        stay.checkedOutAt ||
        stay.checkoutAt ||
        stay.roomReadyAt
    );

  const getCurrentBoardSnapshot = () => ({
    vacantRooms,
    occupiedRooms,
    checkoutRooms,
    serviceRooms,
    rushTempNameEnabled,
  });

  const getBoardLifecycleSignature = (board = {}) => {
    const roomKey = (item) => {
      const room = normalizeRoom(getRoomValue(item));
      const stayId =
        typeof item === "object"
          ? String(item?.stayId || item?.id || "")
          : "";
      const status =
        typeof item === "object"
          ? String(item?.status || "")
          : "";
      return `${room}|${stayId}|${status}`;
    };

    return JSON.stringify({
      vacant: (board.vacantRooms || [])
        .map(roomKey)
        .sort(),
      occupied: (board.occupiedRooms || [])
        .map(roomKey)
        .sort(),
      checkout: (board.checkoutRooms || [])
        .map(roomKey)
        .sort(),
      service: (board.serviceRooms || [])
        .map(roomKey)
        .sort(),
    });
  };

  // Inspect legacy stays and serial claims without changing lifecycle fields.
  // Read-only reconciliation. Old history must never close stays or delete another device's lock.
  const reconcileHotelLifecycle = async (boardInput = getCurrentBoardSnapshot()) => {
    if (!hotelId) return {board: normalizeBoardData(boardInput), survivorByRoom: {}, repairedStayIds: []};
    const snapshot = await getDoc(getBoardDocRef());
    const board = normalizeBoardData(snapshot.exists() ? snapshot.data() : boardInput);
    const staySnapshot = await getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)));
    const survivorByRoom = {};
    const activeStaysByRoom = {};
    const serialAssignments = {};
    for (const record of staySnapshot.docs) {
      const stay = {id: record.id, stayId: record.id, ...record.data()};
      if (String(stay.adRegisterId || 'legacy') === String(snapshot.data()?.adRegisterId || 'legacy') && stay.adRegisterSerialNo) {
        const key = String(Number(stay.adRegisterSerialNo));
        serialAssignments[key] = [...(serialAssignments[key] || []), record.id];
      }
      if (!isStayActive(stay)) continue;
      const room = normalizeRoom(stay.room);
      activeStaysByRoom[room] = [...(activeStaysByRoom[room] || []), stay];
      const lock = await getDoc(getRoomLifecycleLockRef(room));
      if (lock.exists() && lock.data().activeStayId === record.id) survivorByRoom[room] = stay;
      else if (!lock.exists()) console.warn('Active stay requires manual lifecycle review:', record.id, room);
    }
    return {board, survivorByRoom, activeStaysByRoom, serialAssignments, repairedStayIds: []};
  };

  const subscribeRoomBoard =
    () => {
      if (!hotelId) {
        return () => {};
      }

      const boardRef =
        getBoardDocRef();

      let migrationAttempted =
        false;

      return onSnapshot(
        boardRef,
        async (
          snapshot
        ) => {
          if (
            snapshot.exists()
          ) {
            const normalizedBoard =
              applyBoardData(
                snapshot.data()
              );

            setBoardLoading(
              false
            );

            if (
              !lifecycleReconcileStartedRef.current
            ) {
              lifecycleReconcileStartedRef.current = true;

              reconcileHotelLifecycle(
                normalizedBoard,
                { reason: "initial_board_load" }
              ).catch((error) => {
                console.error(
                  "Room lifecycle reconciliation error:",
                  error
                );
              });
            }

            return;
          }

          /*
           * Cloud board pehli baar empty ho to isi browser ka
           * purana localStorage board automatically migrate karenge.
           * Ek baar migrate hone ke baad sab systems same cloud board use karenge.
           */
          if (
            !migrationAttempted
          ) {
            migrationAttempted =
              true;

            try {
              const savedBoard =
                localStorage.getItem(
                  BOARD_KEY
                );

              if (savedBoard) {
                const parsed =
                  JSON.parse(
                    savedBoard
                  );

                const normalized =
                  normalizeBoardData(
                    parsed
                  );

                const hasData =
                  normalized
                    .vacantRooms
                    .length > 0 ||
                  normalized
                    .occupiedRooms
                    .length > 0 ||
                  normalized
                    .checkoutRooms
                    .length > 0 ||
                  normalized
                    .serviceRooms
                    .length > 0;

                if (hasData) {
                  applyBoardData(
                    normalized
                  );

                  await saveBoard(
                    normalized.vacantRooms,
                    normalized.occupiedRooms,
                    normalized.checkoutRooms,
                    normalized.serviceRooms
                  );

                  setBoardLoading(
                    false
                  );

                  return;
                }
              }
            } catch (error) {
              console.error(
                "Local → Cloud board migration error:",
                error
              );
            }
          }

          applyBoardData({
            vacantRooms: [],
            occupiedRooms: [],
            checkoutRooms: [],
            serviceRooms: [],
          });

          setBoardLoading(
            false
          );
        },
        (error) => {
          console.error(
            "Cloud Room Board load error:",
            error
          );

          /*
           * Permission/network issue me local cache fallback.
           */
          try {
            const savedBoard =
              localStorage.getItem(
                BOARD_KEY
              );

            if (savedBoard) {
              applyBoardData(
                JSON.parse(
                  savedBoard
                )
              );
            }
          } catch (
            fallbackError
          ) {
            console.error(
              "Board fallback load error:",
              fallbackError
            );
          }

          setBoardLoading(
            false
          );
        }
      );
    };

  useEffect(() => {
    if (!authReady) {
      return undefined;
    }

    if (!hotelId || !managerLoggedIn || !authAuthorized) {
      navigate("/manager-login", {
        replace: true,
      });

      return undefined;
    }

    const unsubscribeBoard =
      subscribeRoomBoard();

    const bookingsQuery = query(
      collection(db, "bookings"),
      where("hotelId", "==", hotelId)
    );

    const unsubscribe = onSnapshot(
      bookingsQuery,
      (snapshot) => {
        const bookingList = snapshot.docs.map(
          (bookingDocument) => ({
            id: bookingDocument.id,
            ...bookingDocument.data(),
          })
        );

        bookingList.sort((a, b) => {
          const firstTime = a?.createdAt?.toMillis
            ? a.createdAt.toMillis()
            : 0;

          const secondTime = b?.createdAt?.toMillis
            ? b.createdAt.toMillis()
            : 0;

          return secondTime - firstTime;
        });

        setBookings(bookingList);
        setLoading(false);
      },
      (error) => {
        console.error("Bookings load error:", error);

        alert(
          "Bookings load nahi hui: " +
            (error?.message || error)
        );

        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      unsubscribeBoard();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, navigate, authReady, authAuthorized, managerLoggedIn]);

  /*
   * Keep occupied room cards in sync with the live hotelStays document.
   * PendingPaymentsPanel / other panels can update paidTotal/balance without
   * writing the room-board cache, so the board must merge fresh stay data.
   */
  useEffect(() => {
    if (!authReady || !authAuthorized || !hotelId || !managerLoggedIn) {
      return undefined;
    }

    const staysQuery = query(
      collection(db, "hotelStays"),
      where("hotelId", "==", hotelId)
    );

    const unsubscribeStays = onSnapshot(
      staysQuery,
      (snapshot) => {
        const stayById = new Map(
          snapshot.docs.map((stayDocument) => [
            stayDocument.id,
            {
              id: stayDocument.id,
              stayId: stayDocument.id,
              ...stayDocument.data(),
            },
          ])
        );

        latestStaysRef.current = {hotelId, records: stayById};
        const boardCards = latestBoardRef.current.hotelId === hotelId
          ? latestBoardRef.current.board.occupiedRooms : [];
        setOccupiedRooms(hydrateOccupiedCards(boardCards, stayById));
        setSelectedOccupiedRoom(current => current ? hydrateOccupiedCards([current], stayById)[0] || null : null);
      },
      (error) => {
        console.warn(
          "Live stay sync error:",
          error
        );
      }
    );

    return () => unsubscribeStays();
  }, [hotelId, managerLoggedIn, authReady, authAuthorized]);

  const handleLogout = async () => {
    try {
      [sessionStorage, localStorage].forEach((storage) => {
        storage.removeItem("managerHotelId");
        storage.removeItem("managerHotelName");
        storage.removeItem("managerLoggedIn");
      });

      await signOut(auth);

      navigate("/manager-login", {
        replace: true,
      });
    } catch (error) {
      console.error("Logout error:", error);

      alert(
        "Logout error: " +
          (error?.message || error)
      );
    }
  };

  const handleResetBoard = async () => {
    if (currentFirebaseUserId !== ADMIN_UID) {
      alert("Room Board Reset sirf Admin recovery action hai.");
      return;
    }

    try {
      const staySnapshot = await getDocs(
        query(
          collection(db, "hotelStays"),
          where("hotelId", "==", hotelId)
        )
      );

      const activeStays = staySnapshot.docs
        .map((stayDoc) => ({ id: stayDoc.id, ...stayDoc.data() }))
        .filter(isStayActive);

      if (activeStays.length > 0) {
        const activeRooms = Array.from(
          new Set(activeStays.map((stay) => normalizeRoom(stay.room)).filter(Boolean))
        );

        alert(
          `Reset blocked. ${activeStays.length} active stay(s) abhi open hain${
            activeRooms.length ? `: ${activeRooms.join(", ")}` : "."
          } Pehle unka lifecycle close/repair karo.`
        );
        return;
      }

      const typed = window.prompt(
        'Admin recovery only. Poora Room Board clear karne ke liye exactly "RESET" type karo.'
      );

      if (typed !== "RESET") {
        return;
      }

      const previousBoard = getCurrentBoardSnapshot();

      setVacantRooms([]);
      setOccupiedRooms([]);
      setCheckoutRooms([]);
      setServiceRooms([]);

      const boardSaved = await saveBoard([], [], [], []);

      if (!boardSaved) {
        applyBoardData(previousBoard);
        alert("Reset cloud me save nahi hua. Local Room Board restore kar diya gaya hai.");
        return;
      }

      localStorage.removeItem(BOARD_KEY);
      alert("Room board reset ho gaya ✅");
    } catch (error) {
      console.error("Room Board reset safety check error:", error);
      alert(
        "Room Board reset nahi hua: " +
          (error?.message || error)
      );
    }
  };

  const handleBookingCheckIn = async (booking) => {
    if (!booking?.id || checkInLoadingId) return;
    const code = window.prompt('Guest ka 4 digit Check-In Code enter karein:');
    if (code === null) return;
    if (!/^\d{4}$/.test(String(code).trim())) return alert('Valid 4 digit code required hai.');
    setCheckInLoadingId(booking.id);
    try {
      const response = await httpsCallable(getFunctions(app, 'asia-south1'), 'confirmBookingCheckIn')({bookingId: booking.id, checkInCode: String(code).trim()});
      playCheckInSuccessSound();
      alert(response.data.alreadyCheckedIn ? 'Booking pehle hi redeemed hai.' : 'Booking check-in confirm ho gaya ✅');
    } catch (error) {alert('Booking check-in nahi hua: ' + error.message);}
    finally {setCheckInLoadingId('');}
  };

  const handleRemoveReadyRoom = async (roomValue) => {
    const room = normalizeRoom(roomValue);
    if (!room || roomActionSaving) return;
    if (!window.confirm(`Room ${room} galti se add hua hai? Room Setup se remove karein?`)) return;
    setRoomActionSaving(true);
    try {
      const actor = getCurrentActor();
      if (!actor.actorId) throw new Error('Manager login required hai.');
      // Include legacy stay records which may not have a lifecycle lock.
      const staySnapshot = await getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)));
      const matchingStays = staySnapshot.docs.filter(record => normalizeRoom(record.data().room) === room);
      const auditRef = doc(collection(db, 'auditLogs'));
      const eventAt = new Date().toISOString();
      const next = await runTransaction(db, async transaction => {
        const boardRef = getBoardDocRef();
        const [boardSnap, lockSnap, ...stays] = await Promise.all([
          transaction.get(boardRef), transaction.get(getRoomLifecycleLockRef(room)),
          ...matchingStays.map(record => transaction.get(record.ref))
        ]);
        const current = boardSnap.exists() ? boardSnap.data() : {};
        const result = removeReadyRoom(current, lockSnap.exists() ? lockSnap.data() : null,
          stays.filter(record => record.exists()).map(record => record.data()), {hotelId, room});
        transaction.set(boardRef, {...result, hotelId, hotelName: hotelName || '', schemaVersion: 2,
          revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
        transaction.set(auditRef, {hotelId, hotelName: hotelName || '', room, action: 'room_board', actionType: 'room_board',
          title: `Room ${room} removed from setup`, description: `Unused Ready room ${room} removed from Room Setup.`,
          before: {status: 'ready'}, after: {status: 'removed'}, ...actor, eventAt, createdAt: serverTimestamp()});
        return result;
      });
      applyBoardData(next);
      alert(`Room ${room} Room Setup se remove ho gaya. Guest history/payment records preserve hain.`);
    } catch (error) {alert(error?.message || 'Room remove nahi hua.');}
    finally {setRoomActionSaving(false);}
  };

  const saveRoomCategory = async (roomValue, categoryValue) => {
    const room = normalizeRoom(roomValue);
    const category = normalizeRoomCategory(categoryValue);
    const baseline = [...vacantRooms, ...occupiedRooms, ...checkoutRooms].find(item => roomKey(item) === room);
    if (!room || !baseline) return alert('Room board mein nahi mila. Refresh karein.');
    const expectedStayId = String(baseline.stayId || baseline.id || '');
    const actor = getCurrentActor();
    const auditRef = doc(collection(db, 'auditLogs'));
    try {
      const committed = await runTransaction(db, async transaction => {
        const boardRef = getBoardDocRef();
        const [snapshot, lockSnapshot] = await Promise.all([transaction.get(boardRef), transaction.get(getRoomLifecycleLockRef(room))]);
        const current = snapshot.exists() ? snapshot.data() : {};
        const board = normalizeBoardData(current);
        const list = ['vacantRooms', 'occupiedRooms', 'checkoutRooms'].find(name => board[name].some(item => roomKey(item) === room));
        const card = list ? board[list].find(item => roomKey(item) === room) : null;
        if (!card || String(card.stayId || card.id || '') !== expectedStayId) throw new Error('Room/stay badal gaya hai. Fresh room kholein.');
        if (getRoomCategory(card) !== getRoomCategory(baseline) && getRoomCategory(card) !== category) throw new Error('Room category doosre device par badal gayi hai. Refresh karein.');
        const reference = expectedStayId ? doc(db, 'hotelStays', expectedStayId) : null;
        const stay = reference ? await transaction.get(reference) : null;
        if (reference && (!stay.exists() || stay.data().hotelId !== hotelId || normalizeRoom(stay.data().room) !== room)) throw new Error('Stay/room mismatch. Fresh room kholein.');
        const next = {...current, ...board, [list]: board[list].map(item => roomKey(item) === room ? {...item, category, roomCategory: category} : item)};
        assertRoomLockAllowsBoard(room, next, lockSnapshot.exists() ? lockSnapshot.data() : {});
        transaction.set(boardRef, {...next, hotelId, hotelName: hotelName || '', schemaVersion: 2,
          revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
        if (stay?.exists()) transaction.update(reference, {category, roomCategory: category, updatedAt: serverTimestamp()});
        transaction.set(auditRef, {hotelId, hotelName: hotelName || '', room, stayId: expectedStayId,
          action: 'room_board', actionType: 'room_board', title: 'Room Category Updated',
          description: `Room ${room}: category changed to ${category || 'Unassigned'}.`,
          before: {category: getRoomCategory(card)}, after: {category}, ...actor,
          eventAt: new Date().toISOString(), createdAt: serverTimestamp()});
        return next;
      });
      applyBoardData(committed);
      setRoomCategoryDrafts(current => ({...current, [room]: category}));
    } catch (error) {
      alert(error?.message || 'Room category save nahi hui.');
      const fresh = await getDoc(getBoardDocRef()).catch(() => null);
      if (fresh?.exists()) applyBoardData(fresh.data());
    }
  };

  const restoreExistingOccupiedRoom = async (room) => {
    const candidates = (await getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)))).docs
      .filter(record => normalizeRoom(record.data().room) === room);
    const restored = await runTransaction(db, async transaction => {
      const boardRef = getBoardDocRef();
      const lockRef = getRoomLifecycleLockRef(room);
      const [boardSnapshot, lockSnapshot] = await Promise.all([
        transaction.get(boardRef), transaction.get(lockRef)
      ]);
      const lock = lockSnapshot.exists() ? lockSnapshot.data() : {};
      const stayId = String(lock.activeStayId || lock.lastStayId || '');
      if (!stayId) {
        const records = await Promise.all(candidates.map(record => transaction.get(record.ref)));
        const active = records.filter(record => record.exists() && record.data().hotelId === hotelId &&
          normalizeRoom(record.data().room) === room && isStayActive(record.data()));
        const current = boardSnapshot.exists() ? boardSnapshot.data() : {};
        if (active.length > 1) throw new Error(`Room ${room}: multiple active stay records mile. Existing guest verify karein.`);
        if (active.length === 1) {
          const record = active[0], existing = record.data();
          if (existing.actualCheckOutAt || existing.checkedOutAt || existing.checkoutAt) throw new Error(`Room ${room}: active stay mein checkout time bhi saved hai. Guest record verify karein.`);
          if (lock.status === 'details_pending' || lock.status === 'maintenance_hold' ||
              (current.serviceRooms || []).some(item => roomKey(item) === room) ||
              (current.occupiedRooms || []).some(item => roomKey(item) === room && item.detailsPending)) throw new Error(`Room ${room}: quick guest/service hold already hai. Fresh room verify karein.`);
          const restoredLock = {hotelId, room, activeStayId: record.id, status: 'occupied'};
          const next = restoreOccupiedRoom(current, restoredLock, existing, {hotelId, room, stayId: record.id});
          const actor = getCurrentActor();
          if (!actor.actorId) throw new Error('Manager login required hai.');
          transaction.set(lockRef, {...restoredLock, updatedAt: serverTimestamp()});
          transaction.set(boardRef, {...next, hotelId, hotelName: hotelName || '', schemaVersion: 2,
            revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
          transaction.set(doc(collection(db, 'auditLogs')), {hotelId, hotelName: hotelName || '', room,
            stayId: record.id, action: 'room_board', actionType: 'room_board', title: `Room ${room} Occupied Restored`,
            description: 'Existing active guest stay verified; missing room card/lock restored without changing guest/payment records.',
            ...actor, eventAt: new Date().toISOString(), createdAt: serverTimestamp()});
          return {board: next, recoveryStatus: 'Occupied'};
        }
        // A missing lock does not mean a pending cleaning/service task is Ready.
        const taskSnapshot = await transaction.get(doc(db, 'housekeepingTasks', `${hotelId}_${room}`));
        if (taskSnapshot.exists() && (taskSnapshot.data().hotelId !== hotelId || normalizeRoom(taskSnapshot.data().room) !== room)) throw new Error(`Room ${room}: housekeeping hotel/room mismatch hai.`);
        if (taskSnapshot.exists() && (taskSnapshot.data().maintenanceRequired || taskSnapshot.data().roomReady !== true)) {
          const task = taskSnapshot.data(), closedId = String(task.stayId || '');
          if (!closedId) throw new Error(`Room ${room}: pending quick cleaning task ka checkout record verify karein.`);
          const closedSnapshot = await transaction.get(doc(db, 'hotelStays', closedId));
          if (!closedSnapshot.exists()) throw new Error(`Room ${room}: cleaning task ka guest record nahi mila.`);
          const recovered = restoreClosedRoom(current, {hotelId, room, lastStayId: closedId,
            status: task.maintenanceRequired ? 'maintenance_hold' : 'cleaning'}, closedSnapshot.data(), task,
            {hotelId, room, stayId: closedId});
          const actor = getCurrentActor();
          if (!actor.actorId) throw new Error('Manager login required hai.');
          transaction.set(lockRef, {hotelId, room, activeStayId: '', lastStayId: closedId,
            status: recovered.maintenance ? 'maintenance_hold' : 'cleaning', updatedAt: serverTimestamp()});
          transaction.set(boardRef, {...recovered.board, hotelId, hotelName: hotelName || '', schemaVersion: 2,
            revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
          transaction.set(doc(collection(db, 'auditLogs')), {hotelId, hotelName: hotelName || '', room, stayId: closedId,
            action: 'room_board', actionType: 'room_board', title: `Room ${room} Cleaning Restored`,
            description: 'Verified checkout/cleaning task restored to the room board; guest history and payment records unchanged.',
            ...actor, eventAt: new Date().toISOString(), createdAt: serverTimestamp()});
          return {board: recovered.board, recoveryStatus: 'Cleaning'};
        }
        return null;
      }
      const staySnapshot = await transaction.get(doc(db, 'hotelStays', stayId));
      if (!staySnapshot.exists()) throw new Error('Locked guest stay record nahi mila. Lock delete mat karein.');
      const current = boardSnapshot.exists() ? boardSnapshot.data() : {};
      const stayData = staySnapshot.data();
      let next;
      let recoveryStatus = 'Occupied';
      if (stayData.checkedOut === true || stayData.checkoutCompleted === true) {
        const taskRef = doc(db, 'housekeepingTasks', `${hotelId}_${room}`);
        const taskSnapshot = await transaction.get(taskRef);
        const existingTask = taskSnapshot.exists() ? taskSnapshot.data() : null;
        const previousStayId = existingTask && String(existingTask.stayId || '') !== stayId ? String(existingTask.stayId || '') : '';
        const previousStaySnapshot = previousStayId ? await transaction.get(doc(db, 'hotelStays', previousStayId)) : null;
        const recovered = restoreClosedRoom(current, lock, stayData,
          existingTask, {hotelId, room, stayId, previousStayId,
            previousStay: previousStaySnapshot?.exists() ? previousStaySnapshot.data() : null});
        if (recovered.archivedTask) {
          const actor = getCurrentActor();
          if (!actor?.actorId) throw new Error('Manager login required hai.');
          transaction.set(doc(collection(db, 'auditLogs')), {hotelId, hotelName: hotelName || '',
            action: 'housekeeping', actionType: 'housekeeping', title: `Room ${room} Cleaning Restored`,
            description: `Previous completed cleaning task preserved; current checkout restored to Cleaning.`,
            room, stayId, previousStayId, metadata: {archivedTask: recovered.archivedTask},
            ...actor, eventAt: new Date().toISOString(), createdAt: serverTimestamp()});
        }
        next = recovered.board;
        recoveryStatus = recovered.ready ? 'Ready' : 'Cleaning';
        if (recovered.ready) transaction.delete(lockRef);
        else transaction.set(lockRef, {hotelId, room, activeStayId: '', lastStayId: stayId,
          status: recovered.maintenance ? 'maintenance_hold' : 'cleaning', updatedAt: serverTimestamp()}, {merge: true});
        if (!taskSnapshot.exists() || recovered.archivedTask) transaction.set(taskRef, {...recovered.task,
          hotelName: hotelName || '', updatedAt: serverTimestamp()});
      } else {
        next = restoreOccupiedRoom(current, lock, stayData, {hotelId, room, stayId});
      }
      transaction.set(boardRef, {...next, hotelId, hotelName: hotelName || '', schemaVersion: 2,
        revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
      return {board: next, recoveryStatus};
    });
    if (!restored) return false;
    applyBoardData(restored.board);
    setSingleRoom('');
    setSingleRoomCategory('');
    alert(`Room ${room} ka existing record verify karke ${restored.recoveryStatus} status restore ho gaya. Guest history/payment change nahi hua.`);
    return true;
  };

  const addSingleVacantRoom = async () => {
    const room = normalizeRoom(singleRoom);
    const roomCategory = normalizeRoomCategory(singleRoomCategory);

    if (!room) {
      return alert(
        "Room number enter karo. Example: A1"
      );
    }

    try {
      if (await restoreExistingOccupiedRoom(room)) return;
    } catch (error) {
      console.error('Occupied room recovery failed:', error);
      return alert(error?.message || 'Room recovery nahi hui.');
    }

    const vacantIndex =
      vacantRooms.findIndex(
        (item) =>
          normalizeRoom(item.room) ===
          room
      );

    if (vacantIndex >= 0) {
      const nextVacant = [
        ...vacantRooms,
      ];

      nextVacant[vacantIndex] = {
        ...nextVacant[vacantIndex],
        isSir: true,
        category:
          roomCategory ||
          getRoomCategory(nextVacant[vacantIndex]),
        roomCategory:
          roomCategory ||
          getRoomCategory(nextVacant[vacantIndex]),
      };

      const sortedVacant =
        sortRooms(nextVacant);

      const previousVacant = vacantRooms;
      setVacantRooms(sortedVacant);
      setSingleRoom("");
      setSingleRoomCategory("");

      const boardSaved = await saveBoard(
        sortedVacant,
        occupiedRooms,
        checkoutRooms,
        serviceRooms
      );

      if (!boardSaved) {

        return;
      }

      return;
    }

    const existsInOccupied =
      occupiedRooms.some(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) === room
      );

    const existsInCheckout =
      checkoutRooms.some(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) === room
      );

    if (
      existsInOccupied ||
      existsInCheckout
    ) {
      return alert(
        "Ye room Occupied ya Check Out list me hai."
      );
    }

    const nextVacant = sortRooms([
      ...vacantRooms,
      {
        room,
        isSir: false,
        category: roomCategory,
        roomCategory,
      },
    ]);

    const previousVacant = vacantRooms;
    setVacantRooms(nextVacant);
    setSingleRoom("");
    setSingleRoomCategory("");

    const boardSaved = await saveBoard(
      nextVacant,
      occupiedRooms,
      checkoutRooms,
      serviceRooms
    );

    if (!boardSaved) {

    }
  };

  const addFloorRooms = async () => {
    const floor = normalizeRoom(
      floorLetter
    ).replace(/[0-9]/g, "");

    const roomCategory = normalizeRoomCategory(
      floorRoomCategory
    );

    const count = Number(
      floorRoomCount
    );

    if (!floor) {
      return alert(
        "Floor enter karo. Example: A"
      );
    }

    if (!count || count <= 0) {
      return alert(
        "Valid room count enter karo."
      );
    }

    const roomsToAdd = [];

    for (
      let roomNumber = 1;
      roomNumber <= count;
      roomNumber += 1
    ) {
      const room = `${floor}${roomNumber}`;

      const exists =
        vacantRooms.some(
          (item) =>
            normalizeRoom(item.room) ===
            room
        ) ||
        occupiedRooms.some(
          (item) =>
            normalizeRoom(
              getRoomValue(item)
            ) === room
        ) ||
        checkoutRooms.some(
          (item) =>
            normalizeRoom(
              getRoomValue(item)
            ) === room
        );

      if (!exists) {
        roomsToAdd.push({
          room,
          isSir: false,
          category: roomCategory,
          roomCategory,
        });
      }
    }

    if (roomsToAdd.length === 0) {
      return alert(
        "Koi naya room add nahi hua."
      );
    }

    const nextVacant = sortRooms([
      ...vacantRooms,
      ...roomsToAdd,
    ]);

    const previousVacant = vacantRooms;
    setVacantRooms(nextVacant);
    setFloorLetter("");
    setFloorRoomCount("");
    setFloorRoomCategory("");

    const boardSaved = await saveBoard(
      nextVacant,
      occupiedRooms,
      checkoutRooms,
      serviceRooms
    );

    if (!boardSaved) {

    }
  };

  const formatDayBookOut = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const dateText = date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
    const timeText = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${dateText} ${timeText}`;
  };

  const formatAdvanceOriginDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const buildDayBookEntryPayload = ({
    entryType,
    category,
    title,
    amount,
    paymentMode = "",
    transactionReference = "",
    room = "",
    guestName = "",
    stayId = "",
    eventAt = new Date(),
    note = "",
    hideFromRegister = false,
    ledgerSerial = "",
    ledgerRoom = "",
    ledgerTariff = 0,
    ledgerCash = 0,
    ledgerOnline = 0,
    ledgerFab = 0,
    ledgerBrevistay = 0,
    ledgerCashLabel = "",
    ledgerOnlineLabel = "",
    ledgerFabLabel = "",
    ledgerBrevistayLabel = "",
    ledgerCheckIn = "",
    ledgerCheckOut = "",
    ledgerBalance = 0,
    ledgerGuestName = "",
    ledgerAdvance = 0,
    ledgerExpense = 0,
    ledgerNote = "",
    ledgerTransactionReference = "",
    source = "manager_pms",
    operationId = "",
  }) => {
    const eventDate =
      eventAt instanceof Date
        ? eventAt
        : new Date(eventAt);

    return {
      hotelId,
      hotelName: hotelName || "",
      businessDate:
        getBusinessDateKey(
          eventDate
        ),
      entryType,
      category,
      title,
      amount: Number(amount || 0),
      paymentMode,
      transactionReference,
      room,
      guestName,
      stayId,
      note,
      hideFromRegister:
        hideFromRegister === true,
      ledgerSerial,
      ledgerRoom:
        ledgerRoom || room,
      ledgerTariff:
        Number(ledgerTariff || 0),
      ledgerCash:
        Number(ledgerCash || 0),
      ledgerOnline:
        Number(ledgerOnline || 0),
      ledgerFab:
        Number(ledgerFab || 0),
      ledgerBrevistay:
        Number(ledgerBrevistay || 0),
      ledgerCashLabel:
        String(ledgerCashLabel || "").trim(),
      ledgerOnlineLabel:
        String(ledgerOnlineLabel || "").trim(),
      ledgerFabLabel:
        String(ledgerFabLabel || "").trim(),
      ledgerBrevistayLabel:
        String(ledgerBrevistayLabel || "").trim(),
      ledgerCheckIn,
      ledgerCheckOut,
      ledgerBalance:
        Number(ledgerBalance || 0),
      ledgerGuestName:
        ledgerGuestName ||
        guestName,
      ledgerAdvance:
        Number(ledgerAdvance || 0),
      ledgerExpense:
        Number(ledgerExpense || 0),
      ledgerNote:
        ledgerNote || note,
      ledgerTransactionReference:
        ledgerTransactionReference ||
        transactionReference ||
        "",
      eventAt:
        eventDate.toISOString(),
      createdAt:
        serverTimestamp(),
      updatedAt:
        serverTimestamp(),
      source,
      operationId:
        String(operationId || "").trim(),
    };
  };

  const addDayBookEntry = async (entry) => {
    await addDoc(
      collection(db, "dayBookEntries"),
      buildDayBookEntryPayload(entry)
    );
  };

  const queueDayBookEntry = (
    writer,
    entryReference,
    entry
  ) => {
    writer.set(
      entryReference,
      buildDayBookEntryPayload(entry),
      { merge: true }
    );
  };


  /* ------------------------------------------------------------------------ */
  /*                  Room Board Guest Search / Quick Check-In                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    if (!hotelId) {
      setBoardGuestProfiles([]);
      return () => { active = false; };
    }

    setBoardGuestSearchLoading(true);

    loadGuestProfiles({ hotelId, includeBlacklisted: true })
      .then((list) => {
        if (!active) return;
        setBoardGuestProfiles(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        console.warn("Room board guest search load error:", error);
        if (active) setBoardGuestProfiles([]);
      })
      .finally(() => {
        if (active) setBoardGuestSearchLoading(false);
      });

    return () => { active = false; };
  }, [hotelId]);

  const boardGuestMatches = useMemo(() => {
    const queryText = String(boardGuestSearch || "").trim().toLowerCase();
    if (!queryText) return [];

    const digits = queryText.replace(/\D/g, "");
    const compact = queryText.replace(/\s+/g, "");

    return boardGuestProfiles
      .filter((profile) => {
        const name = String(profile?.name || "").toLowerCase();
        const phone = String(profile?.phoneNumber || profile?.phone || "").replace(/\D/g, "");
        const idNumber = String(profile?.idNumber || "").replace(/\s+/g, "").toLowerCase();
        return name.includes(queryText) || (digits && phone.includes(digits)) || idNumber.includes(compact);
      })
      .slice(0, 8);
  }, [boardGuestProfiles, boardGuestSearch]);

  const boardGuestHasExactMatch = useMemo(() => {
    const queryText = String(boardGuestSearch || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (!queryText) return false;

    const digits = String(boardGuestSearch || "").replace(/\D/g, "");
    const compactId = String(boardGuestSearch || "").replace(/\s+/g, "").trim().toLowerCase();

    return boardGuestProfiles.some((profile) => {
      const name = String(profile?.name || "").replace(/\s+/g, " ").trim().toLowerCase();
      const phone = String(profile?.phoneNumber || profile?.phone || "").replace(/\D/g, "");
      const idNumber = String(profile?.idNumber || "").replace(/\s+/g, "").trim().toLowerCase();

      return (
        (name && name === queryText) ||
        (digits && phone && phone === digits) ||
        (compactId && idNumber && idNumber === compactId)
      );
    });
  }, [boardGuestProfiles, boardGuestSearch]);

  const resetBoardGuestSearch = () => {
    setBoardGuestSearch("");
    setSelectedBoardGuest(null);
    setBoardGuestRoom("");
  };

  const openGuestCheckIn = (room, options = {}) => {
    const visibleCard = occupiedRooms.find(item => roomKey(item) === normalizeRoom(room));
    setCheckInRoomContext({...captureCheckInContext(room, visibleCard), booking:options.booking || null});
    setJanakpuriClaim(options.claimId || '');
    setCheckInPrefillProfile(options?.profile || null);
    setCheckInPrefillName(String(options?.name || "").trim());
    setSelectedCheckInRoom(normalizeRoom(room));
  };

  const openBoardSearchFullCheckIn = () => {
    const room = normalizeRoom(boardGuestRoom);
    if (!room) return alert("Pehle Ready Room select karo.");

    const typedName = String(selectedBoardGuest?.name || boardGuestSearch || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!selectedBoardGuest && !typedName) {
      return alert("Guest ka naam ya existing guest select karo.");
    }

    openGuestCheckIn(room, {
      profile: selectedBoardGuest || null,
      name: typedName,
    });
  };

  const saveBoardSearchNameOnly = async () => {
    const room = normalizeRoom(boardGuestRoom);
    const typedName = String(boardGuestSearch || "").replace(/\s+/g, " ").trim();

    if (!room) return alert("Pehle Ready Room select karo.");
    if (!typedName) return alert("New guest ka naam enter karo.");

    if (await quickOccupyRoom(room, typedName)) resetBoardGuestSearch();
  };


  const toggleRushTempNameMode =
    async () => {
      const nextValue =
        !rushTempNameEnabled;

      setRushTempNameEnabled(
        nextValue
      );

      try {
        await runTransaction(db, async transaction => {
          const reference = getBoardDocRef(); const snapshot = await transaction.get(reference);
          const data = snapshot.exists() ? snapshot.data() : {};
          transaction.set(reference, {hotelId, hotelName: hotelName || '', rushTempNameEnabled: nextValue, schemaVersion: 2, revision: Number(data.revision || 0) + 1, updatedAt: serverTimestamp()}, {merge: true});
        });

        try {
          const cached =
            localStorage.getItem(
              BOARD_KEY
            );

          const parsed = cached
            ? JSON.parse(cached)
            : {};

          localStorage.setItem(
            BOARD_KEY,
            JSON.stringify({
              ...parsed,
              rushTempNameEnabled:
                nextValue,
              updatedAt:
                Date.now(),
            })
          );
        } catch (cacheError) {
          console.warn(
            "Rush mode local cache error:",
            cacheError
          );
        }
      } catch (error) {
        setRushTempNameEnabled(
          !nextValue
        );

        console.error(
          "Rush temp-name mode save error:",
          error
        );

        alert(
          "Rush Temp Name mode save nahi hua."
        );
      }
    };

  const handleReadyRoomCheckIn = async (item) => {
    const room = normalizeRoom(getRoomValue(item));
    if (!room) return;

    // Check-In always opens the full form. Name-only rush flow is inside the form.
    openGuestCheckIn(room);
  };

  const quickOccupyRoom = async (roomValue, temporaryGuestName = "") => {
    const room = normalizeRoom(roomValue);
    if (!room || walkInSaving || checkInSubmitLockRef.current) return false;
    checkInSubmitLockRef.current = true;
    setWalkInSaving(true);
    const operationId = doc(collection(db, 'auditLogs')).id;
    const occupiedAt = new Date().toISOString();
    try {
      const activeCandidates = (await getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)))).docs
        .filter(record => normalizeRoom(record.data().room) === room && isStayActive(record.data()));
      const committed = await runTransaction(db, async transaction => {
        const boardRef = getBoardDocRef(), lockRef = getRoomLifecycleLockRef(room);
        const [boardSnapshot, lockSnapshot] = await Promise.all([transaction.get(boardRef), transaction.get(lockRef)]);
        const current = boardSnapshot.exists() ? boardSnapshot.data() : {};
        const lock = lockSnapshot.exists() ? lockSnapshot.data() : {};
        const candidateSnapshots = await Promise.all(activeCandidates.map(record => transaction.get(record.ref)));
        assertNoActiveRoomStays(candidateSnapshots.filter(record => record.exists()).map(record => ({...record.data(), id: record.id})), hotelId, room);
        const next = occupyReadyRoom(current, lock, {hotelId, room, name: temporaryGuestName, operationId, occupiedAt});
        if (next === current) return current;
        const data = {...next, hotelId, hotelName: hotelName || '', schemaVersion: 2,
          revision: Number(current.revision || 0) + 1, updatedAt: serverTimestamp()};
        transaction.set(boardRef, data, {merge: true});
        transaction.set(lockRef, {hotelId, room, activeStayId: '', status: 'details_pending',
          quickOccupancyId: operationId, updatedAt: serverTimestamp()});
        return data;
      });
      applyBoardData(committed);
      return true;
    } catch (error) {
      console.error('Name-only room occupancy failed:', error);
      try {const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data());} catch (_) {}
      alert(error?.message || 'Name Only save nahi hua. Connection check karke dobara try karein.');
      return false;
    } finally {
      checkInSubmitLockRef.current = false;
      setWalkInSaving(false);
    }
  };

  const handleWalkInCheckIn = async (guestData, expectedRoomContext = captureCheckInContext(guestData?.room || selectedCheckInRoom)) => {
    const reservedBooking = expectedRoomContext?.booking || null;
    if (reservedBooking) guestData = {...guestData, room:reservedBooking.room,
      tariff:reservedBooking.finalPrice, baseTariff:reservedBooking.finalPrice, sourcePaidAmount:0, sourcePaymentMode:'',
      expectedCheckOutAt:reservedBooking.endAt, bookingSource:reservedBooking.source==='reception_advance'?'Walk-in Advance':'Website Janakpuri',
      complimentary:reservedBooking.finalPrice===0};
    const room = normalizeRoom(
      guestData?.room || selectedCheckInRoom
    );

    if (
      !room ||
      walkInSaving ||
      checkInSubmitLockRef.current
    ) {
      return;
    }

    /*
     * State render ka wait kiye bina lock ho jaye.
     * Isse same button/keyboard submit se duplicate stay + duplicate Day Book
     * row create nahi hogi.
     */
    checkInSubmitLockRef.current = true;
    setWalkInSaving(true);

    try {
      const pendingDayBookEntries = [];
      const addDayBookEntry = async (entry) => pendingDayBookEntries.push(entry);
      const checkInDate = new Date(guestData.checkInAt);
      const expectedCheckOutDate = new Date(
        guestData.expectedCheckOutAt
      );

      /*
       * Read-only preflight includes active stays whose room lock is missing.
       * No history record is automatically closed or resurrected here.
       */
      const lifecycleState =
        await reconcileHotelLifecycle(
          getCurrentBoardSnapshot(),
          { reason: `before_checkin_${room}` }
        );

      assertNoActiveRoomStays(lifecycleState.activeStaysByRoom?.[room] || [], hotelId, room);
      const activeSameRoomStay =
        lifecycleState.survivorByRoom?.[room] ||
        null;

      if (activeSameRoomStay) {
        setSelectedCheckInRoom("");
        setQuickCheckInOpen(false);
        setCheckInPrefillProfile(null);
        setCheckInPrefillName("");

        alert(
          `Room ${room} me active guest already checked-in hai. Existing stay/room lock mila hai; duplicate entry create nahi hui. Agar board Ready dikhe toh existing stay verify karna zaroori hai.`
        );

        return;
      }

      const checkInBoardBase =
        lifecycleState.board ||
        getCurrentBoardSnapshot();

      const sourceRoomItem =
        (checkInBoardBase.vacantRooms || []).find(
          (item) =>
            normalizeRoom(getRoomValue(item)) === room
        ) ||
        getBoardRoomItem(room);

      const roomCategory =
        getRoomCategory(sourceRoomItem);

      const savedGuests = await saveGuestProfiles({
        hotelId,
        hotelName: hotelName || "",
        guests: guestData.guests,
        incrementVisitCount: false,
      });

      const primaryGuest = savedGuests[0];

      const checkInPaymentMode =
        String(guestData.paymentMode || "Cash").trim();
      const sourcePaidAmount = Math.max(0, Number(guestData.sourcePaidAmount || 0));

      const hasSplitGuestPayment =
        guestData.guestCashReceived !== undefined ||
        guestData.guestOnlineReceived !== undefined;

      const legacyGuestReceived = Math.max(
        0,
        Number(guestData.guestReceivedAmount || 0)
      );

      const guestCashReceived = hasSplitGuestPayment
        ? Math.max(0, Number(guestData.guestCashReceived || 0))
        : checkInPaymentMode.toLowerCase() === "cash"
        ? legacyGuestReceived
        : 0;

      const guestOnlineReceived = hasSplitGuestPayment
        ? Math.max(0, Number(guestData.guestOnlineReceived || 0))
        : checkInPaymentMode.toLowerCase() !== "cash"
        ? legacyGuestReceived
        : 0;

      const guestReceivedAmount =
        guestCashReceived + guestOnlineReceived;

      const priorAdvance = readPriorCheckInAdvance(guestData, Number(reservedBooking?.advanceAmount || 0), Number(guestData.tariff || 0));
      const checkInAdvance =
        sourcePaidAmount + guestReceivedAmount + Number(reservedBooking?.advanceAmount || 0) + priorAdvance.amount;

      const roomTariff = Math.max(0, Number(guestData.tariff || 0));
      const extraPersonCount = Math.max(
        0,
        Math.floor(Number(guestData.extraPersonCount || 0))
      );
      const extraPersonRate = Math.max(
        0,
        Number(guestData.extraPersonRate || 0)
      );
      const extraPersonCharge = Math.max(
        0,
        Number(
          guestData.extraPersonCharge ??
            extraPersonCount * extraPersonRate
        )
      );
      const earlyCheckInCharge = Math.max(0, Number(guestData.earlyCheckInCharge || 0));
      const lateCheckOutCharge = Math.max(0, Number(guestData.lateCheckOutCharge || 0));

      const initialExtraItems = [
        extraPersonCharge > 0 ? {
          type: "extra", title: "Extra Person Charge", quantity: extraPersonCount || 1,
          rate: extraPersonRate || extraPersonCharge, amount: extraPersonCharge, source: "extra_person",
          note: `${extraPersonCount || 1} extra person(s) × ₹${extraPersonRate || extraPersonCharge}`,
          addedAt: checkInDate.toISOString(),
        } : null,
        earlyCheckInCharge > 0 ? {
          type: "extra", title: "Early Check-In Charge", quantity: 1,
          rate: earlyCheckInCharge, amount: earlyCheckInCharge, source: "early_check_in",
          note: "Added during check-in", addedAt: checkInDate.toISOString(),
        } : null,
        lateCheckOutCharge > 0 ? {
          type: "extra", title: "Late Check-Out Charge", quantity: 1,
          rate: lateCheckOutCharge, amount: lateCheckOutCharge, source: "late_checkout",
          note: "Added during check-in", addedAt: checkInDate.toISOString(),
        } : null,
      ].filter(Boolean);
      const initialExtraTotal =
        extraPersonCharge +
        earlyCheckInCharge +
        lateCheckOutCharge;
      const initialGrandTotal = roomTariff + initialExtraTotal;
      const allocatedPaidTotal = Math.min(checkInAdvance, initialGrandTotal);
      const initialBalance = Math.max(0, initialGrandTotal - allocatedPaidTotal);
      const excessAdvanceCredit = Math.max(0, checkInAdvance - initialGrandTotal);

      const priorBookingAdvance = Number(reservedBooking?.advanceAmount || 0);
      const initialPayments = [
        priorAdvancePayment(priorAdvance, checkInDate.toISOString()),
        priorBookingAdvance > 0 ? {type:'payment',title:'Previously received booking advance',amount:priorBookingAdvance,
          quantity:1,rate:priorBookingAdvance,paymentMode:'Advance Adjusted',source:'booking_advance_applied',
          bookingId:reservedBooking.id,addedAt:checkInDate.toISOString(),note:'Already recorded in Day Book when received; no new cash'} : null,
        sourcePaidAmount > 0 ? {
          type: "payment",
          title: `${guestData.sourcePaymentMode || guestData.bookingSource || "OTA"} Paid Amount`,
          amount: sourcePaidAmount, quantity: 1, rate: sourcePaidAmount,
          paymentMode: guestData.sourcePaymentMode || guestData.bookingSource || "OTA",
          transactionReference: guestData.otaBookingId || guestData.transactionReference || "",
          note: "Booking Source Prepaid Amount", source: "check_in_source_prepaid",
          addedAt: checkInDate.toISOString(),
        } : null,
        guestCashReceived > 0 ? {
          type: "payment", title: "Guest Cash Received", amount: guestCashReceived,
          quantity: 1, rate: guestCashReceived, paymentMode: "Cash",
          transactionReference: "",
          note: "Check-In Guest Cash Payment", source: "check_in_guest_payment",
          addedAt: checkInDate.toISOString(),
        } : null,
        guestOnlineReceived > 0 ? {
          type: "payment", title: "Guest Online Received", amount: guestOnlineReceived,
          quantity: 1, rate: guestOnlineReceived, paymentMode: "UPI",
          transactionReference:
            guestData.onlineTransactionReference ||
            guestData.transactionReference ||
            "",
          note: "Check-In Guest Online Payment", source: "check_in_guest_payment",
          addedAt: checkInDate.toISOString(),
        } : null,
      ].filter(Boolean);

      const stayPayload = {
        ...(reservedBooking ? {advanceBookingId:reservedBooking.id,bookingAdvanceApplied:priorBookingAdvance,...(hotelId===JANAKPURI_HOTEL_ID?{janakpuriBookingId:reservedBooking.id}:{})} : {}),
        hotelId,
        hotelName: hotelName || "",
        room,
        category: roomCategory,
        roomCategory,
        adRegisterSerialNo: guestData.adRegisterSerialNo,
        primaryGuestId: primaryGuest?.profileId || "",
        guestProfileIds: savedGuests.map((guest) => guest.profileId),
        guests: savedGuests.map((guest) => ({
          profileId: guest.profileId,
          isPrimary: guest.isPrimary,
          name: guest.name,
          phoneNumber: guest.phoneNumber,
          age: guest.age,
          gender: guest.gender,
          nationality: guest.nationality,
          address: guest.address,
          relation: guest.relation,
          idType: guest.idType,
          idNumber: guest.idNumber,
          idFrontUrl: guest.idFrontUrl,
          idBackUrl: guest.idBackUrl,
        })),
        guestName: primaryGuest?.name || guestData.guestName,
        phoneNumber: primaryGuest?.phoneNumber || guestData.phoneNumber,
        adults: savedGuests.length,
        children: guestData.children,
        idType: primaryGuest?.idType || guestData.idType,
        idNumber: primaryGuest?.idNumber || guestData.idNumber,
        address: primaryGuest?.address || guestData.address,
        checkInAt: checkInDate.toISOString(),
        expectedCheckOutAt:
          expectedCheckOutDate.toISOString(),
        tariff: guestData.tariff,
        baseTariff: guestData.baseTariff ?? guestData.tariff,
        stayType: guestData.stayType || "",
        complimentary: guestData.complimentary === true,
        earlyCheckIn: guestData.earlyCheckIn === true,
        earlyCheckInCharge,
        lateCheckOut: guestData.lateCheckOut === true,
        lateCheckOutCharge,

        /*
         * Full actual amount received at Check-In.
         * Payment history/receipt ke liye preserve rahega.
         */
        advance:
          checkInAdvance,

        priorAdvanceAmount: priorAdvance.amount,
        priorAdvanceMode: priorAdvance.paymentMode,
        priorAdvanceReference: priorAdvance.transactionReference,
        sourcePaidAmount,
        guestCashReceived,
        guestOnlineReceived,
        guestReceivedAmount,
        onlineTransactionReference:
          guestData.onlineTransactionReference ||
          guestData.transactionReference ||
          "",
        sourcePaymentMode:
          guestData.sourcePaymentMode || "",

        extraPersonCount,
        extraPersonRate,
        extraPersonCharge,

        /*
         * Sirf room tariff tak ka amount bill me allocated paid maana jayega.
         * Tariff se extra amount separate advance credit hai.
         */
        paidTotal:
          allocatedPaidTotal,

        balance:
          initialBalance,

        excessAdvanceCredit,

        paidAllocationNormalized:
          true,

        foodItems: [],
        foodTotal: 0,
        foodPaidTotal: 0,
        foodPaymentHistory: [],

        laundryItems: [],
        laundryTotal: 0,
        extraItems: initialExtraItems,
        extraTotal: initialExtraTotal,

        payments: initialPayments,

        paymentMode:
          guestCashReceived > 0 &&
          guestOnlineReceived > 0
            ? "Mixed"
            : guestOnlineReceived > 0
            ? "UPI"
            : guestCashReceived > 0
            ? "Cash"
            : guestData.paymentMode || "Pending",
        transactionReference:
          guestOnlineReceived > 0
            ? guestData.onlineTransactionReference ||
              guestData.transactionReference ||
              ""
            : "",
        bookingSource: guestData.bookingSource,
        notes: guestData.notes,
        businessDate: getBusinessDateKey(checkInDate),
        status: "checked_in",
        source: "manager_walk_in",
        occupancyLockId: getRoomLifecycleLockId(room),
        quickOccupancyId: expectedRoomContext.quickOccupancyId || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const stayDocument = doc(
        collection(db, "hotelStays")
      );

      const roomLifecycleLockRef =
        getRoomLifecycleLockRef(room);

      /*
       * Stay create + room occupancy lock ek transaction me.
       * Do systems same Ready room ko ek saath check-in nahi kar sakte.
       */
      const commitCheckIn = () => runTransaction(
        db,
        async (transaction) => {
          const lockSnapshot =
            await transaction.get(
              roomLifecycleLockRef
            );

          if (reservedBooking) {
            const bookingRef=doc(db,'bookings',reservedBooking.id);
            const freshBooking=await transaction.get(bookingRef);
            assertAdvanceForCheckIn(freshBooking.exists()?freshBooking.data():null,reservedBooking,hotelId,room);
            transaction.update(bookingRef,{advanceAppliedStayId:stayDocument.id,advanceAppliedAmount:priorBookingAdvance,
              checkedIn:true,status:'checked_in',stayId:stayDocument.id,updatedAt:serverTimestamp()});
          }

          const boardReference = getBoardDocRef();
          const cloudBoardSnapshot = await transaction.get(boardReference);
          const cloudBoard = normalizeBoardData(cloudBoardSnapshot.exists() ? cloudBoardSnapshot.data() : {});
          assertCheckInContext(cloudBoard, lockSnapshot.exists() ? lockSnapshot.data() : {}, expectedRoomContext, room);
          const existingCard = cloudBoard.occupiedRooms.find(item => roomKey(item) === room);
          if (existingCard && !existingCard.detailsPending && existingCard.status !== 'details_pending') throw new Error(`Room ${room} already occupied hai.`);
          if (cloudBoard.checkoutRooms.some(item => roomKey(item) === room) || cloudBoard.serviceRooms.some(item => roomKey(item) === room)) throw new Error(`Room ${room} cleaning/service mein hai.`);
          if (!cloudBoard.vacantRooms.some(item => roomKey(item) === room) && !existingCard?.detailsPending && existingCard?.status !== 'details_pending') throw new Error(`Room ${room} Ready nahi hai.`);
          const profileSnapshots = await Promise.all(savedGuests.map(guest => transaction.get(doc(db, 'guestProfiles', guest.profileId))));
                    if (lockSnapshot.exists()) {
            const lockData =
              lockSnapshot.data();
            const lockedStayId = String(
              lockData?.activeStayId || ""
            );
            const lockStatus = String(
              lockData?.status || ""
            )
              .trim()
              .toLowerCase();

            if (lockedStayId) {
              const lockedStaySnapshot =
                await transaction.get(
                  doc(
                    db,
                    "hotelStays",
                    lockedStayId
                  )
                );

              if (
                lockedStaySnapshot.exists() &&
                isStayActive(
                  lockedStaySnapshot.data()
                )
              ) {
                throw new Error(
                  `Room ${room} already occupied hai. Dashboard refresh ho raha hai.`
                );
              }
            }

            if (
              [
                "checkout_pending",
                "cleaning",
                "maintenance_hold",
              ].includes(lockStatus)
            ) {
              throw new Error(
                `Room ${room} abhi Ready nahi hai (${lockStatus.replace(
                  /_/g,
                  " "
                )}).`
              );
            }
          }

          const allocation = allocateAdRegisterSerial(cloudBoardSnapshot.data() || {},
            {mode: guestData.adRegisterSerialMode || 'auto', value: guestData.adRegisterSerialNo,
              registerId: guestData.adRegisterId || 'legacy', historicalAssignments: lifecycleState.serialAssignments,
              allowReuse: guestData.adRegisterAllowReuse === true, reuseReason: guestData.adRegisterReuseReason}, stayDocument.id);
          const serial = allocation.serial;
          stayPayload.adRegisterSerialNo = serial;
          stayPayload.adRegisterId = allocation.registerId;
          stayPayload.adRegisterSerialReused = allocation.reusedSerial;
          stayPayload.adRegisterReuseReason = allocation.reuseReason;
          stayPayload.adRegisterDuplicateStayIds = allocation.duplicateStayIds;
          occupiedRoom.adRegisterSerialNo = serial;
          occupiedRoom.adRegisterId = allocation.registerId;
          const committedBoard = {
            ...cloudBoard, hotelId, hotelName: hotelName || "", schemaVersion: 2, revision: Number(cloudBoardSnapshot.data()?.revision || 0) + 1,
            vacantRooms: cloudBoard.vacantRooms.filter(item => roomKey(item) !== room),
            occupiedRooms: [...cloudBoard.occupiedRooms.filter(item => roomKey(item) !== room), occupiedRoom],
            ...allocation.boardFields,
            updatedAt: serverTimestamp(),
          };
          transaction.set(boardReference, committedBoard, {mergeFields: Object.keys(committedBoard)});
          if (guestData.adRegisterSerialMode && guestData.adRegisterSerialMode !== 'auto') {
            transaction.set(doc(collection(db, 'auditLogs')), {hotelId, hotelName: hotelName || '',
              action: 'registration', actionType: 'registration', title: allocation.reusedSerial ? 'A&D Serial Reused' : guestData.adRegisterSerialMode === 'new_register' ? 'New A&D Register' : 'Manual A&D Serial',
              description: `Room ${room}: A&D serial ${serial} assigned.${allocation.reuseReason ? ' Reason: ' + allocation.reuseReason : ''}`, room, stayId: stayDocument.id,
              metadata: {serial, registerId: allocation.registerId, previousRegisterId: cloudBoardSnapshot.data()?.adRegisterId || 'legacy',
                checkInAt: checkInDate.toISOString(), reusedSerial: allocation.reusedSerial, reuseReason: allocation.reuseReason,
                duplicateStayIds: allocation.duplicateStayIds},
              ...getCurrentActor(), eventAt: checkInDate.toISOString(), createdAt: serverTimestamp()});
          }
          profileSnapshots.forEach((profile, index) => {
            if (profile.exists()) transaction.update(profile.ref, {visitCount: Number(profile.data().visitCount || 0) + 1, lastVisitAt: checkInDate.toISOString(), updatedAt: serverTimestamp()});
          });
          stayPayload.currentRoomDayBookEntryId = `CHECKIN_${stayDocument.id}_0`;
          pendingDayBookEntries.forEach((entry, index) => {
            queueDayBookEntry(transaction, doc(db, 'dayBookEntries', `CHECKIN_${stayDocument.id}_${index}`), {...entry,
              note: entry.category === 'room' ? String(entry.note || '').replace(/A&D \S+/, `A&D ${serial}`) : entry.note,
              ledgerSerial: entry.category === 'room' ? String(serial) : entry.ledgerSerial, operationId: `CHECKIN_${stayDocument.id}`});
          });
                    transaction.set(
            stayDocument,
            stayPayload
          );

          transaction.set(
            roomLifecycleLockRef,
            {
              hotelId,
              hotelName: hotelName || "",
              room,
              activeStayId: stayDocument.id,
              status: "occupied",
              acquiredAt:
                checkInDate.toISOString(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      );

      const checkInBookingSource =
        String(
          guestData.bookingSource || "Walk-in"
        )
          .trim()
          .toLowerCase();

      const checkInTariff = roomTariff;

      /*
       * Payment allocation rule:
       * 1) OTA/FAB prepaid amount room tariff me pehle allocate hoga.
       * 2) Guest se received payment remaining room tariff ko fill karega.
       * 3) Guest payment ka jo hissa current tariff se extra bachega,
       *    wahi Advance & Pending Received box me jayega.
       */
      const sourceAppliedToRoom =
        Math.min(
          sourcePaidAmount,
          checkInTariff
        );

      const tariffAfterSource =
        Math.max(
          0,
          checkInTariff -
            sourceAppliedToRoom - priorBookingAdvance - priorAdvance.amount
        );

      const cashAppliedToRoom =
        Math.min(
          guestCashReceived,
          tariffAfterSource
        );

      const tariffAfterCash =
        Math.max(
          0,
          tariffAfterSource -
            cashAppliedToRoom
        );

      const onlineAppliedToRoom =
        Math.min(
          guestOnlineReceived,
          tariffAfterCash
        );

      const guestAppliedToRoom =
        cashAppliedToRoom +
        onlineAppliedToRoom;

      const guestExcessAdvance =
        Math.max(
          0,
          guestReceivedAmount -
            guestAppliedToRoom
        );

      const cashExcessAdvance =
        Math.max(
          0,
          guestCashReceived -
            cashAppliedToRoom
        );

      const onlineExcessAdvance =
        Math.max(
          0,
          guestOnlineReceived -
            onlineAppliedToRoom
        );

      const isCashGuestPayment =
        guestCashReceived > 0 &&
        guestOnlineReceived <= 0;

      const isFabBooking =
        checkInBookingSource === "fab";

      const isBrevistayBooking =
        checkInBookingSource ===
        "brevistay";

      const isOtherOtaBooking =
        checkInBookingSource === "ota";

      const guestIsOnline =
        guestOnlineReceived > 0;

      const checkInTimeText =
        checkInDate.toLocaleTimeString(
          "en-IN",
          {
            hour: "2-digit",
            minute: "2-digit",
          }
        );

      await addDayBookEntry({
        entryType: "revenue",
        category: "room",
        title: "Room Rent / Check-In",
        amount: guestData.tariff,
        paymentMode:
          checkInPaymentMode,
        transactionReference:
          guestData.transactionReference || "",
        room,
        guestName:
          primaryGuest?.name ||
          guestData.guestName ||
          "",
        stayId: stayDocument.id,
        eventAt: checkInDate,
        note: `Check-In • A&D ${guestData.adRegisterSerialNo || "-"}`,

        ledgerSerial:
          guestData.adRegisterSerialNo || "",
        ledgerRoom: room,
        ledgerTariff:
          Number(
            guestData.tariff || 0
          ),
        ledgerCash:
          cashAppliedToRoom,
        ledgerOnline:
          onlineAppliedToRoom +
          (isOtherOtaBooking
            ? sourceAppliedToRoom
            : 0),
        ledgerFab:
          isFabBooking
            ? sourceAppliedToRoom
            : 0,
        ledgerBrevistay:
          isBrevistayBooking
            ? sourceAppliedToRoom
            : 0,
        ledgerCheckIn:
          checkInTimeText,
        ledgerCheckOut:
          formatDayBookOut(
            expectedCheckOutDate
          ),
        ledgerBalance:
          initialBalance,
        ledgerGuestName:
          primaryGuest?.name ||
          guestData.guestName ||
          "",
        ledgerTransactionReference:
          guestData.onlineTransactionReference ||
          guestData.transactionReference ||
          "",
      });

      for (const extraItem of initialExtraItems) {
        await addDayBookEntry({
          entryType: "revenue", category: "extra", title: extraItem.title, amount: extraItem.amount,
          paymentMode: "", room, guestName: primaryGuest?.name || guestData.guestName || "",
          stayId: stayDocument.id, eventAt: checkInDate, note: extraItem.note,
          hideFromRegister: true, source: extraItem.source,
        });
      }

      if (sourcePaidAmount > 0) {
        await addDayBookEntry({
          entryType: "payment",
          category: "source_prepaid",
          title: `${guestData.sourcePaymentMode || guestData.bookingSource || "OTA"} Paid Amount`,
          amount: sourcePaidAmount,
          paymentMode:
            guestData.sourcePaymentMode ||
            guestData.bookingSource ||
            "OTA",
          transactionReference:
            guestData.otaBookingId ||
            guestData.transactionReference ||
            "",
          room,
          guestName:
            primaryGuest?.name ||
            guestData.guestName ||
            "",
          stayId: stayDocument.id,
          eventAt: checkInDate,
          note: "Booking Source Prepaid Amount",
          hideFromRegister: true,
        });
      }

      const sourceExcessAdvance = Math.max(
        0,
        sourcePaidAmount - sourceAppliedToRoom
      );

      const totalAdvanceCredit = Math.max(
        0,
        Number(stayPayload.excessAdvanceCredit || 0)
      );

      const advanceCandidates = [
        {
          paymentMode: "Cash",
          amount: cashExcessAdvance,
          transactionReference: "",
          source: "checkin_cash_excess",
        },
        {
          paymentMode: "UPI",
          amount: onlineExcessAdvance,
          transactionReference:
            guestData.onlineTransactionReference ||
            guestData.transactionReference ||
            "",
          source: "checkin_online_excess",
        },
        {
          paymentMode:
            guestData.sourcePaymentMode ||
            guestData.bookingSource ||
            "OTA",
          amount: sourceExcessAdvance,
          transactionReference:
            guestData.otaBookingId ||
            guestData.transactionReference ||
            "",
          source: "checkin_source_excess",
        },
      ];

      let creditLeftToRecord = totalAdvanceCredit;
      const initialAdvanceCredits = [];

      advanceCandidates.forEach((candidate, index) => {
        if (creditLeftToRecord <= 0) return;

        const candidateAmount = Math.max(
          0,
          Number(candidate.amount || 0)
        );

        if (candidateAmount <= 0) return;

        const amount = Math.min(
          candidateAmount,
          creditLeftToRecord
        );

        initialAdvanceCredits.push({
          creditId: `ADV_${stayDocument.id}_${index + 1}`,
          originalAmount: amount,
          remainingAmount: amount,
          paymentMode: candidate.paymentMode,
          transactionReference: candidate.transactionReference,
          receivedAt: checkInDate.toISOString(),
          businessDate: getBusinessDateKey(checkInDate),
          source: candidate.source,
        });

        creditLeftToRecord = Math.max(
          0,
          creditLeftToRecord - amount
        );
      });

      if (creditLeftToRecord > 0) {
        initialAdvanceCredits.push({
          creditId: `ADV_${stayDocument.id}_fallback`,
          originalAmount: creditLeftToRecord,
          remainingAmount: creditLeftToRecord,
          paymentMode: guestData.paymentMode || "Cash",
          transactionReference:
            guestData.transactionReference || "",
          receivedAt: checkInDate.toISOString(),
          businessDate: getBusinessDateKey(checkInDate),
          source: "checkin_excess_fallback",
        });
      }

      Object.assign(stayPayload, {
        advanceCreditBalance: totalAdvanceCredit,
        excessAdvanceCredit: totalAdvanceCredit,
        advanceCredits: initialAdvanceCredits,
        updatedAt: serverTimestamp(),
      });

      for (const credit of initialAdvanceCredits) {
        if (Number(credit.remainingAmount || 0) <= 0) continue;

        await addDayBookEntry({
          entryType: "register_note",
          category: "checkin_excess_received",
          title: "Advance Amount Received",
          amount: credit.remainingAmount,
          paymentMode: credit.paymentMode,
          transactionReference: credit.transactionReference || "",
          room,
          guestName:
            primaryGuest?.name ||
            guestData.guestName ||
            "",
          stayId: stayDocument.id,
          eventAt: checkInDate,
          note: `Advance Amount Received – Room No. ${room}`,
          ledgerAdvance: credit.remainingAmount,
          ledgerNote: `Advance Amount Received – Room No. ${room}`,
          hideFromRegister: true,
        });
      }

      for (const [index, payment] of initialPayments.entries()) {
        if (['check_in_source_prepaid','booking_advance_applied'].includes(payment.source) || !['Cash', 'UPI'].includes(payment.paymentMode)) continue;
        await addDayBookEntry({entryType: 'payment', category: payment.source === 'check_in_prior_advance' ? 'booking_diary_advance' : 'checkin_guest_payment',
          ledgerAdvance: payment.source === 'check_in_prior_advance' ? payment.amount : 0,
          ledgerNote: payment.source === 'check_in_prior_advance' ? 'Previously received advance — first recorded at check-in' : '',
          title: payment.title || 'Check-In Payment', amount: payment.amount, paymentMode: payment.paymentMode,
          transactionReference: payment.transactionReference || '', stayId: stayDocument.id, room,
          guestName: primaryGuest?.name || '', eventAt: checkInDate, hideFromRegister: true,
          source: payment.source || 'checkin_guest_payment', receiptNumber: `RCPT-${stayDocument.id}-${index}`});
      }

      const occupiedRoom = {
        room,
        category: roomCategory,
        roomCategory,
        stayId: stayDocument.id,
        adRegisterSerialNo: guestData.adRegisterSerialNo,
        guestProfileIds: savedGuests.map((guest) => guest.profileId),

        guests: savedGuests.map((guest) => ({
          profileId: guest.profileId,
          isPrimary: guest.isPrimary,
          name: guest.name,
          phoneNumber: guest.phoneNumber,
          age: guest.age,
          gender: guest.gender,
          nationality: guest.nationality,
          address: guest.address,
          relation: guest.relation,
          idType: guest.idType,
          idNumber: guest.idNumber,
          idFrontUrl: guest.idFrontUrl,
          idBackUrl: guest.idBackUrl,
        })),

        guestCount: savedGuests.length,
        guestName:
          primaryGuest?.name ||
          guestData.guestName,
        phoneNumber:
          primaryGuest?.phoneNumber ||
          guestData.phoneNumber,

        age: primaryGuest?.age || "",
        gender:
          primaryGuest?.gender || "",
        nationality:
          primaryGuest?.nationality || "",
        idType:
          primaryGuest?.idType || "",
        idNumber:
          primaryGuest?.idNumber || "",
        idFrontUrl:
          primaryGuest?.idFrontUrl || "",
        idBackUrl:
          primaryGuest?.idBackUrl || "",

        checkInAt: checkInDate.toISOString(),
        expectedCheckOutAt:
          expectedCheckOutDate.toISOString(),
        tariff: guestData.tariff,
        baseTariff: guestData.baseTariff ?? guestData.tariff,
        stayType: guestData.stayType || "",
        complimentary: guestData.complimentary === true,
        earlyCheckIn: guestData.earlyCheckIn === true,
        earlyCheckInCharge,
        lateCheckOut: guestData.lateCheckOut === true,
        lateCheckOutCharge,
        advance: checkInAdvance,
        priorAdvanceAmount: priorAdvance.amount,
        priorAdvanceMode: priorAdvance.paymentMode,
        priorAdvanceReference: priorAdvance.transactionReference,
        sourcePaidAmount,
        guestCashReceived,
        guestOnlineReceived,
        guestReceivedAmount,
        onlineTransactionReference:
          guestData.onlineTransactionReference ||
          guestData.transactionReference ||
          "",
        sourcePaymentMode:
          guestData.sourcePaymentMode || "",
        extraPersonCount,
        extraPersonRate,
        extraPersonCharge,
        paidTotal: stayPayload.paidTotal,
        balance: stayPayload.balance,
        excessAdvanceCredit: totalAdvanceCredit,
        advanceCreditBalance: totalAdvanceCredit,
        advanceCredits: initialAdvanceCredits,
        paidAllocationNormalized: true,

        foodItems: [],
        foodTotal: 0,
        foodPaidTotal: 0,
        foodPaymentHistory: [],

        laundryItems: [],
        laundryTotal: 0,
        extraItems: initialExtraItems,
        extraTotal: initialExtraTotal,

        payments: initialPayments,

        paymentMode:
          guestCashReceived > 0 &&
          guestOnlineReceived > 0
            ? "Mixed"
            : guestOnlineReceived > 0
            ? "UPI"
            : guestCashReceived > 0
            ? "Cash"
            : guestData.paymentMode || "Pending",
        transactionReference:
          guestOnlineReceived > 0
            ? guestData.onlineTransactionReference ||
              guestData.transactionReference ||
              ""
            : "",
        bookingSource:
          guestData.bookingSource || "",
        status: "checked_in",
      };

      await commitCheckIn();
      if (reservedBooking && hotelId===JANAKPURI_HOTEL_ID && reservedBooking.source!=='reception_advance') {
        try {await janakpuriCall('rewards',{bookingId:reservedBooking.id});}
        catch (error) {console.warn('PMS stay saved; booking/reward sync pending',error);
          alert('Check-in saved. Bookings tab mein Sync PMS check-in & rewards dobara click karein.');}
        setJanakpuriClaim('');
      }
      guestData.adRegisterSerialNo = stayPayload.adRegisterSerialNo;
      guestData.adRegisterId = stayPayload.adRegisterId;
      const committedBoard = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed room action: refresh pending', error); return null;});
      if (committedBoard?.exists()) applyBoardData(committedBoard.data());
      for (const payment of initialPayments) {
        try {
          await logPaymentReceived({
            hotelId, hotelName: hotelName || hotelId, room, stayId: stayDocument.id,
            guestName: primaryGuest?.name || guestData.guestName || "", amount: payment.amount,
            referenceId: payment.transactionReference || stayDocument.id, title: payment.title,
            description: `${payment.title} • ${payment.paymentMode || "-"}`,
            metadata: { source: payment.source, paymentMode: payment.paymentMode, transactionReference: payment.transactionReference || "" },
            eventAt: checkInDate,
          });
        } catch (auditError) { console.warn("Check-in payment audit log error:", auditError); }
      }

      try {
      await logGuestCheckIn({
        hotelId,
        hotelName: hotelName || hotelId,
        room,
        guestName: primaryGuest?.name || guestData.guestName || "",
        phoneNumber: primaryGuest?.phoneNumber || guestData.phoneNumber || "",
        stayId: stayDocument.id,
        guestProfileId: primaryGuest?.profileId || "",
        amount: Number(guestData.tariff || 0),
        description: `Guest checked in through ${guestData.bookingSource || "Walk-In"} booking.`,
        after: {
          room,
          tariff: Number(guestData.tariff || 0),
          advance: Number(guestData.advance || 0),
          balance: Number(guestData.balance || 0),
          stayType: guestData.stayType || "",
          bookingSource: guestData.bookingSource || "Walk-In",
          adRegisterSerialNo: guestData.adRegisterSerialNo || "",
        },
        eventAt: checkInDate,
      });

      } catch (error) {console.warn('Check-in committed; audit delivery failed', error);}

      setSelectedCheckInRoom("");
      setQuickCheckInOpen(false);
      setCheckInPrefillProfile(null);
      setCheckInPrefillName("");

      await sendTelegram(
        `🏨 <b>WALK-IN CHECK-IN</b>
` +
          `🏢 ${hotelName || hotelId}
` +
          `🚪 Room: <b>${room}</b>
` +
          `📒 A&D Serial: <b>${guestData.adRegisterSerialNo}</b>
` +
          `👤 Primary Guest: ${primaryGuest?.name || guestData.guestName}
` +
          `👥 Total Guests: ${savedGuests.length}
` +
          `📞 ${primaryGuest?.phoneNumber || guestData.phoneNumber}
` +
          `💰 Tariff: ₹${guestData.tariff}
` +
          `🏦 Source Paid: ₹${Number(guestData.sourcePaidAmount || 0)} (${guestData.sourcePaymentMode || guestData.bookingSource || "-"})
` +
          `💵 Cash Received: ₹${guestCashReceived}
` +
          `💳 Online Received: ₹${guestOnlineReceived}
` +
          `👥 Extra Person Charge: ₹${extraPersonCharge}
` +
          `✅ Total Paid: ₹${guestData.advance}
` +
          `⏳ Balance: ₹${guestData.balance}`
      );

      playCheckInSuccessSound();
      alert("Guest check-in successful ✅");
    } catch (error) {
      console.error("Walk-in check-in error:", error);
      alert(
        "Guest check-in nahi hua: " +
          (error?.message || error)
      );
    } finally {
      checkInSubmitLockRef.current = false;
      setWalkInSaving(false);
    }
  };

  const handleContinueStay = async (continuation) => {
    const stay = selectedOccupiedRoom;
    if (!stay?.stayId || roomActionSaving) {
      alert("Stay ID missing hai.");
      return;
    }

    const cnuTariff = Math.max(0, Number(continuation?.tariff || 0));
    const received = Math.max(0, Number(continuation?.amountReceived || 0));
    const expectedCheckOutDate = new Date(continuation?.expectedCheckOutAt);
    const continuedAt = continuation?.continuedAt
      ? new Date(continuation.continuedAt)
      : new Date();

    if (cnuTariff <= 0) throw new Error("CNU tariff invalid hai.");
    if (Number.isNaN(expectedCheckOutDate.getTime())) {
      throw new Error("Expected checkout invalid hai.");
    }

    const normalizeCreditMode = (value) => {
      const mode = String(value || "").trim().toLowerCase();
      if (mode === "cash") return "Cash";
      if (mode === "fab") return "Fab";
      if (mode === "brevistay") return "Brevistay";
      return "Online";
    };

    const buildAdvanceCellLabels = (allocations = []) => {
      const labels = {
        Cash: [],
        Online: [],
        Fab: [],
        Brevistay: [],
      };

      allocations.forEach((allocation) => {
        const amount = Math.max(0, Number(allocation?.amount || 0));
        if (amount <= 0) return;

        const mode = normalizeCreditMode(allocation?.paymentMode);
        const dateText = formatAdvanceOriginDate(
          allocation?.receivedAt || continuedAt
        );

        labels[mode].push(
          `Adv ₹${amount.toLocaleString("en-IN")}${
            dateText ? ` (${dateText})` : ""
          }`
        );
      });

      return Object.fromEntries(
        Object.entries(labels).map(([key, list]) => [
          key,
          list.join(" + "),
        ])
      );
    };

    const buildRegisterPaymentLabel = ({
      freshAmount = 0,
      freshMode = "",
      targetMode,
      advanceLabel = "",
    }) => {
      const freshBelongsHere =
        freshAmount > 0 &&
        normalizeCreditMode(freshMode) === targetMode;

      const freshText = freshBelongsHere
        ? `₹${Number(freshAmount).toLocaleString("en-IN")}`
        : "";

      return [freshText, advanceLabel]
        .filter(Boolean)
        .join(" + ");
    };

    setRoomActionSaving(true);
    try {
      const stayRef = doc(db, "hotelStays", stay.stayId);

      /*
       * Phase 2: ek stable operation id transaction retry ke dauran bhi same rahega.
       * Isse CNU Day Book rows duplicate nahi hongi.
       */
      const cnuOperationId =
        `CNU_${stay.stayId}_${continuedAt.getTime()}`;

      const cnuRoomEntryRef =
        doc(
          db,
          "dayBookEntries",
          `${cnuOperationId}_ROOM`
        );

      const cnuPaymentEntryRef =
        doc(
          db,
          "dayBookEntries",
          `${cnuOperationId}_PAYMENT`
        );

      const cnuAdvanceEntryRef =
        doc(
          db,
          "dayBookEntries",
          `${cnuOperationId}_ADVANCE`
        );

      // Previous/latest physical room row -> OUT = CNU
      const dayBookSnap = await getDocs(
        query(
          collection(db, "dayBookEntries"),
          where("stayId", "==", stay.stayId)
        )
      );

      const roomRows = dayBookSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter(
          (entry) =>
            entry?.category === "room" &&
            entry?.hideFromRegister !== true
        )
        .sort(
          (a, b) =>
            new Date(b?.eventAt || 0).getTime() -
            new Date(a?.eventAt || 0).getTime()
        );

      const previousRoomRow = roomRows[0];

      const result = await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(stayRef);

        if (!snap.exists()) {
          throw new Error("Stay record not found.");
        }

        const data = snap.data();
        if (!isStayActive(data)) throw new Error("Stay ab active nahi hai. Fresh record kholein.");
        if (normalizeRoom(data.room) !== normalizeRoom(stay.room)) throw new Error('Stay doosre room mein shift ho gaya hai. Fresh stay kholein.');
        const rowId = data.currentRoomDayBookEntryId || previousRoomRow?.id;
        const previousRowSnapshot = rowId ? await transaction.get(doc(db, 'dayBookEntries', rowId)) : null;
        const freshPreviousRow = previousRowSnapshot?.exists() ? {id: previousRowSnapshot.id, ...previousRowSnapshot.data()} : null;
        const existingContinuations = Array.isArray(data.continuations)
          ? data.continuations
          : [];

        if (
          existingContinuations.some(
            (item) =>
              item?.continuationId ===
              cnuOperationId
          )
        ) {
          throw new Error(
            "Ye CNU operation pehle hi save ho chuki hai. Duplicate CNU create nahi ki gayi."
          );
        }

        const existingCnuTariffTotal = existingContinuations.reduce(
          (sum, item) =>
            sum + Math.max(0, Number(item?.tariff || 0)),
          0
        );

        const existingRoomTariff = Math.max(
          0,
          Number(data.tariff || 0)
        );

        const originalCheckInTariff = Math.max(
          0,
          existingRoomTariff - existingCnuTariffTotal
        );

        const checkInReceived = Math.max(
          0,
          Number(data.advance || 0)
        );

        const legacyExcessAdvance = Math.max(
          0,
          checkInReceived - originalCheckInTariff
        );

        const storedCreditBalance = Math.max(
          0,
          Number(
            data.advanceCreditBalance ??
              data.excessAdvanceCredit ??
              data.excessAdvanceReceived ??
              legacyExcessAdvance
          )
        );

        let advanceCredits = Array.isArray(data.advanceCredits)
          ? data.advanceCredits
              .map((credit, index) => ({
                ...credit,
                creditId:
                  credit?.creditId ||
                  `LEGACY_ADV_${stay.stayId}_${index + 1}`,
                originalAmount: Math.max(
                  0,
                  Number(
                    credit?.originalAmount ??
                      credit?.remainingAmount ??
                      credit?.amount ??
                      0
                  )
                ),
                remainingAmount: Math.max(
                  0,
                  Number(
                    credit?.remainingAmount ??
                      credit?.amount ??
                      0
                  )
                ),
                paymentMode: credit?.paymentMode || "Cash",
                receivedAt:
                  credit?.receivedAt ||
                  data.checkInAt ||
                  continuedAt.toISOString(),
              }))
              .filter((credit) => credit.remainingAmount > 0)
          : [];

        const creditListBalance = advanceCredits.reduce(
          (sum, credit) => sum + credit.remainingAmount,
          0
        );

        if (storedCreditBalance > creditListBalance + 0.01) {
          const missingCredit =
            storedCreditBalance - creditListBalance;

          advanceCredits.push({
            creditId: `LEGACY_ADV_${stay.stayId}_fallback`,
            originalAmount: missingCredit,
            remainingAmount: missingCredit,
            paymentMode:
              Number(data.guestOnlineReceived || 0) >
              Number(data.guestCashReceived || 0)
                ? "UPI"
                : data.paymentMode || "Cash",
            transactionReference:
              data.onlineTransactionReference ||
              data.transactionReference ||
              "",
            receivedAt:
              data.checkInAt || continuedAt.toISOString(),
            businessDate:
              data.businessDate ||
              getBusinessDateKey(
                new Date(data.checkInAt || continuedAt)
              ),
            source: "legacy_advance_credit",
          });
        }

        const availableAdvanceBefore = advanceCredits.reduce(
          (sum, credit) => sum + Math.max(0, credit.remainingAmount),
          0
        );

        const storedPaidTotal = Math.max(
          0,
          Number(data.paidTotal ?? data.advance ?? 0)
        );

        const allocatedPaidBeforeCnu =
          data.paidAllocationNormalized === true
            ? storedPaidTotal
            : Math.max(0, storedPaidTotal - storedCreditBalance);

        const newTariff = Math.round(
          (existingRoomTariff + cnuTariff + Number.EPSILON) * 100
        ) / 100;

        const grossTotal = Math.round(
          (
            newTariff +
            Number(data.foodTotal || 0) +
            Number(data.laundryTotal || 0) +
            Number(data.extraTotal || 0) +
            Number.EPSILON
          ) * 100
        ) / 100;

        const discountType = data.discountType || "flat";
        const discountValue = Math.max(
          0,
          Number(data.discountValue ?? data.discountAmount ?? 0)
        );

        const discountAmount =
          discountType === "percent"
            ? Math.round(
                (
                  Math.min(
                    grossTotal,
                    (grossTotal * Math.min(100, discountValue)) / 100
                  ) + Number.EPSILON
                ) * 100
              ) / 100
            : Math.min(grossTotal, discountValue);

        const taxableAmount = Math.max(
          0,
          grossTotal - discountAmount
        );

        const gstRate = Math.max(0, Number(data.gstRate || 0));
        const gstAmount = Math.round(
          (((taxableAmount * gstRate) / 100) + Number.EPSILON) * 100
        ) / 100;

        const grandTotal = Math.round(
          (taxableAmount + gstAmount + Number.EPSILON) * 100
        ) / 100;

        const outstandingBeforeAdvance = Math.max(
          0,
          Math.round(
            (grandTotal - allocatedPaidBeforeCnu + Number.EPSILON) * 100
          ) / 100
        );

        const advanceToApply = Math.min(
          availableAdvanceBefore,
          cnuTariff,
          outstandingBeforeAdvance
        );

        const allocation = allocateAdvanceCredits(advanceCredits, advanceToApply,
          data.checkInAt || continuedAt.toISOString());
        const advanceAllocations = allocation.allocations;
        advanceCredits = allocation.credits;

        const outstandingAfterAdvance = Math.max(
          0,
          outstandingBeforeAdvance - advanceToApply
        );

        const freshApplied = Math.min(
          received,
          outstandingAfterAdvance
        );

        const freshExcessAdvance = Math.max(
          0,
          Math.round(
            (received - freshApplied + Number.EPSILON) * 100
          ) / 100
        );

        if (freshExcessAdvance > 0) {
          advanceCredits.push({
            creditId: `${cnuOperationId}_CREDIT`,
            originalAmount: freshExcessAdvance,
            remainingAmount: freshExcessAdvance,
            paymentMode: continuation?.paymentMode || "Cash",
            transactionReference:
              continuation?.transactionReference || "",
            receivedAt: continuedAt.toISOString(),
            businessDate: getBusinessDateKey(continuedAt),
            source: "cnu_excess_received",
          });
        }

        advanceCredits = advanceCredits.filter(
          (credit) => Number(credit.remainingAmount || 0) > 0.009
        );

        const advanceCreditBalance = Math.round(
          (
            advanceCredits.reduce(
              (sum, credit) =>
                sum + Math.max(0, Number(credit.remainingAmount || 0)),
              0
            ) + Number.EPSILON
          ) * 100
        ) / 100;

        const newPaidTotal = Math.round(
          (
            allocatedPaidBeforeCnu +
            advanceToApply +
            freshApplied +
            Number.EPSILON
          ) * 100
        ) / 100;

        const balance = Math.max(
          0,
          Math.round(
            (grandTotal - newPaidTotal + Number.EPSILON) * 100
          ) / 100
        );

        const cnuDueAfterAdvance = Math.max(
          0,
          cnuTariff - advanceToApply
        );

        const freshAppliedToCurrentCnu = Math.min(
          freshApplied,
          cnuDueAfterAdvance
        );

        const cnuPending = Math.max(
          0,
          Math.round(
            (
              cnuDueAfterAdvance -
              freshAppliedToCurrentCnu +
              Number.EPSILON
            ) * 100
          ) / 100
        );

        const existingPayments = Array.isArray(data.payments)
          ? data.payments
          : [];

        const cnuPaymentRecord =
          received > 0
            ? {
                type: "payment",
                title: "CNU Payment Received",
                amount: received,
                quantity: 1,
                rate: received,
                paymentMode:
                  continuation?.paymentMode || "Cash",
                transactionReference:
                  continuation?.transactionReference || "",
                note: "Continue Stay Payment",
                source: "cnu_payment",
                addedAt: continuedAt.toISOString(),
              }
            : null;

        const continuationRecord = {
          continuationId: cnuOperationId,
          continuedAt: continuedAt.toISOString(),
          tariff: cnuTariff,
          amountReceived: received,
          amountAppliedNow: freshApplied,
          freshExcessAdvance,
          advanceApplied: advanceToApply,
          advanceAllocations,
          advanceCreditBalanceBefore: availableAdvanceBefore,
          advanceCreditBalanceAfter: advanceCreditBalance,
          pending: cnuPending,
          totalStayBalance: balance,
          paymentMode:
            received > 0
              ? continuation?.paymentMode || "Cash"
              : "Advance / Pending",
          transactionReference:
            received > 0
              ? continuation?.transactionReference || ""
              : "",
          expectedCheckOutAt: expectedCheckOutDate.toISOString(),
          note: continuation?.note || "",
          businessDate: getBusinessDateKey(continuedAt),
          addedBy: auth.currentUser?.email || "manager",
        };

        transaction.update(stayRef, {
          tariff: newTariff,
          baseTariff: newTariff,
          paidTotal: newPaidTotal,
          paid: newPaidTotal,
          excessAdvanceCredit: advanceCreditBalance,
          advanceCreditBalance,
          advanceCredits,
          paidAllocationNormalized: true,
          expectedCheckOutAt: expectedCheckOutDate.toISOString(),
          grossTotal,
          discountAmount,
          taxableAmount,
          gstAmount,
          grandTotal,
          balance,
          payments: cnuPaymentRecord
            ? [...existingPayments, cnuPaymentRecord]
            : existingPayments,
          continuations: [
            ...existingContinuations,
            continuationRecord,
          ],
          lastContinuationAt: continuedAt.toISOString(),
          currentRoomDayBookEntryId: cnuRoomEntryRef.id,
          ...financialFields({...data, tariff: newTariff, paidTotal: newPaidTotal, paidAllocationNormalized: true, advanceCreditBalance, excessAdvanceCredit: advanceCreditBalance}),
          updatedAt: serverTimestamp(),
        });

        if (freshPreviousRow?.id) {
          const oldNote =
            freshPreviousRow.ledgerNote ||
            freshPreviousRow.note ||
            "";

          transaction.update(
            doc(
              db,
              "dayBookEntries",
              freshPreviousRow.id
            ),
            {
              ledgerCheckOut: "CNU",
              ledgerNote: `${oldNote}${
                oldNote ? " • " : ""
              }Continued / CNU`,
              updatedAt:
                serverTimestamp(),
            }
          );
        }

        const canonicalNext = {...data, tariff: newTariff, paidTotal: newPaidTotal, paidAllocationNormalized: true, advanceCreditBalance, excessAdvanceCredit: advanceCreditBalance};
        const oldBill = calculateBill(data); const nextBill = calculateBill(canonicalNext);
        for (const [category, amount] of [['discount', roundMoney(nextBill.discount - oldBill.discount)], ['gst', roundMoney(nextBill.gstAmount - oldBill.gstAmount)]]) {
          if (amount !== 0) queueDayBookEntry(transaction, doc(db, 'dayBookEntries', `${cnuOperationId}_${category}`), {entryType: 'adjustment', category, title: `CNU ${category} adjustment`, amount, room: data.room, stayId: stay.stayId, guestName: data.guestName || '', eventAt: continuedAt, hideFromRegister: true});
        }

        const freshMode = String(
          continuation?.paymentMode || ""
        ).trim();

        const freshModeKey =
          normalizeCreditMode(
            freshMode
          );

        const advanceLabels =
          buildAdvanceCellLabels(
            advanceAllocations
          );

        const cashLabel =
          buildRegisterPaymentLabel({
            freshAmount: received,
            freshMode,
            targetMode: "Cash",
            advanceLabel:
              advanceLabels.Cash,
          });

        const onlineLabel =
          buildRegisterPaymentLabel({
            freshAmount: received,
            freshMode,
            targetMode: "Online",
            advanceLabel:
              advanceLabels.Online,
          });

        const fabLabel =
          buildRegisterPaymentLabel({
            freshAmount: received,
            freshMode,
            targetMode: "Fab",
            advanceLabel:
              advanceLabels.Fab,
          });

        const brevistayLabel =
          buildRegisterPaymentLabel({
            freshAmount: received,
            freshMode,
            targetMode: "Brevistay",
            advanceLabel:
              advanceLabels.Brevistay,
          });

        queueDayBookEntry(
          transaction,
          cnuRoomEntryRef,
          {
            entryType: "revenue",
            category: "room",
            title: "Room Rent / CNU",
            amount: cnuTariff,
            paymentMode:
              received > 0
                ? continuation?.paymentMode ||
                  "Cash"
                : advanceToApply > 0
                ? "Advance"
                : "Pending",
            transactionReference:
              continuation?.transactionReference ||
              "",
            room: stay.room,
            guestName:
              stay.guestName || "",
            stayId:
              stay.stayId,
            eventAt:
              continuedAt,
            note: `CNU • A&D ${
              stay.adRegisterSerialNo ||
              stay.adSerialNo ||
              "-"
            }${
              advanceToApply > 0
                ? ` • Advance Adjusted ₹${Number(
                    advanceToApply
                  ).toLocaleString(
                    "en-IN"
                  )}`
                : ""
            }${
              continuation?.note
                ? ` • ${continuation.note}`
                : ""
            }`,
            ledgerSerial:
              stay.adRegisterSerialNo ||
              stay.adSerialNo ||
              "",
            ledgerRoom:
              stay.room,
            ledgerTariff:
              cnuTariff,
            ledgerCash:
              freshModeKey === "Cash"
                ? received
                : 0,
            ledgerOnline:
              freshModeKey === "Online"
                ? received
                : 0,
            ledgerFab:
              freshModeKey === "Fab"
                ? received
                : 0,
            ledgerBrevistay:
              freshModeKey ===
              "Brevistay"
                ? received
                : 0,
            ledgerCashLabel:
              cashLabel,
            ledgerOnlineLabel:
              onlineLabel,
            ledgerFabLabel:
              fabLabel,
            ledgerBrevistayLabel:
              brevistayLabel,
            ledgerCheckIn:
              "CNU",
            ledgerCheckOut:
              formatDayBookOut(
                expectedCheckOutDate
              ),
            ledgerBalance:
              balance,
            ledgerGuestName:
              stay.guestName || "",
            ledgerTransactionReference:
              continuation?.transactionReference ||
              "",
            operationId:
              cnuOperationId,
          }
        );

        if (received > 0) {
          queueDayBookEntry(
            transaction,
            cnuPaymentEntryRef,
            {
              entryType: "payment",
              category: "cnu_payment",
              title:
                "CNU Payment Received",
              amount:
                received,
              paymentMode:
                continuation?.paymentMode ||
                "Cash",
              transactionReference:
                continuation?.transactionReference ||
                "",
              room:
                stay.room,
              guestName:
                stay.guestName || "",
              stayId:
                stay.stayId,
              eventAt:
                continuedAt,
              note:
                "CNU / Continue Stay Payment Received",
              hideFromRegister:
                true,
              operationId:
                cnuOperationId,
            }
          );
        }

        if (
          freshExcessAdvance > 0
        ) {
          queueDayBookEntry(
            transaction,
            cnuAdvanceEntryRef,
            {
              entryType:
                "register_note",
              category:
                "cnu_excess_received",
              title:
                "Advance Amount Received",
              amount:
                freshExcessAdvance,
              paymentMode:
                continuation?.paymentMode ||
                "Cash",
              transactionReference:
                continuation?.transactionReference ||
                "",
              room:
                stay.room,
              guestName:
                stay.guestName || "",
              stayId:
                stay.stayId,
              eventAt:
                continuedAt,
              note:
                `Advance Amount Received – Room No. ${stay.room}`,
              ledgerAdvance:
                freshExcessAdvance,
              ledgerNote:
                `Advance Amount Received – Room No. ${stay.room}`,
              hideFromRegister:
                true,
              operationId:
                cnuOperationId,
            }
          );
        }

        return {
          tariff: newTariff,
          paidTotal: newPaidTotal,
          excessAdvanceCredit: advanceCreditBalance,
          advanceCreditBalance,
          advanceCredits,
          paidAllocationNormalized: true,
          expectedCheckOutAt: expectedCheckOutDate.toISOString(),
          grossTotal,
          discountAmount,
          taxableAmount,
          gstAmount,
          grandTotal,
          balance,
          payments: cnuPaymentRecord
            ? [...existingPayments, cnuPaymentRecord]
            : existingPayments,
          continuations: [
            ...existingContinuations,
            continuationRecord,
          ],
          lastContinuationAt: continuedAt.toISOString(),
          advanceApplied: advanceToApply,
          advanceAllocations,
          freshApplied,
          freshExcessAdvance,
          cnuPending,
          ...financialFields(canonicalNext),
        };
      });

      await refreshOccupiedItem(stay.room, {...result, stayId: stay.stayId});

      alert(
        `CNU saved ✅\n` +
          `Room ${stay.room} • Tariff ₹${cnuTariff}\n` +
          `Advance Adjusted ₹${Number(
            result.advanceApplied || 0
          ).toLocaleString("en-IN")}\n` +
          `Received Now ₹${received.toLocaleString("en-IN")}\n` +
          `Advance Left ₹${Number(
            result.advanceCreditBalance || 0
          ).toLocaleString("en-IN")}\n` +
          `Total Balance ₹${Number(
            result.balance || 0
          ).toLocaleString("en-IN")}`
      );
    } catch (error) {
      console.error("CNU save error:", error);
      alert(
        "CNU save nahi hua: " +
          (error?.message || error)
      );
      throw error;
    } finally {
      setRoomActionSaving(false);
    }
  };

  const openOccupiedRoomManager =
    async (item) => {
      const baseItem =
        typeof item === "object"
          ? item
          : {
              room:
                normalizeRoom(item),
            };

      const stayId =
        baseItem?.stayId ||
        baseItem?.id ||
        "";

      if (stayId) {
        try {
          const staySnap =
            await getDoc(
              doc(
                db,
                "hotelStays",
                stayId
              )
            );

          if (staySnap.exists()) {
            setSelectedOccupiedRoom({
              ...baseItem,
              id: staySnap.id,
              stayId: staySnap.id,
              ...staySnap.data(),
            });
            return;
          }
        } catch (error) {
          console.error(
            "Full occupied stay load error:",
            error
          );
        }
      }

      setSelectedOccupiedRoom(
        baseItem
      );
    };

  const refreshOccupiedItem = async (room, updates) => {
    const stayId = String(updates?.stayId || updates?.id || selectedOccupiedRoom?.stayId || '');
    if (!stayId) return false;
    const snapshot = await getDoc(doc(db, 'hotelStays', stayId)).catch(() => null);
    const records = new Map(latestStaysRef.current.hotelId === hotelId ? latestStaysRef.current.records : []);
    const fresh = snapshot?.exists() ? snapshot.data() : records.get(stayId);
    if (fresh) records.set(stayId, {...fresh, id: stayId, stayId});
    const board = latestBoardRef.current.hotelId === hotelId ? latestBoardRef.current.board : {occupiedRooms};
    const cards = hydrateOccupiedCards(board.occupiedRooms, records);
    setOccupiedRooms(cards);
    // A delayed payment/CNU response cannot reopen a checked-out guest or an old room.
    setSelectedOccupiedRoom(current => current && String(current.stayId || current.id || '') === stayId
      ? cards.find(card => String(card.stayId || card.id || '') === stayId) || null : current);
    return true;
  };

  const handleAddGuestToStay =
    async (guest) => {
      const stay =
        selectedOccupiedRoom;

      if (
        !stay?.stayId ||
        roomActionSaving
      ) {
        alert(
          "Active stay ID missing hai."
        );
        return;
      }

      const existingStayGuests =
        Array.isArray(stay.guests)
          ? stay.guests
          : [];

      const incomingProfileId =
        guest?.profileId ||
        guest?.id ||
        "";

      const incomingId =
        String(
          guest?.idNumber || ""
        )
          .replace(
            /[^a-zA-Z0-9]/g,
            ""
          )
          .toUpperCase();

      const alreadyInStay =
        existingStayGuests.some(
          (existingGuest) => {
            const sameProfile =
              incomingProfileId &&
              (
                existingGuest.profileId ===
                  incomingProfileId ||
                existingGuest.id ===
                  incomingProfileId
              );

            const existingId =
              String(
                existingGuest.idNumber ||
                  ""
              )
                .replace(
                  /[^a-zA-Z0-9]/g,
                  ""
                )
                .toUpperCase();

            const sameId =
              incomingId &&
              existingId ===
                incomingId;

            return (
              sameProfile ||
              sameId
            );
          }
        );

      if (alreadyInStay) {
        alert(
          "Ye guest is room/stay me already added hai."
        );
        return;
      }

      setRoomActionSaving(true);

      try {
        const savedGuests =
          await saveGuestProfiles({
            hotelId,
            hotelName:
              hotelName || "",
            guests: [
              {
                ...guest,
                isPrimary: false,
                relation:
                  guest?.relation ||
                  "Additional Guest",
              },
            ],
          });

        const addedGuest =
          savedGuests[0];

        if (!addedGuest) {
          throw new Error(
            "Guest profile save nahi hua."
          );
        }

        const stayRef = doc(
          db,
          "hotelStays",
          stay.stayId
        );

        const result =
          await runTransaction(
            db,
            async (transaction) => {
              const snap =
                await transaction.get(
                  stayRef
                );

              if (!snap.exists()) {
                throw new Error(
                  "Stay record not found."
                );
              }

              const data =
                snap.data();
              if (!isStayActive(data)) throw new Error("Stay ab active nahi hai.");

              const existingGuests =
                Array.isArray(
                  data.guests
                )
                  ? data.guests
                  : [];

              const existingProfileIds =
                Array.isArray(
                  data.guestProfileIds
                )
                  ? data.guestProfileIds
                  : [];

              const profileId =
                addedGuest.profileId ||
                addedGuest.id ||
                "";
              const profileReference = profileId ? doc(db, 'guestProfiles', profileId) : null;
              const profileSnapshot = profileReference ? await transaction.get(profileReference) : null;


              const normalizedNewId =
                String(
                  addedGuest.idNumber ||
                    ""
                )
                  .replace(
                    /[^a-zA-Z0-9]/g,
                    ""
                  )
                  .toUpperCase();

              const duplicate =
                existingGuests.some(
                  (existingGuest) => {
                    const sameProfile =
                      profileId &&
                      (
                        existingGuest.profileId ===
                          profileId ||
                        existingGuest.id ===
                          profileId
                      );

                    const existingId =
                      String(
                        existingGuest.idNumber ||
                          ""
                      )
                        .replace(
                          /[^a-zA-Z0-9]/g,
                          ""
                        )
                        .toUpperCase();

                    const sameId =
                      normalizedNewId &&
                      existingId ===
                        normalizedNewId;

                    return (
                      sameProfile ||
                      sameId
                    );
                  }
                );

              if (duplicate) {
                throw new Error(
                  "Ye guest is room/stay me already added hai."
                );
              }

              const guestRecord = {
                profileId,
                isPrimary: false,
                name:
                  addedGuest.name ||
                  "",
                phoneNumber:
                  addedGuest.phoneNumber ||
                  "",
                age:
                  addedGuest.age ??
                  "",
                gender:
                  addedGuest.gender ||
                  "",
                nationality:
                  addedGuest.nationality ||
                  "",
                address:
                  addedGuest.address ||
                  "",
                relation:
                  addedGuest.relation ||
                  guest?.relation ||
                  "Additional Guest",
                idType:
                  addedGuest.idType ||
                  "",
                idNumber:
                  addedGuest.idNumber ||
                  "",
                idFrontUrl:
                  addedGuest.idFrontUrl ||
                  "",
                idBackUrl:
                  addedGuest.idBackUrl ||
                  "",
                addedToStayAt:
                  new Date().toISOString(),
              };

              const nextGuests = [
                ...existingGuests,
                guestRecord,
              ];

              const nextProfileIds =
                profileId
                  ? Array.from(
                      new Set([
                        ...existingProfileIds,
                        profileId,
                      ])
                    )
                  : existingProfileIds;

              if (profileSnapshot?.exists()) transaction.update(profileReference, {visitCount: Number(profileSnapshot.data().visitCount || 0) + 1, lastVisitAt: new Date().toISOString(), updatedAt: serverTimestamp()});
              transaction.update(
                stayRef,
                {
                  guests:
                    nextGuests,
                  guestProfileIds:
                    nextProfileIds,
                  adults:
                    nextGuests.length,
                  guestCount:
                    nextGuests.length,
                  updatedAt:
                    serverTimestamp(),
                }
              );

              return {
                ...data,
                guests:
                  nextGuests,
                guestProfileIds:
                  nextProfileIds,
                adults:
                  nextGuests.length,
                guestCount:
                  nextGuests.length,
              };
            }
          );

        await refreshOccupiedItem(
          stay.room,
          {
            ...result,
            id: stay.stayId,
            stayId:
              stay.stayId,
          }
        );

        alert(
          `${addedGuest.name || "Guest"} Room ${stay.room} me add ho gaya ✅`
        );
      } catch (error) {
        console.error(
          "Add guest to occupied stay error:",
          error
        );

        alert(
          "Guest add nahi hua: " +
            (error?.message || error)
        );

        throw error;
      } finally {
        setRoomActionSaving(false);
      }
    };

  const handleRoomShift = async ({
    fromRoom,
    toRoom,
    reason = "",
    oldRoomStatus = "dirty",
  }) => {
    const stay = selectedOccupiedRoom;

    if (!stay?.stayId || roomActionSaving) {
      return alert("Stay ID missing hai.");
    }

    const oldRoom = normalizeRoom(
      fromRoom || stay.room
    );
    const newRoom = normalizeRoom(toRoom);

    if (!oldRoom || !newRoom) {
      return alert(
        "Current ya new room missing hai."
      );
    }

    if (oldRoom === newRoom) {
      return alert(
        "Current aur new room same nahi ho sakte."
      );
    }

    const targetVacant = vacantRooms.find(
      (item) =>
        normalizeRoom(item.room) === newRoom
    );

    if (!targetVacant) {
      return alert(
        `Room ${newRoom} ab vacant nahi hai. Board refresh karke dobara try karo.`
      );
    }

    let newRoomCategory =
      getRoomCategory(targetVacant);

    let oldRoomCategory =
      getRoomCategory(
        occupiedRooms.find(
          (item) =>
            normalizeRoom(getRoomValue(item)) === oldRoom
        ) || stay
      );

    const targetBlocked =
      occupiedRooms.some(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) === newRoom
      ) ||
      checkoutRooms.some(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) === newRoom
      ) ||
      serviceRooms.some(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) === newRoom
      );

    if (targetBlocked) {
      return alert(
        `Room ${newRoom} kisi aur status me hai. Shift cancel kiya gaya.`
      );
    }

    setRoomActionSaving(true);

    try {
      const shiftLifecycle = await reconcileHotelLifecycle(
        getCurrentBoardSnapshot(),
        { reason: `before_room_shift_${oldRoom}_to_${newRoom}` }
      );
      assertNoActiveRoomStays(shiftLifecycle.activeStaysByRoom?.[newRoom] || [], hotelId, newRoom);

      const shiftedAt = new Date();
      const shiftedAtIso =
        shiftedAt.toISOString();
      const stayRef = doc(
        db,
        "hotelStays",
        stay.stayId
      );

      const shiftRecord = {
        fromRoom: oldRoom,
        toRoom: newRoom,
        reason: String(reason || "").trim(),
        oldRoomStatus,
        shiftedAt: shiftedAtIso,
      };

      const updatedStay =
        await runTransaction(
          db,
          async (transaction) => {
            const oldLockRef =
              getRoomLifecycleLockRef(oldRoom);
            const newLockRef =
              getRoomLifecycleLockRef(newRoom);

            const snapshot =
              await transaction.get(stayRef);
            const oldLockSnapshot =
              await transaction.get(oldLockRef);
            const newLockSnapshot =
              await transaction.get(newLockRef);

            const shiftBoardRef = getBoardDocRef();
            const shiftBoardSnapshot = await transaction.get(shiftBoardRef);
            const shiftBoard = normalizeBoardData(shiftBoardSnapshot.exists() ? shiftBoardSnapshot.data() : {});
            if (!shiftBoard.vacantRooms.some(item => roomKey(item) === newRoom)) throw new Error(`Room ${newRoom} ab Ready nahi hai.`);
            if ([...shiftBoard.occupiedRooms, ...shiftBoard.checkoutRooms, ...shiftBoard.serviceRooms].some(item => roomKey(item) === newRoom)) throw new Error(`Room ${newRoom} ab blocked/service mein hai.`);
            newRoomCategory = getRoomCategory(shiftBoard.vacantRooms.find(item => roomKey(item) === newRoom));
            oldRoomCategory = getRoomCategory(shiftBoard.occupiedRooms.find(item => roomKey(item) === oldRoom) || snapshot.data());
            if (!oldLockSnapshot.exists() || oldLockSnapshot.data().activeStayId !== stay.stayId) throw new Error('Original room lock badal gaya hai.');
            if (!snapshot.exists()) {
              throw new Error(
                "Stay record not found."
              );
            }

            const data = snapshot.data();
            if (!isStayActive(data)) throw new Error("Stay ab active nahi hai.");
            const currentStoredRoom =
              normalizeRoom(data.room);

            if (
              currentStoredRoom &&
              currentStoredRoom !== oldRoom
            ) {
              throw new Error(
                `Stay already Room ${currentStoredRoom} me shift ho chuka hai. Dashboard refresh karo.`
              );
            }

            if (newLockSnapshot.exists()) {
              const newLockData =
                newLockSnapshot.data();
              const otherStayId = String(
                newLockData?.activeStayId || ""
              );
              const newLockStatus = String(
                newLockData?.status || ""
              )
                .trim()
                .toLowerCase();

              if (
                (otherStayId &&
                  otherStayId !== stay.stayId) ||
                [
                  "checkout_pending",
                  "cleaning",
                  "maintenance_hold",
                ].includes(newLockStatus)
              ) {
                throw new Error(
                  `Room ${newRoom} lifecycle lock me busy hai. Refresh karke dobara try karo.`
                );
              }
            }

            const history = Array.isArray(
              data.roomShiftHistory
            )
              ? data.roomShiftHistory
              : [];

            const oldCard = {room: oldRoom, category: oldRoomCategory, roomCategory: oldRoomCategory,
              stayId: stay.stayId, status: oldRoomStatus === 'service' ? 'maintenance_hold' : 'dirty',
              cleaningStatus: 'pending', guestName: data.guestName || '', source: 'room_shift', checkoutAt: shiftedAtIso};
            transaction.set(shiftBoardRef, {...shiftBoard, hotelId, hotelName: hotelName || "", schemaVersion: 2, revision: Number(shiftBoardSnapshot.data()?.revision || 0) + 1,
              occupiedRooms: [...shiftBoard.occupiedRooms.filter(item => ![oldRoom,newRoom].includes(roomKey(item))), {...data, room: newRoom, stayId: stay.stayId, category: newRoomCategory, roomCategory: newRoomCategory, previousRoom: oldRoom, lastRoomShiftAt: shiftedAtIso}],
              vacantRooms: [...shiftBoard.vacantRooms.filter(item => ![oldRoom,newRoom].includes(roomKey(item))), ...(oldRoomStatus === 'ready' ? [{room: oldRoom, category: oldRoomCategory, roomCategory: oldRoomCategory}] : [])],
              checkoutRooms: [...shiftBoard.checkoutRooms.filter(item => ![oldRoom,newRoom].includes(roomKey(item))), ...(oldRoomStatus === 'ready' ? [] : [oldCard])],
              serviceRooms: [...shiftBoard.serviceRooms.filter(item => roomKey(item) !== oldRoom), ...(oldRoomStatus === 'service' ? [oldCard] : [])], updatedAt: serverTimestamp()}, {merge: true});
            if (oldRoomStatus !== 'ready') transaction.set(doc(db, 'housekeepingTasks', `${hotelId}_${oldRoom}`), {hotelId, hotelName: hotelName || '', ...oldCard, status: oldCard.status, roomReady: false, maintenanceRequired: oldRoomStatus === 'service', updatedAt: serverTimestamp()});
            queueDayBookEntry(transaction, doc(db, 'dayBookEntries', `SHIFT_${stay.stayId}_${shiftedAt.getTime()}`), {entryType: 'register_note', category: 'room_shift', title: 'Room Shift', amount: 0, room: newRoom, stayId: stay.stayId, guestName: data.guestName || '', eventAt: shiftedAt, note: `${oldRoom} → ${newRoom}`, hideFromRegister: true});

            transaction.update(stayRef, {
              room: newRoom,
              category: newRoomCategory,
              roomCategory: newRoomCategory,
              previousRoom: oldRoom,
              lastRoomShiftAt: shiftedAtIso,
              lastRoomShiftReason:
                shiftRecord.reason,
              roomShiftHistory: [
                ...history,
                shiftRecord,
              ],
              updatedAt: serverTimestamp(),
            });

            transaction.set(
              newLockRef,
              {
                hotelId,
                hotelName: hotelName || "",
                room: newRoom,
                activeStayId: stay.stayId,
                status: "occupied",
                acquiredAt:
                  data.checkInAt || shiftedAtIso,
                shiftedFromRoom: oldRoom,
                shiftedAt: shiftedAtIso,
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );

            if (oldRoomStatus === "ready") {
              if (oldLockSnapshot.exists()) {
                transaction.delete(oldLockRef);
              }
            } else {
              transaction.set(
                oldLockRef,
                {
                  hotelId,
                  hotelName: hotelName || "",
                  room: oldRoom,
                  activeStayId: "",
                  lastStayId: stay.stayId,
                  status:
                    oldRoomStatus === "service"
                      ? "maintenance_hold"
                      : "cleaning",
                  shiftedToRoom: newRoom,
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              );
            }

            return {
              ...data,
              room: newRoom,
              category: newRoomCategory,
              roomCategory: newRoomCategory,
              previousRoom: oldRoom,
              lastRoomShiftAt: shiftedAtIso,
              lastRoomShiftReason:
                shiftRecord.reason,
              roomShiftHistory: [
                ...history,
                shiftRecord,
              ],
            };
          }
        );

      const shiftedOccupied = {...updatedStay, room: newRoom, stayId: stay.stayId};
      const freshShiftBoard = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed room action: refresh pending', error); return null;});
      if (freshShiftBoard?.exists()) applyBoardData(freshShiftBoard.data());

      try {
        await logRoomBoardUpdate({
          hotelId,
          hotelName:
            hotelName || hotelId,
          room: newRoom,
          stayId: stay.stayId,
          guestName:
            stay.guestName || "",
          description:
            `Room shifted ${oldRoom} → ${newRoom}` +
            (shiftRecord.reason
              ? ` • ${shiftRecord.reason}`
              : ""),
          before: {
            room: oldRoom,
          },
          after: {
            room: newRoom,
            oldRoomStatus,
          },
          eventAt: shiftedAt,
        });
      } catch (auditError) {
        console.warn(
          "Room shift audit log error:",
          auditError
        );
      }

      try {
        await sendTelegram(
          `🔁 <b>ROOM SHIFT</b>\n` +
            `🏢 ${hotelName || hotelId}\n` +
            `👤 ${stay.guestName || "Guest"}\n` +
            `🚪 ${oldRoom} → <b>${newRoom}</b>\n` +
            `📝 ${shiftRecord.reason || "No reason entered"}`
        );
      } catch (telegramError) {
        console.warn(
          "Room shift Telegram error:",
          telegramError
        );
      }

      setSelectedOccupiedRoom(
        shiftedOccupied
      );

      alert(
        `Room shift successful ✅\n\n${oldRoom} → ${newRoom}\nGuest aur poora active stay new room me move ho gaya.`
      );
    } catch (error) {
      console.error(
        "Room shift error:",
        error
      );

      alert(
        "Room shift nahi hua: " +
          (error?.message || error)
      );

      throw error;
    } finally {
      setRoomActionSaving(false);
    }
  };

  const handleAddRoomEntry = async (entry) => {
    const stay = selectedOccupiedRoom;
    if (!stay?.stayId || roomActionSaving) throw new Error('Active stay ID missing/busy hai.');
    if (entry.type === 'food') {
      setRoomActionSaving(true);
      try {
        const result = await placeFoodOrder({database: db, hotelId, stayId: stay.stayId,
          expectedRoom: stay.room, entry, actor: getCurrentActor(), operationId: entry.foodOrderId});
        await refreshOccupiedItem(stay.room, result);
        return result;
      } finally { setRoomActionSaving(false); }
    }
    setRoomActionSaving(true);
    const operationRef = doc(collection(db, 'dayBookEntries'));
    const payment = ['payment', 'food_payment'].includes(entry.type);
    const savedEntry = {...entry, operationId: operationRef.id,
      receiptNumber: payment ? (entry.receiptNumber || `RCPT-${operationRef.id}`) : '',
      receivedBy: auth.currentUser?.email || 'manager', addedAt: new Date().toISOString()};
    try {
      const stayRef = doc(db, 'hotelStays', stay.stayId);
      const result = await runTransaction(db, async transaction => {
        const [snapshot, alreadySaved] = await Promise.all([transaction.get(stayRef), transaction.get(operationRef)]);
        if (!snapshot.exists()) throw new Error('Stay record missing hai.');
        if (alreadySaved.exists()) return snapshot.data();
        const current = snapshot.data();
        if (current.hotelId !== hotelId || normalizeRoom(current.room) !== normalizeRoom(stay.room)) throw new Error('Stay/room change ho chuka hai. Fresh stay kholein.');
        const next = applyStayEntry(current, savedEntry);
        transaction.update(stayRef, {...next, updatedAt: serverTimestamp()});
        queueDayBookEntry(transaction, operationRef, {entryType: payment ? 'payment' : entry.type === 'discount' ? 'adjustment' : 'revenue',
          category: entry.type === 'food_payment' ? 'food_payment' : entry.type === 'payment' ? 'balance' : entry.type,
          title: entry.title || entry.type, amount: entry.type === "discount" ? roundMoney(calculateBill(next).discount - calculateBill(current).discount) : Number(entry.amount || 0),
          paymentMode: entry.paymentMode || '', transactionReference: entry.transactionReference || '',
          room: current.room, guestName: current.guestName || '', stayId: stay.stayId,
          eventAt: new Date(savedEntry.addedAt), note: entry.note || '', hideFromRegister: true, operationId: operationRef.id});
        const gstDelta = roundMoney(calculateBill(next).gstAmount - calculateBill(current).gstAmount);
        if (gstDelta !== 0) queueDayBookEntry(transaction, doc(db, 'dayBookEntries', `${operationRef.id}_GST`), {entryType: 'adjustment', category: 'gst', title: 'GST adjustment', amount: gstDelta, room: current.room, guestName: current.guestName || '', stayId: stay.stayId, eventAt: new Date(savedEntry.addedAt), hideFromRegister: true});
        if (entry.type !== 'discount') {
          const discountDelta = roundMoney(calculateBill(next).discount - calculateBill(current).discount);
          if (discountDelta !== 0) queueDayBookEntry(transaction, doc(db, 'dayBookEntries', `${operationRef.id}_DISCOUNT`), {entryType: 'adjustment', category: 'discount', title: 'Discount adjustment', amount: discountDelta, room: current.room, guestName: current.guestName || '', stayId: stay.stayId, eventAt: new Date(savedEntry.addedAt), hideFromRegister: true});
        }
        return {...next, receiptNumber: savedEntry.receiptNumber, paymentEntry: savedEntry};
      });
      await refreshOccupiedItem(stay.room, result);
      if (payment) await logPaymentReceived({hotelId, hotelName: hotelName || hotelId, room: result.room, stayId: stay.stayId,
        guestName: result.guestName || '', amount: savedEntry.amount, receiptNumber: savedEntry.receiptNumber,
        title: savedEntry.title || 'Payment Received', metadata: {paymentMode: entry.paymentMode || ''}});
      else if (entry.type === 'discount') await logDiscountUpdate({hotelId, hotelName, room: result.room, stayId: stay.stayId, amount: result.discountAmount, description: entry.note || 'Discount updated'});
      alert(payment ? 'Payment save ho gayi ✅' : 'Entry save ho gayi ✅');
      return result;
    } catch (error) {alert('Entry save nahi hui: ' + error.message); throw error;}
    finally {setRoomActionSaving(false);}
  };

  const handleRoomCheckout = async (checkoutData) => {
    const stay = selectedOccupiedRoom;

    if (!stay || roomActionSaving) return;

    const stayId = stay.stayId || stay.id || "";
    const room = normalizeRoom(stay.room);

    if (!stayId) {
      alert("Stay ID missing hai. Checkout complete nahi ho sakta.");
      return;
    }

    if (!room) {
      alert("Room number missing hai.");
      return;
    }

    let finalBalance = Math.max(0, Number(checkoutData?.balance || 0));
    const allowPendingCheckout =
      checkoutData?.allowPendingCheckout === true ||
      checkoutData?.pendingCheckoutOverride === true;

    if (finalBalance > 0 && !allowPendingCheckout) {
      alert(
        `₹${finalBalance} balance pending hai. Manager Override ke bina checkout allow nahi hai.`
      );
      return;
    }

    if (
      finalBalance > 0 &&
      !String(checkoutData?.pendingReason || "").trim()
    ) {
      alert("Pending balance ke saath checkout ke liye reason required hai.");
      return;
    }

    setRoomActionSaving(true);

    try {
      const checkoutAt = checkoutData?.actualCheckOutAt
        ? new Date(checkoutData.actualCheckOutAt)
        : new Date();

      if (Number.isNaN(checkoutAt.getTime())) {
        throw new Error("Invalid checkout date/time.");
      }

      const checkoutIso = checkoutAt.toISOString();
      const checkoutBusinessDate = getBusinessDateKey(checkoutAt);
      const invoiceNumber =
        checkoutData?.invoiceNumber || stay.invoiceNumber || `INV-${Date.now()}`;

      const stayReference = doc(db, "hotelStays", stayId);
      const staySnapshot = await getDoc(stayReference);

      if (!staySnapshot.exists()) {
        throw new Error("Hotel stay record Firestore me nahi mila.");
      }

      const currentStayData = staySnapshot.data();
      const existingPayments = Array.isArray(currentStayData.payments)
        ? currentStayData.payments
        : [];

      const rawCheckoutPayment = checkoutData?.checkoutPaymentEntry;
      const checkoutPaymentEntry =
        rawCheckoutPayment && Number(rawCheckoutPayment.amount || 0) > 0
          ? {
              ...rawCheckoutPayment,
              amount: Number(rawCheckoutPayment.amount || 0),
              quantity: 1,
              rate: Number(rawCheckoutPayment.amount || 0),
              paymentMode: rawCheckoutPayment.paymentMode || "Cash",
              transactionReference: String(
                rawCheckoutPayment.transactionReference || ""
              ).trim(),
              receiptNumber:
                rawCheckoutPayment.receiptNumber || `RCPT-${Date.now()}`,
              title: rawCheckoutPayment.title || "Checkout Payment",
              note:
                rawCheckoutPayment.note || "Payment received during checkout",
              source: "checkout_payment",
              addedAt: rawCheckoutPayment.addedAt || checkoutIso,
            }
          : null;

      const updatedPayments = checkoutPaymentEntry
        ? [...existingPayments, checkoutPaymentEntry]
        : existingPayments;

      let finalPaidTotal = Number(
        checkoutData?.paidTotal ??
          checkoutData?.paid ??
          currentStayData.paidTotal ??
          currentStayData.advance ??
          0
      );

      const stayUpdatePayload = {
        status: "checked_out",
        checkedOut: true,
        checkoutConfirmedBy: auth.currentUser?.uid || "",
        checkoutConfirmedAt: serverTimestamp(),
        checkoutLockId: getRoomLifecycleLockId(room),
        actualCheckOutAt: checkoutIso,
        checkedOutAt: checkoutIso,
        checkoutBusinessDate,
        invoiceNumber,

        roomRent: Number(
          checkoutData?.roomRent ?? currentStayData.tariff ?? 0
        ),
        foodTotal: Number(
          checkoutData?.food ?? currentStayData.foodTotal ?? 0
        ),
        laundryTotal: Number(
          checkoutData?.laundry ?? currentStayData.laundryTotal ?? 0
        ),
        extraTotal: Number(
          checkoutData?.extra ?? currentStayData.extraTotal ?? 0
        ),
        grossTotal: Number(checkoutData?.grossTotal || 0),
        discountType:
          checkoutData?.discountType || currentStayData.discountType || "flat",
        discountValue: Number(
          checkoutData?.discountValue ?? currentStayData.discountValue ?? 0
        ),
        discountAmount: Number(
          checkoutData?.discountAmount ?? checkoutData?.discount ?? 0
        ),
        taxableAmount: Number(checkoutData?.taxableAmount || 0),
        gstRate: Number(checkoutData?.gstRate || 0),
        gstAmount: Number(checkoutData?.gstAmount || 0),
        cgst: Number(checkoutData?.cgst || 0),
        sgst: Number(checkoutData?.sgst || 0),
        damageCharge: Number(checkoutData?.damageCharge || 0),
        damageNote: String(checkoutData?.damageNote || "").trim(),
        grandTotal: Number(checkoutData?.grandTotal || 0),
        paidTotal: finalPaidTotal,
        balance: finalBalance,
        excessPayment: Number(checkoutData?.excessPayment || 0),
        paymentStatus:
          checkoutData?.paymentStatus ||
          (finalBalance <= 0
            ? "paid"
            : finalPaidTotal > 0
            ? "partial"
            : "pending"),
        payments: updatedPayments,
        checkoutPayment: Number(checkoutData?.checkoutPayment || 0),
        checkoutPaymentReceiptNumber:
          checkoutPaymentEntry?.receiptNumber || "",
        pendingCheckoutOverride: Boolean(
          finalBalance > 0 && allowPendingCheckout
        ),
        allowPendingCheckout: Boolean(finalBalance > 0 && allowPendingCheckout),
        pendingReason:
          finalBalance > 0
            ? String(checkoutData?.pendingReason || "").trim()
            : "",
        managerNote: String(checkoutData?.managerNote || "").trim(),
        roomKeysReceived: checkoutData?.roomKeysReceived === true,
        guestSignatureReceived:
          checkoutData?.guestSignatureReceived === true,
        idDocumentsVerified: checkoutData?.idDocumentsVerified === true,
        roomCheckedForDamage:
          checkoutData?.roomCheckedForDamage === true,
        sendRoomForCleaning: checkoutData?.sendRoomForCleaning !== false,
        printInvoiceAfterCheckout:
          checkoutData?.printInvoiceAfterCheckout !== false,
        printReceiptAfterCheckout:
          checkoutData?.printReceiptAfterCheckout === true,
        checkoutCompleted: true,
        updatedAt: serverTimestamp(),
      };

      const checkoutLifecycleLockRef =
        getRoomLifecycleLockRef(room);

      /*
       * Phase 2 financial integrity:
       * Stay close + lifecycle lock + current Day Book room row +
       * checkout payment + damage/pending status + checkout status + invoice
       * ek hi Firestore transaction me commit hote hain.
       */
      const checkoutOperationId =
        `CHECKOUT_${stayId}`;

      const checkoutDayBookSnap =
        await getDocs(
          query(
            collection(
              db,
              "dayBookEntries"
            ),
            where(
              "stayId",
              "==",
              stayId
            )
          )
        );

      const latestRoomRow =
        checkoutDayBookSnap.docs
          .map((item) => ({
            id: item.id,
            ...item.data(),
          }))
          .filter(
            (entry) =>
              entry?.category === "room" &&
              entry?.hideFromRegister !==
                true
          )
          .sort(
            (first, second) =>
              new Date(
                second?.eventAt || 0
              ).getTime() -
              new Date(
                first?.eventAt || 0
              ).getTime()
          )[0];

      const checkoutPaymentDayBookRef =
        doc(
          db,
          "dayBookEntries",
          `${checkoutOperationId}_PAYMENT`
        );

      const damageDayBookRef =
        doc(
          db,
          "dayBookEntries",
          `${checkoutOperationId}_DAMAGE`
        );

      const pendingDayBookRef =
        doc(
          db,
          "dayBookEntries",
          `${checkoutOperationId}_PENDING`
        );

      const checkoutStatusDayBookRef =
        doc(
          db,
          "dayBookEntries",
          `${checkoutOperationId}_STATUS`
        );

      const invoiceReference =
        doc(
          db,
          "hotelInvoices",
          invoiceNumber
        );

      const checkoutCommit =
        await runTransaction(
          db,
          async (transaction) => {
            const latestStaySnapshot =
              await transaction.get(
                stayReference
              );

            const lockSnapshot =
              await transaction.get(
                checkoutLifecycleLockRef
              );

            const checkoutBoardRef = getBoardDocRef();
            const checkoutBoardSnapshot = await transaction.get(checkoutBoardRef);
            const checkoutBoard = normalizeBoardData(checkoutBoardSnapshot.exists() ? checkoutBoardSnapshot.data() : {});

            if (
              !latestStaySnapshot.exists()
            ) {
              throw new Error(
                "Hotel stay record checkout transaction me nahi mila."
              );
            }

            const latestStayData =
              latestStaySnapshot.data();

            const currentRowId = latestStayData.currentRoomDayBookEntryId || latestRoomRow?.id;
            const currentRowSnapshot = currentRowId ? await transaction.get(doc(db, 'dayBookEntries', currentRowId)) : null;
            const transactionRoomRow = currentRowSnapshot?.exists() ? {id: currentRowSnapshot.id, ...currentRowSnapshot.data()} : null;

            if (
              latestStayData.checkoutCompleted ===
                true ||
              latestStayData.checkedOut ===
                true ||
              String(
                latestStayData.status || ""
              )
                .trim()
                .toLowerCase() ===
                "checked_out"
            ) {
              /*
               * Idempotent recovery:
               * Checkout pehle hi commit ho chuka ho to isko error mat banao.
               * Koi financial row/invoice/payment dobara create nahi hogi.
               * Transaction ke baad sirf Room Board / lifecycle state repair hogi.
               */
              return {
                alreadyCheckedOut: true,
                payments: Array.isArray(latestStayData.payments)
                  ? latestStayData.payments
                  : [],
              };
            }

            const latestPayments =
              Array.isArray(
                latestStayData.payments
              )
                ? latestStayData.payments
                : [];

            const checkoutReceiptAlreadySaved =
              checkoutPaymentEntry
                ? latestPayments.some(
                    (item) =>
                      String(
                        item?.receiptNumber ||
                          ""
                      ).trim() ===
                      String(
                        checkoutPaymentEntry
                          .receiptNumber ||
                          ""
                      ).trim()
                  )
                : false;

            const transactionPayments =
              checkoutPaymentEntry &&
              !checkoutReceiptAlreadySaved
                ? [
                    ...latestPayments,
                    checkoutPaymentEntry,
                  ]
                : latestPayments;

            if (normalizeRoom(latestStayData.room) !== room || !lockSnapshot.exists() || lockSnapshot.data().activeStayId !== stayId) {
              throw new Error('Room/stay doosre device par shift ya change ho chuka hai. Fresh stay kholein.');
            }
            if (!isStayActive(latestStayData) || latestStayData.hotelId !== hotelId) throw new Error('Active stay/hotel mismatch.');
            const actualCheckIn = new Date(latestStayData.checkInAt);
            if (!Number.isFinite(actualCheckIn.getTime()) || checkoutAt < actualCheckIn) throw new Error('Checkout actual check-in se pehle nahi ho sakta.');
            const freshStay = {...latestStayData,
              damageCharge: Math.max(0, Number(checkoutData?.damageCharge || 0)),
              paidTotal: calculatePaidTotal(latestStayData) + (checkoutReceiptAlreadySaved ? 0 : Number(checkoutPaymentEntry?.amount || 0))};
            const freshBill = calculateBill(freshStay);
            finalBalance = freshBill.balance;
            finalPaidTotal = freshStay.paidTotal;
            if (finalBalance > 0 && (!allowPendingCheckout || !String(checkoutData?.pendingReason || '').trim())) {
              throw new Error(`Fresh balance ₹${finalBalance.toFixed(2)} pending hai. Payment/override confirm karein.`);
            }
            Object.assign(stayUpdatePayload, financialFields(freshStay), {
              tariff: freshBill.roomRent, roomRent: freshBill.roomRent,
              foodTotal: freshBill.food, laundryTotal: freshBill.laundry, extraTotal: freshBill.extra,
              discountType: latestStayData.discountType || 'flat', discountValue: Number(latestStayData.discountValue || 0),
              gstRate: freshBill.gstRate, paidTotal: finalPaidTotal, advanceCreditBalance: latestStayData.advanceCreditBalance || 0,
              excessAdvanceCredit: latestStayData.excessAdvanceCredit || 0, balance: finalBalance,
              pendingCheckoutOverride: finalBalance > 0 && allowPendingCheckout, allowPendingCheckout: finalBalance > 0 && allowPendingCheckout,
              pendingReason: finalBalance > 0 ? String(checkoutData?.pendingReason || '').trim() : ''});

            const checkoutCard = {...latestStayData, ...stayUpdatePayload, room, stayId,
              status: 'checkout_pending', cleaningStatus: 'pending', roomReady: false, checkoutAt: checkoutIso,
              updatedAt: checkoutIso, checkoutConfirmedAt: checkoutIso};
            const clean = checkoutData?.sendRoomForCleaning !== false;
            transaction.set(checkoutBoardRef, {...checkoutBoard, hotelId, hotelName: hotelName || "", schemaVersion: 2, revision: Number(checkoutBoardSnapshot.data()?.revision || 0) + 1,
              occupiedRooms: checkoutBoard.occupiedRooms.filter(item => roomKey(item) !== room),
              vacantRooms: [...checkoutBoard.vacantRooms.filter(item => roomKey(item) !== room), ...(clean ? [] : [{room, category: latestStayData.category || '', roomCategory: latestStayData.roomCategory || ''}])],
              checkoutRooms: [...checkoutBoard.checkoutRooms.filter(item => roomKey(item) !== room), ...(clean ? [checkoutCard] : [])], updatedAt: serverTimestamp()}, {merge: true});
            if (clean) transaction.set(doc(db, 'housekeepingTasks', `${hotelId}_${room}`), {
              hotelId, hotelName: hotelName || '', room, stayId, guestName: latestStayData.guestName || '',
              checkedOutAt: checkoutIso, cleaningStatus: 'pending', status: 'pending', roomReady: false,
              maintenanceRequired: false, cleaningChecklist: {}, assignedTo: '', updatedAt: serverTimestamp()});

            transaction.update(
              stayReference,
              {
                ...stayUpdatePayload,
                payments:
                  transactionPayments,
                financialSyncStatus:
                  "complete",
                financialSyncOperationId:
                  checkoutOperationId,
              }
            );

            if (
              checkoutData?.sendRoomForCleaning !==
              false
            ) {
              transaction.set(
                checkoutLifecycleLockRef,
                {
                  hotelId,
                  hotelName:
                    hotelName || "",
                  room,
                  activeStayId: "",
                  lastStayId: stayId,
                  status:
                    "checkout_pending",
                  checkedOutAt:
                    checkoutIso,
                  updatedAt:
                    serverTimestamp(),
                },
                { merge: true }
              );
            } else if (
              lockSnapshot.exists()
            ) {
              transaction.delete(
                checkoutLifecycleLockRef
              );
            }

            if (
              transactionRoomRow?.id
            ) {
              const previousNote =
                String(
                  transactionRoomRow
                    .ledgerNote ||
                    transactionRoomRow.note ||
                    ""
                ).trim();

              transaction.update(
                doc(
                  db,
                  "dayBookEntries",
                  transactionRoomRow.id
                ),
                {
                  ledgerBalance:
                    finalBalance,
                  ledgerCheckOut:
                    formatDayBookOut(
                      checkoutAt
                    ),
                  ledgerNote: [
                    previousNote,
                    "Final Checkout",
                  ]
                    .filter(Boolean)
                    .join(" • "),
                  checkoutOperationId,
                  updatedAt:
                    serverTimestamp(),
                }
              );
            }

            if (
              checkoutPaymentEntry
            ) {
              queueDayBookEntry(
                transaction,
                checkoutPaymentDayBookRef,
                {
                  entryType:
                    "payment",
                  category:
                    "pending_received",
                  title:
                    `Pending Amount Received - Room ${room}`,
                  amount:
                    checkoutPaymentEntry
                      .amount,
                  paymentMode:
                    checkoutPaymentEntry
                      .paymentMode,
                  transactionReference:
                    checkoutPaymentEntry
                      .transactionReference ||
                    "",
                  room,
                  guestName:
                    stay.guestName ||
                    latestStayData
                      .guestName ||
                    "",
                  stayId,
                  eventAt:
                    checkoutAt,
                  note:
                    `Pending Amount Received – Room No. ${room}`,
                  hideFromRegister:
                    true,
                  ledgerRoom:
                    room,
                  ledgerGuestName:
                    stay.guestName ||
                    latestStayData
                      .guestName ||
                    "",
                  ledgerAdvance:
                    Number(
                      checkoutPaymentEntry
                        .amount ||
                        0
                    ),
                  ledgerNote:
                    `Pending Amount Received – Room No. ${room}`,
                  ledgerTransactionReference:
                    checkoutPaymentEntry
                      .transactionReference ||
                    "",
                  operationId:
                    checkoutOperationId,
                  source:
                    "pending_received",
                }
              );
            }

            if (
              Number(
                checkoutData?.damageCharge ||
                  0
              ) > 0
            ) {
              queueDayBookEntry(
                transaction,
                damageDayBookRef,
                {
                  entryType:
                    "revenue",
                  category:
                    "damage_charge",
                  title:
                    "Room Damage Charge",
                  amount:
                    Number(
                      checkoutData
                        .damageCharge ||
                        0
                    ),
                  room,
                  guestName:
                    stay.guestName ||
                    latestStayData
                      .guestName ||
                    "",
                  stayId,
                  eventAt:
                    checkoutAt,
                  note:
                    checkoutData
                      .damageNote ||
                    "Room damage/missing item charge",
                  operationId:
                    checkoutOperationId,
                }
              );
            }

            if (
              finalBalance > 0
            ) {
              queueDayBookEntry(
                transaction,
                pendingDayBookRef,
                {
                  entryType:
                    "status",
                  category:
                    "pending_checkout",
                  title:
                    `Pending Amount from Room ${room}`,
                  amount:
                    finalBalance,
                  paymentMode:
                    "Pending",
                  room,
                  guestName:
                    stay.guestName ||
                    latestStayData
                      .guestName ||
                    "",
                  stayId,
                  eventAt:
                    checkoutAt,
                  note:
                    checkoutData
                      .pendingReason ||
                    "Pending balance at checkout",
                  operationId:
                    checkoutOperationId,
                }
              );
            }

            queueDayBookEntry(
              transaction,
              checkoutStatusDayBookRef,
              {
                entryType:
                  "status",
                category:
                  "checkout",
                title:
                  `Guest Checkout • ${invoiceNumber}`,
                amount: 0,
                room,
                guestName:
                  latestStayData.guestName ||
                  stay.guestName ||
                  "",
                stayId,
                eventAt:
                  checkoutAt,
                note:
                  checkoutData
                    ?.managerNote ||
                  "",
                operationId:
                  checkoutOperationId,
              }
            );

            transaction.set(
              invoiceReference,
              {
                ...latestStayData, ...stayUpdatePayload,
                invoiceNumber,
                hotelId,
                hotelName:
                  hotelName || "",
                stayId,
                room,
                guestName:
                  latestStayData.guestName || stay.guestName || "Guest",
                phoneNumber:
                  latestStayData.phoneNumber ||
                  stay.phoneNumber ||
                  "",
                checkInAt:
                  latestStayData
                    .checkInAt ||
                  stay.checkInAt ||
                  "",
                checkOutAt:
                  checkoutIso,
                checkoutBusinessDate,
                roomRent:
                  stayUpdatePayload
                    .roomRent,
                foodTotal:
                  stayUpdatePayload
                    .foodTotal,
                laundryTotal:
                  stayUpdatePayload
                    .laundryTotal,
                extraTotal:
                  stayUpdatePayload
                    .extraTotal,
                damageCharge:
                  stayUpdatePayload
                    .damageCharge,
                grossTotal:
                  stayUpdatePayload
                    .grossTotal,
                discountAmount:
                  stayUpdatePayload
                    .discountAmount,
                taxableAmount:
                  stayUpdatePayload
                    .taxableAmount,
                gstRate:
                  stayUpdatePayload
                    .gstRate,
                gstAmount:
                  stayUpdatePayload
                    .gstAmount,
                cgst:
                  stayUpdatePayload
                    .cgst,
                sgst:
                  stayUpdatePayload
                    .sgst,
                grandTotal:
                  stayUpdatePayload
                    .grandTotal,
                paidTotal:
                  stayUpdatePayload
                    .paidTotal,
                balance:
                  stayUpdatePayload
                    .balance,
                paymentStatus:
                  stayUpdatePayload
                    .paymentStatus,
                pendingCheckoutOverride:
                  stayUpdatePayload
                    .pendingCheckoutOverride,
                pendingReason:
                  stayUpdatePayload
                    .pendingReason,
                payments:
                  transactionPayments,
                operationId:
                  checkoutOperationId,
                createdAt:
                  serverTimestamp(),
                createdAtIso:
                  checkoutIso,
                createdBy:
                  auth.currentUser
                    ?.email ||
                  "manager",
                updatedAt:
                  serverTimestamp(),
              },
              { merge: true }
            );

            return {
              payments: transactionPayments,
              completedStay: {...latestStayData, ...stayUpdatePayload, payments: transactionPayments, financialSyncStatus: "complete", financialSyncOperationId: checkoutOperationId},
            };
          }
        );

      if (checkoutCommit?.alreadyCheckedOut === true) {
        const freshBoard = await getDoc(getBoardDocRef());
        if (freshBoard.exists()) applyBoardData(freshBoard.data());
        setSelectedOccupiedRoom(null);
        alert('Is stay ka checkout pehle hi complete ho chuka hai.');
        return;
      }

      const committedPayments =
        checkoutCommit.payments;

      // Read-only inspection after checkout; never auto-close other stay records.
      try {
        await reconcileHotelLifecycle(
          getCurrentBoardSnapshot(),
          { reason: `after_checkout_${room}` }
        );
      } catch (reconcileError) {
        console.warn(
          "Post-checkout lifecycle reconciliation warning:",
          reconcileError
        );
      }

      if (checkoutPaymentEntry) {
        try {
          await logPaymentReceived({
            hotelId,
            hotelName: hotelName || hotelId,
            room,
            stayId,
            guestName: stay.guestName || currentStayData.guestName || "",
            invoiceNumber,
            receiptNumber: checkoutPaymentEntry.receiptNumber || "",
            referenceId:
              checkoutPaymentEntry.transactionReference ||
              checkoutPaymentEntry.receiptNumber ||
              invoiceNumber,
            amount: checkoutPaymentEntry.amount,
            title:
              finalBalance > 0
                ? "Partial Checkout Payment"
                : "Checkout Payment Received",
            description: `Checkout payment received • ${checkoutPaymentEntry.paymentMode || "-"}`,
            metadata: {
              paymentMode: checkoutPaymentEntry.paymentMode || "",
              transactionReference: checkoutPaymentEntry.transactionReference || "",
              remainingBalance: finalBalance,
              operationId:
                checkoutOperationId,
            },
            eventAt: checkoutAt,
          });
        } catch (auditError) {
          console.warn("Checkout payment audit log error:", auditError);
        }
      }

      const completedCheckoutStay = {
        ...checkoutCommit.completedStay,

        id: stayId,
        stayId,
        hotelId,
        hotelName: hotelName || currentStayData.hotelName || hotelId || "",
        room,
        invoiceNumber,
        actualCheckOutAt: checkoutIso,
        checkedOutAt: checkoutIso,
        checkoutBusinessDate,
        payments: committedPayments,
        checkoutPaymentEntry,
        checkoutPayment: Number(checkoutData?.checkoutPayment || 0),
        paid: stayUpdatePayload.paidTotal,
        paidTotal: stayUpdatePayload.paidTotal,
        status: "checked_out",
        checkedOut: true,
        checkoutCompleted: true,
      };

      try {
        await logGuestCheckout({
          hotelId,
          hotelName: hotelName || hotelId,
          room,
          guestName: stay.guestName || currentStayData.guestName || "",
          phoneNumber: stay.phoneNumber || currentStayData.phoneNumber || "",
          stayId,
          invoiceNumber,
          receiptNumber: checkoutPaymentEntry?.receiptNumber || "",
          amount: stayUpdatePayload.grandTotal,
          description:
            finalBalance > 0
              ? `Checkout completed with ${finalBalance} pending balance.`
              : "Checkout completed with full payment.",
          after: {
            status: "checked_out",
            grandTotal: stayUpdatePayload.grandTotal,
            paidTotal: stayUpdatePayload.paidTotal,
            balance: stayUpdatePayload.balance,
            paymentStatus: stayUpdatePayload.paymentStatus,
            sendRoomForCleaning: stayUpdatePayload.sendRoomForCleaning,
          },
          eventAt: checkoutAt,
        });
      } catch (auditError) {
        console.warn(
          "Checkout audit log warning (financial checkout already committed):",
          auditError
        );
      }

      setSelectedOccupiedRoom(null);

      const cloudCheckoutBoard = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed room action: refresh pending', error); return null;});
      if (cloudCheckoutBoard?.exists()) applyBoardData(cloudCheckoutBoard.data());

      try {
        await sendTelegram(
          `🚪 <b>ROOM CHECKOUT</b>\n🏢 ${hotelName || hotelId}\n🚪 Room: <b>${room}</b>\n👤 ${
            stay.guestName || currentStayData.guestName || "Guest"
          }\n🧾 Invoice: ${invoiceNumber}\n💰 Total: ₹${
            stayUpdatePayload.grandTotal
          }\n✅ Paid: ₹${stayUpdatePayload.paidTotal}\n⏳ Balance: ₹${finalBalance}`
        );
      } catch (telegramError) {
        console.warn("Checkout Telegram notification failed:", telegramError);
      }

      setCheckoutSuccessData(completedCheckoutStay);
    } catch (error) {
      console.error("Room checkout error:", error);
      alert("Checkout complete nahi hua: " + (error?.message || error));
      throw error;
    } finally {
      setRoomActionSaving(false);
    }
  };

  const handleOpenHousekeepingRoom = (housekeepingTask) => {
    if (!housekeepingTask || !housekeepingTask.room) {
      alert("Housekeeping task me room number missing hai.");
      return;
    }

    const room = normalizeRoom(housekeepingTask.room);
    const boardRoom = checkoutRooms.find(
      (item) => normalizeRoom(getRoomValue(item)) === room
    );

    setSelectedCleaningRoom({
      ...(boardRoom || {}),
      ...housekeepingTask,
      room,
      hotelId: housekeepingTask.hotelId || hotelId,
      hotelName: housekeepingTask.hotelName || hotelName || hotelId,
      stayId: housekeepingTask.stayId || boardRoom?.stayId || "",
      guestName: housekeepingTask.guestName || boardRoom?.guestName || "",
      checkedOutAt:
        housekeepingTask.checkedOutAt ||
        housekeepingTask.actualCheckOutAt ||
        boardRoom?.checkedOutAt ||
        boardRoom?.actualCheckOutAt ||
        "",
      cleaningStatus:
        housekeepingTask.cleaningStatus ||
        housekeepingTask.status ||
        boardRoom?.cleaningStatus ||
        "pending",
      status:
        housekeepingTask.status ||
        housekeepingTask.cleaningStatus ||
        boardRoom?.status ||
        "pending",
      cleaningChecklist:
        housekeepingTask.cleaningChecklist ||
        boardRoom?.cleaningChecklist ||
        {},
      assignedTo:
        housekeepingTask.assignedTo || boardRoom?.assignedTo || "",
      maintenanceRequired:
        housekeepingTask.maintenanceRequired === true ||
        boardRoom?.maintenanceRequired === true,
    });
  };

  const commitRoomCleaning = async (data, {ready = false, ticket = null, resolvingTicketId = ''} = {}) => {
    const room = normalizeRoom(data.room);
    const expectedStayId = String(data.stayId || '');
    const baselineCard = checkoutRooms.find(item => roomKey(item) === room);
    const cleanedBy = String(data.cleanedBy || data.assignedTo || '').replace(/\s+/g, ' ').trim();
    if (ready && (!cleanedBy || cleanedBy.length > 80)) throw new Error('Clean karne wale staff ka naam likho (maximum 80 characters).');
    const actor = ready ? getCurrentActor() : null;
    if (ready && !actor?.actorId) throw new Error('Manager login required hai.');
    const readyAt = new Date().toISOString();
    const readyAuditRef = ready ? doc(collection(db, 'auditLogs')) : null;
    // Legacy quick checkout can leave an older housekeeping task behind. Read all
    // known room stays again inside the transaction before releasing the room.
    const roomStayReferences = ready ? (await getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)))).docs
      .filter(record => normalizeRoom(record.data().room) === room).map(record => record.ref) : [];
    const maintenanceRefs = ready && data.resolveShiftMaintenance === true ? (await getDocs(query(collection(db, 'maintenanceTickets'), where('hotelId', '==', hotelId)))).docs
      .filter(record => normalizeRoom(record.data().room) === room).map(record => record.ref) : [];
    return runTransaction(db, async transaction => {
      const boardRef = getBoardDocRef(); const lockRef = getRoomLifecycleLockRef(room);
      const taskRef = doc(db, 'housekeepingTasks', `${hotelId}_${room}`);
      const [boardSnap, lockSnap, taskSnap] = await Promise.all([transaction.get(boardRef), transaction.get(lockRef), transaction.get(taskRef)]);
      const board = normalizeBoardData(boardSnap.exists() ? boardSnap.data() : {});
      const card = board.checkoutRooms.find(item => roomKey(item) === room);
      const lock = lockSnap.exists() ? lockSnap.data() : {};
      if (lock.activeStayId || board.occupiedRooms.some(item => roomKey(item) === room)) throw new Error('Room mein ab active guest hai. Old cleaning action reject hui.');
      if (!card || String(card.stayId || '') !== expectedStayId || (lock.lastStayId && String(lock.lastStayId) !== expectedStayId)) throw new Error('Cleaning stay/room badal gaya hai. Fresh task kholein.');
      if (card.boardOnlyCheckout === true && card.quickOccupancyId && data.quickOccupancyId !== card.quickOccupancyId) throw new Error('Room ki quick guest entry badal gayi hai. Fresh Mark Ready card kholein.');
      const currentTask = taskSnap.exists() ? taskSnap.data() : null;
      if (currentTask && (currentTask.hotelId !== hotelId || normalizeRoom(currentTask.room) !== room)) throw new Error('Cleaning task hotel/room mismatch hai. Pehle verification karein.');
      const roomStaySnapshots = ready ? await Promise.all(roomStayReferences.map(reference => transaction.get(reference))) : [];
      const conflicts = roomStaySnapshots.filter(snapshot => snapshot.exists() && snapshot.data().hotelId === hotelId &&
        normalizeRoom(snapshot.data().room) === room && isStayActive(snapshot.data()));
      if (conflicts.length) {
        const error = new Error(`Room ${room}: guest stay abhi active hai. Guest checkout verify karein; Mark Ready nahi hua.`);
        error.roomStayConflicts = conflicts.map(snapshot => {const stay = snapshot.data(); return {
          id: snapshot.id, guestName: stay.guestName || 'Guest', checkInAt: stay.checkInAt || '',
          legacyRepair: stay.closedByLifecycleRepair === true && stay.lifecycleRepairReason === 'later_checkout_exists_for_same_room' && !!stay.orphanClosedAt,
        };});
        throw error;
      }
      const stayRef = expectedStayId ? doc(db, 'hotelStays', expectedStayId) : null;
      const staySnap = stayRef ? await transaction.get(stayRef) : null;
      const currentStay = staySnap?.exists() ? staySnap.data() : null;
      const taskBelongsToPreviousVisit = currentTask && (String(currentTask.stayId || '') !== expectedStayId ||
        (card.boardOnlyCheckout === true && card.quickOccupancyId && currentTask.quickOccupancyId !== card.quickOccupancyId));
      let archivedCleaningTask = null;
      if (taskBelongsToPreviousVisit) {
        const fail = reason => {throw new Error(`Room ${room}: purani cleaning task verify nahi hui — ${reason}. Koi guest/task delete nahi hua.`);};
        const time = value => {
          if (typeof value?.toDate === 'function') return value.toDate().getTime();
          if (value?.seconds) return Number(value.seconds) * 1000;
          return Date.parse(value || '');
        };
        const previousStayId = String(currentTask.stayId || '');
        const previousSnapshot = previousStayId ? await transaction.get(doc(db, 'hotelStays', previousStayId)) : null;
        const previousStay = previousSnapshot?.exists() ? previousSnapshot.data() : null;
        const checkoutAt = time(card.actualCheckOutAt || card.checkedOutAt || card.checkoutAt || currentStay?.actualCheckOutAt || currentStay?.checkedOutAt);
        const visitStartedAt = time(card.checkInAt || card.occupiedAt);
        const visitBoundary = Number.isFinite(visitStartedAt) ? visitStartedAt : checkoutAt;
        const previousCheckout = time(previousStay?.actualCheckOutAt || previousStay?.checkedOutAt || previousStay?.checkoutAt);
        const taskCheckout = time(currentTask.actualCheckOutAt || currentTask.checkedOutAt || currentTask.checkoutAt);
        const cleanedAt = time(currentTask.cleanedAt || currentTask.roomReadyAt);
        const isQuickCheckout = card.boardOnlyCheckout === true && !expectedStayId;
        const isConfirmedCheckout = currentStay?.hotelId === hotelId && normalizeRoom(currentStay.room) === room &&
          currentStay.checkedOut === true && currentStay.checkoutCompleted === true && !isStayActive(currentStay);
        if (!ready || (!isQuickCheckout && !isConfirmedCheckout)) fail('current checkout ka proof incomplete hai');
        if (lock.hotelId !== hotelId || normalizeRoom(lock.room) !== room ||
            !['cleaning', 'checkout_pending'].includes(lock.status)) fail('current cleaning room lock match nahi hai');
        if (!Number.isFinite(checkoutAt) || !Number.isFinite(visitBoundary)) fail('current checkout date missing hai');
        if (previousStayId) {
          if (!previousStay) fail(`previous stay ${previousStayId} nahi mila`);
          if (previousStay.hotelId !== hotelId) fail('previous stay kisi doosre hotel ka hai');
          if (normalizeRoom(previousStay.room) === room) {
            if (previousStay.checkedOut !== true || previousStay.checkoutCompleted !== true || isStayActive(previousStay)) fail('previous guest ka checkout incomplete hai');
            if (!Number.isFinite(previousCheckout) || previousCheckout > visitBoundary) fail('previous checkout ki date current visit se match nahi hai');
          } else {
            // A shifted guest may still be active in another room. Preserve that
            // guest unchanged; verify the old-room task's earlier event instead.
            if (!Number.isFinite(taskCheckout) || taskCheckout > visitBoundary) fail('shifted guest ki old-room task date verify nahi hui');
          }
        } else {
          if (currentTask.boardOnlyCheckout !== true) fail('previous task ke paas guest/quick-checkout identity nahi hai');
          const oldEvent = Number.isFinite(taskCheckout) ? taskCheckout : cleanedAt;
          if (!Number.isFinite(oldEvent) || oldEvent > visitBoundary) fail('previous quick checkout ki date verify nahi hui');
        }
        if (Number.isFinite(cleanedAt) && cleanedAt > visitBoundary) fail('cleaning task current visit se newer hai');
        if (currentTask.maintenanceRequired === true || currentTask.status === 'maintenance_hold') fail('previous task mein maintenance pending hai');
        const oldTicketRef = currentTask.maintenanceTicketId ? doc(db, 'maintenanceTickets', currentTask.maintenanceTicketId) : null;
        const oldTicket = oldTicketRef ? await transaction.get(oldTicketRef) : null;
        if (oldTicket?.exists() && !['resolved', 'completed', 'cancelled', 'closed'].includes(oldTicket.data().status)) fail('previous maintenance ticket abhi open hai');
        // Pending/legacy tasks may lack a cleaning-completed timestamp. Verified
        // checkout + fresh occupancy checks, not old housekeeping status, govern
        // whether the manager can confirm that the current room is clean.
        archivedCleaningTask = currentTask;
      }
      if (resolvingTicketId && taskSnap.data()?.maintenanceTicketId && taskSnap.data().maintenanceTicketId !== resolvingTicketId) throw new Error('Room par doosri maintenance ticket active hai.');
      let shiftMaintenanceResolved = false;
      if (ready && (card.maintenanceRequired || lock.status === 'maintenance_hold' || (!archivedCleaningTask && currentTask?.maintenanceRequired))) {
        const eligible = currentTask?.source === 'room_shift' && !taskBelongsToPreviousVisit &&
          !currentTask.maintenanceTicketId && !card.maintenanceTicketId &&
          currentStay?.hotelId === hotelId && normalizeRoom(currentStay.room) !== room &&
          (currentStay.roomShiftHistory || []).some(move => normalizeRoom(move.fromRoom) === room);
        if (!eligible || data.resolveShiftMaintenance !== true) {
          const error = new Error(eligible ? 'Room shift ka maintenance hold hai. Repair complete confirm karein.' : 'Pehle maintenance resolve karein.');
          error.shiftMaintenanceConfirmation = eligible; throw error;
        }
        if (String(data.maintenanceResolutionNote || '').trim().length < 5) throw new Error('Repair completion note likhein (minimum 5 characters).');
        const tickets = await Promise.all(maintenanceRefs.map(ref => transaction.get(ref)));
        if (tickets.some(t => t.exists() && !['resolved','completed','cancelled','closed'].includes(t.data().status))) throw new Error('Open maintenance ticket hai. Maintenance panel se resolve karein.');
        shiftMaintenanceResolved = true;
      }
      const ticketRef = resolvingTicketId ? doc(db, 'maintenanceTickets', resolvingTicketId) : null;
      const ticketSnapshot = ticketRef ? await transaction.get(ticketRef) : null;
      if (ticketSnapshot?.exists() && (ticketSnapshot.data().hotelId !== hotelId || normalizeRoom(ticketSnapshot.data().room) !== room)) throw new Error('Maintenance ticket mismatch.');
      const payload = {...data, ...(shiftMaintenanceResolved ? {maintenanceRequired: false, maintenanceResolvedAt: readyAt, maintenanceResolvedBy: actor.actorId} : {}), ...(ready ? {cleanedBy, assignedTo: cleanedBy, cleanedAt: readyAt} : {}), hotelId, hotelName: hotelName || '', room, updatedAt: serverTimestamp()};
      const target = ready ? 'completed' : (data.cleaningStatus || data.status || 'pending');
      const cardPayload = {...payload, updatedAt: new Date().toISOString()};
      const mergedCard = {...card, ...cardPayload, cleaningStatus: target, status: target, roomReady: ready};
      if (!ready && baselineCard) {
        const before = {checkoutRooms: [baselineCard]}; const after = {checkoutRooms: [{...baselineCard, ...cardPayload, status: target, cleaningStatus: target, roomReady: false}]};
        mergeBoardMutation(board, planBoardMutation(before, after)); // same-field conflicts must not silently win
      }
      if (archivedCleaningTask) transaction.set(doc(collection(db, 'auditLogs')), {
        hotelId, hotelName: hotelName || '', action: 'housekeeping', actionType: 'housekeeping',
        title: `Room ${room} Previous Cleaning Preserved`,
        description: 'Previous cleaning task preserved after verifying checkout and current room occupancy; current cleaning confirmed by manager.',
        room, stayId: expectedStayId, previousStayId: String(archivedCleaningTask.stayId || ''),
        metadata: {archivedTask: archivedCleaningTask, quickOccupancyId: card.quickOccupancyId || ''},
        ...actor, eventAt: readyAt, createdAt: serverTimestamp()});
      transaction.set(taskRef, {...payload, ...(card.boardOnlyCheckout === true ? {
        stayId: expectedStayId, boardOnlyCheckout: true, quickOccupancyId: card.quickOccupancyId || '',
        checkInAt: card.checkInAt || card.occupiedAt || '', checkoutAt: card.checkoutAt || ''} : {}),
        cleaningStatus: target, status: target, roomReady: ready}, {merge: !archivedCleaningTask});
      if (staySnap?.exists() && !isStayActive(staySnap.data()) && normalizeRoom(staySnap.data().room) === room) transaction.update(stayRef, {
        cleaningStatus: target, roomReady: ready, cleaningChecklist: data.cleaningChecklist || {},
        cleaningAssignedTo: ready ? cleanedBy : data.assignedTo || '', ...(ready ? {cleanedBy, cleanedAt: readyAt} : {}), inspectedBy: data.inspectedBy || '',
        maintenanceRequired: data.maintenanceRequired === true, roomReadyAt: ready ? new Date().toISOString() : '', updatedAt: serverTimestamp()});
      if (ticket) transaction.set(ticket.ref, ticket.payload);
      if (ticketSnapshot?.exists()) transaction.update(ticketRef, {status: 'resolved', resolutionNote: data.resolutionNote || '', resolvedAt: data.resolvedAt || new Date().toISOString(), maintenanceCompleted: true, updatedAt: serverTimestamp()});
      transaction.set(boardRef, {...board, hotelId, hotelName: hotelName || "", schemaVersion: 2, revision: Number(boardSnap.data()?.revision || 0) + 1,
        checkoutRooms: ready ? board.checkoutRooms.filter(item => roomKey(item) !== room) : board.checkoutRooms.map(item => roomKey(item) === room ? mergedCard : item),
        vacantRooms: ready ? [...board.vacantRooms.filter(item => roomKey(item) !== room), {room, category: getRoomCategory(card), roomCategory: getRoomCategory(card)}] : board.vacantRooms,
        serviceRooms: ready || resolvingTicketId ? board.serviceRooms.filter(item => roomKey(item) !== room) : board.serviceRooms,
        updatedAt: serverTimestamp()}, {merge: true});
      if (ready) {
        transaction.set(readyAuditRef, {hotelId, hotelName: hotelName || '', action: 'housekeeping', actionType: 'housekeeping',
          title: `Room ${room} Ready`, description: `Room ${room} cleaned by ${cleanedBy} and marked Ready.`,
          room, stayId: expectedStayId, guestName: card.guestName || '', cleanedBy,
          before: {status: card.status || 'cleaning'}, after: {status: 'ready', roomReady: true, cleanedBy},
          metadata: {cleanedBy, shiftMaintenanceResolved, maintenanceResolutionNote: shiftMaintenanceResolved ? String(data.maintenanceResolutionNote).trim() : ''}, ...actor, eventAt: readyAt, createdAt: serverTimestamp()});
        if (lockSnap.exists()) transaction.delete(lockRef);
      }
      else transaction.set(lockRef, {hotelId, room, activeStayId: '', lastStayId: expectedStayId,
        status: data.maintenanceRequired ? 'maintenance_hold' : 'cleaning', updatedAt: serverTimestamp()}, {merge: true});
      return mergedCard;
    });
  };

  const handleSaveCleaningProgress = async (data) => {
    if (!data?.room || cleaningActionSaving) return;
    setCleaningActionSaving(true);
    try {
      const card = await commitRoomCleaning(data);
      const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data());
      setSelectedCleaningRoom(card);
      await logHousekeepingUpdate({hotelId, hotelName, room: data.room, stayId: data.stayId || '', description: `Cleaning status: ${card.cleaningStatus}`});
    } catch (error) {alert(error.message); throw error;}
    finally {setCleaningActionSaving(false);}
  };

  const handleCreateMaintenanceTicket = async (data) => {
    if (!data?.room || cleaningActionSaving) return;
    setCleaningActionSaving(true);
    try {
      const reference = doc(collection(db, 'maintenanceTickets'));
      const payload = {hotelId, hotelName: hotelName || '', room: normalizeRoom(data.room), stayId: data.stayId || '',
        guestName: data.guestName || '', category: data.maintenanceCategory || 'Other', priority: data.maintenancePriority || 'Normal',
        description: data.maintenanceDescription || '', damagedItems: data.damagedItems || '', missingItems: data.missingItems || '',
        status: 'open', reportedFrom: 'room_cleaning', reportedAt: new Date().toISOString(), createdAt: serverTimestamp(), updatedAt: serverTimestamp()};
      const card = await commitRoomCleaning({...data, maintenanceRequired: true, maintenanceTicketId: reference.id, status: 'maintenance_hold', cleaningStatus: 'maintenance_hold'}, {ticket: {ref: reference, payload}});
      const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data());
      setSelectedCleaningRoom(card);
      alert('Maintenance ticket create ho gayi.');
    } catch (error) {alert(error.message); throw error;}
    finally {setCleaningActionSaving(false);}
  };

  const handleVerifyLegacyCleaningEntry = async (data, oldId, confirmation) => {
    if (cleaningActionSaving) throw new Error('Saving in progress hai.');
    const room = normalizeRoom(data.room);
    setCleaningActionSaving(true);
    try {
      await commitLegacyRecovery({db, api: {doc, collection, runTransaction, serverTimestamp}, hotelId,
        hotelName: hotelName || '', room, oldId, currentId: String(data.stayId || ''),
        lockId: getRoomLifecycleLockRef(room).id, actor: getCurrentActor(),
        confirmedEmpty: confirmation.confirmedEmpty, note: confirmation.note});
    } finally {setCleaningActionSaving(false);}
  };

  const handleMarkRoomVacant = async (data) => {
    if (!data?.room || cleaningActionSaving) return;
    setCleaningActionSaving(true);
    try {
      await commitRoomCleaning({...data, maintenanceRequired: false}, {ready: true});
      const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data());
      setSelectedCleaningRoom(null);
      alert(`Room ${data.room} Vacant & Ready mark ho gaya ✅`);
    } catch (error) {if (!error.roomStayConflicts) alert(error.message); throw error;}
    finally {setCleaningActionSaving(false);}
  };

  const handleSendRoomToCleaning = async (data) => {
    if (!data?.room || cleaningActionSaving) return;
    setCleaningActionSaving(true);
    try {
      const resolvedWithoutTransition = await runTransaction(db, async transaction => {
        const boardRef = getBoardDocRef();
        const board = await transaction.get(boardRef);
        const ticketRef = data.id ? doc(db, 'maintenanceTickets', data.id) : null;
        const ticket = ticketRef ? await transaction.get(ticketRef) : null;
        if (!ticket?.exists() || ticket.data().hotelId !== hotelId || normalizeRoom(ticket.data().room) !== normalizeRoom(data.room)) throw new Error('Maintenance ticket mismatch.');
        const activeCard = (board.data()?.occupiedRooms || []).find(item => roomKey(item) === normalizeRoom(data.room));
        if (!activeCard) return false;
        // Repairs can be resolved while a guest stays in the room; never change occupancy/cleaning.
        transaction.update(ticketRef, {status: 'resolved', resolutionNote: data.resolutionNote || '', resolvedAt: data.resolvedAt || new Date().toISOString(), maintenanceCompleted: true, updatedAt: serverTimestamp()});
        return true;
      });
      if (resolvedWithoutTransition) {alert('Maintenance resolved. Active guest ka room occupied hi rahega.'); return;}
      await commitRoomCleaning({...data, maintenanceRequired: false, status: 'inspection_pending', cleaningStatus: 'inspection_pending'}, {resolvingTicketId: data.id || ''});
      const fresh = await getDoc(getBoardDocRef()).catch(error => {console.warn('Committed cleaning: refresh pending', error); return null;}); if (fresh?.exists()) applyBoardData(fresh.data());
    } catch (error) {alert(error.message); throw error;}
    finally {setCleaningActionSaving(false);}
  };

  const quickCheckoutPendingRoom =
    async (roomValue) => {
      const room =
        normalizeRoom(
          roomValue
        );

      if (!room) return;

      const occupiedItem =
        occupiedRooms.find(
          (item) =>
            normalizeRoom(
              getRoomValue(item)
            ) === room
        );

      if (!occupiedItem) {
        return;
      }

      const isDetailsPending =
        typeof occupiedItem ===
          "object" &&
        (
          occupiedItem.status ===
            "details_pending" ||
          occupiedItem.detailsPending ===
            true
        );

      /*
       * Quick checkout sirf board-only occupied room ke liye.
       * Guest details save nahi hui, isliye:
       * - hotelStays me kuch save/update nahi hoga
       * - dayBookEntries me kuch save nahi hoga
       * - invoice/payment flow nahi chalega
       */
      if (!isDetailsPending) {
        return;
      }

      const confirmed =
        window.confirm(
          `Room ${room} ko bina guest details ke Check Out Rooms me move karna hai?\n\nYe sirf Room Occupation Board movement hoga. Day Book me koi entry nahi बनेगी.`
        );

      if (!confirmed) {
        return;
      }

      const checkoutAt =
        new Date().toISOString();

      const nextOccupied =
        occupiedRooms.filter(
          (item) =>
            normalizeRoom(
              getRoomValue(item)
            ) !== room
        );

      const nextCheckout =
        sortRooms([
          ...checkoutRooms.filter(
            (item) =>
              normalizeRoom(
                getRoomValue(item)
              ) !== room
          ),

          {
            ...(typeof occupiedItem ===
            "object"
              ? occupiedItem
              : {
                  room,
                }),

            room,

            status:
              "checkout_pending",

            checkoutAt,

            /*
             * Marker: ye room normal PMS checkout se nahi aaya.
             * All Okay ke baad direct Vacant hoga.
             */
            boardOnlyCheckout:
              true,

            detailsPending:
              false,

            guestName:
              "No Details",

            source:
              "quick_board_checkout",
          },
        ]);

      setOccupiedRooms(
        nextOccupied
      );

      setCheckoutRooms(
        nextCheckout
      );

      await saveBoard(
        vacantRooms,
        nextOccupied,
        nextCheckout,
        serviceRooms
      );


    };

  const moveOccupiedToCheckout = async (
    room
  ) => {
    const normalizedRoom =
      normalizeRoom(room);

    const occupiedItem = occupiedRooms.find(
      (item) =>
        normalizeRoom(getRoomValue(item)) ===
        normalizedRoom
    );

    const nextOccupied = occupiedRooms.filter(
      (item) =>
        normalizeRoom(getRoomValue(item)) !==
        normalizedRoom
    );

    const nextCheckout = sortRooms([
      ...checkoutRooms,
      typeof occupiedItem === "object"
        ? {
            ...occupiedItem,
            status: "checkout_pending",

            // Timer starts from the moment room enters Check Out Rooms.
            checkoutAt:
              new Date().toISOString(),
          }
        : {
            room: normalizedRoom,
            status: "checkout_pending",
            checkoutAt:
              new Date().toISOString(),
          },
    ]);

    setOccupiedRooms(nextOccupied);
    setCheckoutRooms(nextCheckout);

    await saveBoard(
      vacantRooms,
      nextOccupied,
      nextCheckout,
      serviceRooms
    );
  };

  const moveCheckoutToVacant = async (
    room
  ) => {
    const normalizedRoom =
      normalizeRoom(room);

    const checkoutRoomItem =
      checkoutRooms.find(
        (item) =>
          normalizeRoom(getRoomValue(item)) === normalizedRoom
      ) || null;

    const roomCategory =
      getRoomCategory(checkoutRoomItem);

    const nextCheckout =
      checkoutRooms.filter(
        (item) =>
          normalizeRoom(
            getRoomValue(item)
          ) !== normalizedRoom
      );

    const alreadyVacant =
      vacantRooms.some(
        (item) =>
          normalizeRoom(item.room) ===
          normalizedRoom
      );

    const nextVacant = alreadyVacant
      ? vacantRooms
      : sortRooms([
          ...vacantRooms,
          {
            room: normalizedRoom,
            isSir: false,
            category: roomCategory,
            roomCategory,
          },
        ]);

    setCheckoutRooms(nextCheckout);
    setVacantRooms(nextVacant);

    const boardSaved = await saveBoard(
      nextVacant,
      occupiedRooms,
      nextCheckout,
      serviceRooms
    );

    if (!boardSaved) {
      return;
    }


  };

  const addServiceRoom = async (directRoom = "") => {
    const room = String(
      directRoom || serviceRoomInput || ""
    ).trim();

    if (!room) {
      return alert(
        "Service room enter karo."
      );
    }

    const existingIndex =
      serviceRooms.findIndex(
        (item) =>
          normalizeRoom(item.room) ===
          normalizeRoom(room)
      );

    const nextService = [
      ...serviceRooms,
    ];

    if (existingIndex >= 0) {
      nextService[existingIndex] = {
        ...nextService[existingIndex],
        room,
        status: "not_clear",
      };
    } else {
      nextService.push({
        room,
        status: "not_clear",
      });
    }

    const previousService = serviceRooms;
    setServiceRooms(nextService);
    setServiceRoomInput("");

    const boardSaved = await saveBoard(
      vacantRooms,
      occupiedRooms,
      checkoutRooms,
      nextService
    );

    if (!boardSaved) {

    }
  };

  const toggleServiceRoom = async (room) => {
    const nextService =
      serviceRooms.map((item) =>
        normalizeRoom(item.room) ===
        normalizeRoom(room)
          ? {
              ...item,
              status:
                item.status === "cleared"
                  ? "not_clear"
                  : "cleared",
            }
          : item
      );

    const previousService = serviceRooms;
    setServiceRooms(nextService);

    const boardSaved = await saveBoard(
      vacantRooms,
      occupiedRooms,
      checkoutRooms,
      nextService
    );

    if (!boardSaved) {

    }
  };

  const deleteServiceRoom = async (room) => {
    const confirmed = window.confirm(
      `${room} ko Service Rooms se delete karna hai?`
    );

    if (!confirmed) return;

    const nextService =
      serviceRooms.filter(
        (item) =>
          normalizeRoom(item.room) !==
          normalizeRoom(room)
      );

    const previousService = serviceRooms;
    setServiceRooms(nextService);

    const boardSaved = await saveBoard(
      vacantRooms,
      occupiedRooms,
      checkoutRooms,
      nextService
    );

    if (!boardSaved) {

    }
  };

  const roomPillStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 12,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    minWidth: 175,
    boxSizing: "border-box",
  };

  const renderVacantRooms = () => {
    const floors = Object.keys(
      groupedVacant
    );

    if (floors.length === 0) {
      return <p>No vacant rooms.</p>;
    }

    return floors.map((floor) => (
      <div
        key={floor}
        style={{
          marginBottom: 18,
        }}
      >
        <div
          style={{
            marginBottom: 10,
            color: "#374151",
            fontWeight: 800,
          }}
        >
          {floor === "OTHER"
            ? "Other Rooms"
            : `${floor} Floor`}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          {groupedVacant[floor].map(
            (item) => (
              <div
                key={item.room}
                style={{
                  ...roomPillStyle,
                  background: item.isSir
                    ? "#fff7d6"
                    : "#ffffff",
                  border: item.isSir
                    ? "1px solid #f59e0b"
                    : "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <strong>
                    {item.room}
                  </strong>

                  {item.isSir && (
                    <span
                      style={{
                        padding:
                          "3px 8px",
                        borderRadius: 999,
                        background:
                          "#fcd34d",
                        color: "#92400e",
                        fontSize: 11,
                        fontWeight: 900,
                      }}
                    >
                      Sir
                    </span>
                  )}
                </div>

                <button
                  onClick={() =>
                    quickOccupyRoom(
                      item.room
                    )
                  }
                  style={actionButton(
                    "#16a34a"
                  )}
                >
                  Check In
                </button>
              </div>
            )
          )}
        </div>
      </div>
    ));
  };

  const renderOccupiedRooms = () => {
    const floors = Object.keys(groupedOccupied);

    if (floors.length === 0) {
      return <p>No occupied rooms.</p>;
    }

    return floors.map((floor) => (
      <div key={floor} style={{ marginBottom: 18 }}>
        <div
          style={{
            marginBottom: 10,
            color: "#374151",
            fontWeight: 800,
          }}
        >
          {floor === "OTHER"
            ? "Other Rooms"
            : `${floor} Floor`}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          {groupedOccupied[floor].map((item) => {
            const room = getRoomValue(item);
            const guestName =
              typeof item === "object"
                ? item.guestName
                : "";

            return (
              <div
                key={room}
                style={{
                  ...roomPillStyle,
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <strong>{room}</strong>
                  {typeof item ===
                    "object" &&
                  (
                    item.status ===
                      "details_pending" ||
                    item.detailsPending ===
                      true
                  ) ? (
                    <>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: "#b45309",
                          fontWeight: 900,
                        }}
                      >
                        ⏳ Details Pending
                      </div>

                      <PendingDetailsTimer
                        startedAt={
                          item.occupiedAt ||
                          item.checkInAt
                        }
                      />
                    </>
                  ) : (
                    <>
                      {guestName && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: "#475569",
                            fontWeight: 800,
                          }}
                        >
                          {guestName}
                        </div>
                      )}

                      {typeof item ===
                        "object" && (
                        <div
                          style={{
                            marginTop: 3,
                            fontSize: 11,
                            color: roomOpsPalette.dark ? "#9fb1c5" : "#64748b",
                          }}
                        >
                          Balance: ₹
                          {getOccupiedDisplayBalance(
                            item
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {typeof item ===
                  "object" &&
                (
                  item.status ===
                    "details_pending" ||
                  item.detailsPending ===
                    true
                ) ? (
                  <div
                    style={{
                      display:
                        "flex",
                      gap: 6,
                      flexWrap:
                        "wrap",
                      justifyContent:
                        "flex-end",
                    }}
                  >
                    <button
                      onClick={() =>
                        openGuestCheckIn(
                          room
                        )
                      }
                      style={actionButton(
                        "#f59e0b"
                      )}
                    >
                      Add Details
                    </button>

                    <button
                      onClick={() =>
                        quickCheckoutPendingRoom(
                          room
                        )
                      }
                      style={actionButton(
                        "#ef4444"
                      )}
                    >
                      Check Out
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() =>
                      openOccupiedRoomManager(
                        typeof item ===
                          "object"
                          ? item
                          : {
                              room,
                            }
                      )
                    }
                    style={actionButton(
                      "#2563eb"
                    )}
                  >
                    Manage
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    ));
  };

  const renderNormalRooms = (
    groupedRooms,
    emptyText,
    buttonText,
    buttonColor,
    action
  ) => {
    const floors =
      Object.keys(groupedRooms);

    if (floors.length === 0) {
      return <p>{emptyText}</p>;
    }

    return floors.map((floor) => (
      <div
        key={floor}
        style={{
          marginBottom: 18,
        }}
      >
        <div
          style={{
            marginBottom: 10,
            color: "#374151",
            fontWeight: 800,
          }}
        >
          {floor === "OTHER"
            ? "Other Rooms"
            : `${floor} Floor`}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          {groupedRooms[floor].map(
            (item) => {
              const room =
                getRoomValue(item);

              const checkoutAt =
                typeof item === "object"
                  ? item?.checkoutAt ||
                    item?.checkedOutAt ||
                    item?.checkoutCompletedAt ||
                    ""
                  : "";

              return (
                <div
                  key={room}
                  style={{
                    ...roomPillStyle,
                    alignItems:
                      "center",
                  }}
                >
                  <div>
                    <strong>{room}</strong>

                    {checkoutAt ? (
                      <CheckoutElapsedTimer
                        startedAt={
                          checkoutAt
                        }
                      />
                    ) : (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: "#94a3b8",
                          fontWeight: 700,
                        }}
                      >
                        Timer unavailable
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() =>
                      action(room)
                    }
                    style={actionButton(
                      buttonColor
                    )}
                  >
                    {buttonText}
                  </button>
                </div>
              );
            }
          )}
        </div>
      </div>
    ));
  };

  const dashboardNavItems = [
    ["room_board", "▦", "Room Board"],
    ["maintenance", "⚒", "Maintenance"],
    ["guest_crm", "♙", "Guest CRM"],
    ["pending_payments", "₹", "Pending Payments"],
    ["bookings", "▣", "Bookings"],
    ["booking_diary", "◫", "Booking Diary"],
    ["day_book", "▤", "Day Book"],
    ["expenses", "₹", "Expenses"],
    ["reports", "◩", "Reports"],
    ["audit_logs", "☷", "Audit Logs"],
  ];

  const getFloorLabel = (floor) => {
    if (floor === "OTHER") return "Other / Service";
    if (floor === "GROUND") return "Ground Floor";
    if (/^\d+$/.test(String(floor))) return `Floor ${floor}`;
    return `${floor} Floor`;
  };

  const pmsRoomMap = useMemo(() => {
    const rawBoard = latestBoardRef.current.hotelId === hotelId ? latestBoardRef.current.board : {vacantRooms, occupiedRooms, checkoutRooms};
    const records = latestStaysRef.current.hotelId === hotelId ? latestStaysRef.current.records : new Map();
    const rows = buildRoomDisplayRows(rawBoard, records);

    const grouped = {};
    sortRooms(rows).forEach((item) => {
      const floor = getRoomFloor(item.room);
      if (!grouped[floor]) grouped[floor] = [];
      grouped[floor].push(item);
    });

    return grouped;
  }, [hotelId, vacantRooms, occupiedRooms, checkoutRooms]);

  const pmsStatusMeta = (status, item = {}) => {
    const darkRoomCard =
      isRoomOperations &&
      roomOpsPalette.dark;

    if (status === "sync_pending") {
      return {label: 'Status verify', background: darkRoomCard ? '#252b36' : '#f1f5f9',
        border: '#94a3b8', accent: darkRoomCard ? '#cbd5e1' : '#475569', dot: '#64748b'};
    }

    if (status === "vacant") {
      return {
        label: item.isSir ? "SIR" : "Ready",
        background:
          darkRoomCard
            ? item.isSir
              ? "#3a2c10"
              : "#0d2f23"
            : item.isSir
            ? "#fff7cc"
            : "#ecfdf5",
        border:
          item.isSir
            ? darkRoomCard
              ? "#b7791f"
              : "#f59e0b"
            : darkRoomCard
            ? "#2f855a"
            : "#86efac",
        accent:
          item.isSir
            ? darkRoomCard
              ? "#f6c453"
              : "#b45309"
            : darkRoomCard
            ? "#6ee7b7"
            : "#15803d",
        dot: "#22c55e",
      };
    }

    if (status === "details_pending") {
      return {
        label: "Details Pending",
        background:
          darkRoomCard
            ? "#3a2511"
            : "#fff7ed",
        border:
          darkRoomCard
            ? "#b86a24"
            : "#fdba74",
        accent:
          darkRoomCard
            ? "#fdba74"
            : "#c2410c",
        dot: "#f59e0b",
      };
    }

    if (status === "occupied") {
      return {
        label: "Occupied",
        background:
          darkRoomCard
            ? "#132744"
            : "#eff6ff",
        border:
          darkRoomCard
            ? "#315f9f"
            : "#93c5fd",
        accent:
          darkRoomCard
            ? "#93c5fd"
            : "#1d4ed8",
        dot: "#2563eb",
      };
    }

    if (status === "checkout") {
      return {
        label: "Dirty / Checkout",
        background:
          darkRoomCard
            ? "#351722"
            : "#fff1f2",
        border:
          darkRoomCard
            ? "#9f4560"
            : "#fda4af",
        accent:
          darkRoomCard
            ? "#fda4af"
            : "#be123c",
        dot: "#e11d48",
      };
    }

    return {
      label:
        item.status === "cleared"
          ? "Service Clear"
          : "Blocked / Service",
      background:
        darkRoomCard
          ? "#241b3e"
          : "#f5f3ff",
      border:
        darkRoomCard
          ? "#6d55a6"
          : "#c4b5fd",
      accent:
        darkRoomCard
          ? "#c4b5fd"
          : "#6d28d9",
      dot: "#8b5cf6",
    };
  };

  const refreshRoomStatus = async () => {
    try {
      const [boardSnapshot, staySnapshot] = await Promise.all([
        getDoc(getBoardDocRef()), getDocs(query(collection(db, 'hotelStays'), where('hotelId', '==', hotelId)))
      ]);
      latestStaysRef.current = {hotelId, records: new Map(staySnapshot.docs.map(record => [record.id, {...record.data(), id: record.id, stayId: record.id}]))};
      if (boardSnapshot.exists()) applyBoardData(boardSnapshot.data());
    } catch (error) {alert('Room status refresh nahi hua: ' + error.message);}
  };

  const renderPmsRoomCard = (item) => {
    const room = normalizeRoom(item.room);
    const roomCategory = getRoomCategory(item);
    const meta = pmsStatusMeta(item.pmsStatus, item);
    const checkoutAt = item?.checkoutAt || item?.checkedOutAt || item?.checkoutCompletedAt || "";

    return (
      <div
        key={`${item.pmsStatus}-${room}`}
        className="pms-room-card"
        style={{
          minHeight: 132,
          padding: 13,
          borderRadius: 14,
          border: `1px solid ${meta.border}`,
          background: meta.background,
          boxShadow: "0 5px 14px rgba(15,23,42,0.06)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 10,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
            <div className="pms-room-number" style={{ fontSize: 22, lineHeight: 1, fontWeight: 950, color: roomOpsPalette.dark ? "#f8fafc" : "#0f172a" }}>{room}</div>
            <span style={{ width: 9, height: 9, marginTop: 3, borderRadius: 999, background: meta.dot, boxShadow: `0 0 0 3px ${meta.background}` }} />
          </div>
          {item.pmsStatus === "vacant" ? (
            <div
              style={{
                minHeight: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <div
                className="pms-ready-center"
                style={{
                  fontSize:
                    item.isSir
                      ? 21
                      : 27,
                  lineHeight: 1,
                  fontWeight: 950,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: meta.accent,
                }}
              >
                {item.isSir ? "SIR" : "READY"}
              </div>
            </div>
          ) : (
            <div
              style={{
                marginTop: 8,
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                color: meta.accent,
              }}
            >
              {meta.label}
            </div>
          )}

          {roomCategory && (
            <div
              style={{
                marginTop: item.pmsStatus === "vacant" ? -5 : 6,
                marginLeft: item.pmsStatus === "vacant" ? "auto" : 0,
                marginRight: item.pmsStatus === "vacant" ? "auto" : 0,
                width: "fit-content",
                maxWidth: "100%",
                padding: "3px 7px",
                borderRadius: 999,
                background: roomOpsPalette.dark ? "rgba(7,17,31,.72)" : "rgba(255,255,255,.72)",
                border: `1px solid ${meta.border}`,
                color: meta.accent,
                fontSize: 9,
                fontWeight: 950,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={roomCategory}
            >
              {roomCategory}
            </div>
          )}

          {item.pmsStatus === "occupied" && item.guestName && (
            <div style={{ marginTop: 7, fontSize: 12, fontWeight: 850, color: roomOpsPalette.dark ? "#e5edf7" : "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.guestName}</div>
          )}
          {item.pmsStatus === "occupied" && (
            <div style={{ marginTop: 3, fontSize: 11, color: roomOpsPalette.dark ? "#a8b7c9" : "#64748b" }}>Balance ₹{getOccupiedDisplayBalance(item).toLocaleString("en-IN")}</div>
          )}
          {item.pmsStatus === "details_pending" &&
            item.temporaryGuestName && (
              <div
                style={{
                  marginTop: 7,
                  fontSize: 13,
                  fontWeight: 950,
                  color: roomOpsPalette.dark ? "#fed7aa" : "#7c2d12",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={item.temporaryGuestName}
              >
                👤 {item.temporaryGuestName}
              </div>
            )}

          {item.pmsStatus === "details_pending" && <PendingDetailsTimer startedAt={item.occupiedAt || item.checkInAt} />}
          {item.pmsStatus === "checkout" && checkoutAt && <CheckoutElapsedTimer startedAt={checkoutAt} />}
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {item.pmsStatus === "sync_pending" && <>
            <div style={{fontSize: 12, color: meta.accent, width: '100%'}}>{item.syncReason}</div>
            <button onClick={refreshRoomStatus} style={pmsTileButton('#475569')}>Refresh status</button>
          </>}
          {item.pmsStatus === "vacant" && (
            <button onClick={() => handleReadyRoomCheckIn(item)} style={pmsTileButton("#15803d")}>Check In</button>
          )}
          {item.pmsStatus === "details_pending" && (
            <>
              <button
                onClick={() =>
                  openGuestCheckIn(room, {
                    name: item.temporaryGuestName || item.guestName || "",
                  })
                }
                style={pmsTileButton("#d97706")}
              >
                Add Details
              </button>
              <button onClick={() => quickCheckoutPendingRoom(room)} style={pmsTileButton("#e11d48")}>Out</button>
            </>
          )}
          {item.pmsStatus === "occupied" && (
            <button onClick={() => openOccupiedRoomManager(item)} style={pmsTileButton("#2563eb")}>Manage</button>
          )}
          {item.pmsStatus === "checkout" && (
            <button onClick={() => handleOpenHousekeepingRoom(item)} style={pmsTileButton("#be123c")}>Mark Ready</button>
          )}
        </div>
      </div>
    );
  };

  const renderPmsFloorBoard = () => {
    const floors = Object.keys(pmsRoomMap).sort((a, b) => {
      if (/^\d+$/.test(a) && /^\d+$/.test(b)) return Number(a) - Number(b);
      return a.localeCompare(b, undefined, { numeric: true });
    });

    if (!floors.length) {
      return <div style={{ padding: 30, textAlign: "center", color: roomOpsPalette.muted }}>No rooms added yet.</div>;
    }

    return floors.map((floor) => (
      <section
        key={floor}
        className="pms-floor-section"
        style={{
          marginTop: 18,
          border: `1px solid ${roomOpsPalette.border}`,
          borderRadius: 16,
          overflow: "hidden",
          background: roomOpsPalette.surface,
          backdropFilter: "blur(14px)",
        }}
      >
        <div
          style={{
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            background: roomOpsPalette.surfaceSoft,
            borderBottom: `1px solid ${roomOpsPalette.border}`,
          }}
        >
          <div
            style={{
              fontWeight: 950,
              color: roomOpsPalette.title,
            }}
          >
            {getFloorLabel(floor)}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: roomOpsPalette.muted,
            }}
          >
            {pmsRoomMap[floor].length} Rooms
          </div>
        </div>
        <div className="pms-floor-grid" style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
          {pmsRoomMap[floor].map(renderPmsRoomCard)}
        </div>
      </section>
    ));
  };

  if (!authReady) {
    return (
      <div style={{ padding: 20 }}>
        Verifying manager session...
      </div>
    );
  }

  if (!hotelId || !managerLoggedIn || !authAuthorized) {
    return (
      <div style={{ padding: 20 }}>
        Redirecting...
      </div>
    );
  }

  return (
    <div
      ref={dashboardRootRef}
      className="pms-dashboard-root"
      data-room-ops-active={isRoomOperations ? "true" : "false"}
      data-pms-time-phase={timePhase}
      style={{
        minHeight: "100vh",
        background: isRoomOperations
          ? roomOpsPalette.root
          : "#f8f5ee",
        color: isRoomOperations
          ? roomOpsPalette.title
          : "#172033",
        "--pms-page-bg":
          isRoomOperations
            ? roomOpsPalette.page
            : "linear-gradient(180deg,#f8f5ee 0%,#f3f6fa 58%,#eef2f7 100%)",
        "--pms-glow-a":
          isRoomOperations
            ? roomOpsPalette.glowA
            : "rgba(251,191,36,.18)",
        "--pms-glow-b":
          isRoomOperations
            ? roomOpsPalette.glowB
            : "rgba(56,189,248,.12)",
      }}
    >
      <style>{`
        .pms-dashboard-root, .pms-dashboard-root * { box-sizing: border-box; }
        .pms-dashboard-shell { display: flex; flex-direction: column; min-height: 100vh; width: 100%; overflow-x: hidden; }
        .pms-dashboard-sidebar {
          scrollbar-width: thin; position: sticky !important; left: auto !important; right: auto !important; bottom: auto !important; top: 0 !important;
          width: 100% !important; height: 62px !important; min-height: 62px !important; max-height: 62px !important; z-index: 500 !important;
          display: flex !important; flex-direction: row !important; align-items: center; justify-content: space-between; gap: 12px;
          padding: 7px 12px !important; overflow: visible !important; background: linear-gradient(90deg,#0d2238 0%,#143957 58%,#0d2238 100%) !important; border-bottom: 1px solid rgba(200,160,74,.38); box-shadow: 0 8px 28px rgba(15,23,42,.18);
        }
        .pms-sidebar-brand { min-width: 0; padding: 0 !important; border: 0 !important; }
        .pms-compact-menu { position: relative; flex: 0 0 auto; }
        .pms-menu-trigger { width: 42px; height: 38px; border-radius: 11px; border: 1px solid #d9e2ec; background: #ffffff; color: #102a43; font-size: 24px; line-height: 1; font-weight: 950; cursor: pointer; display: grid; place-items: center; box-shadow: 0 4px 12px rgba(15,23,42,.06); }
        .pms-menu-trigger:hover, .pms-menu-trigger:focus-visible { background: #102a43; color: #ffffff; border-color: #102a43; outline: none; }
        .pms-compact-menu-panel { position: absolute; top: calc(100% + 8px); right: 0; width: min(330px, calc(100vw - 24px)); max-height: min(76vh, 640px); overflow-y: auto; padding: 10px; border-radius: 15px; background: #0d2238; border: 1px solid rgba(230,200,122,.38); box-shadow: 0 20px 55px rgba(7,18,31,.34); z-index: 1000; }
        .pms-sidebar-nav { display: grid !important; gap: 6px !important; padding: 0 !important; margin: 0 !important; overflow: visible !important; }
        .pms-sidebar-nav > button { width: 100% !important; min-height: 38px !important; padding: 8px 10px !important; border-radius: 9px !important; }
        .pms-sidebar-utilities { display: grid !important; grid-template-columns: 1fr !important; gap: 7px !important; margin-top: 9px !important; padding-top: 9px !important; border-top: 1px solid rgba(255,255,255,.12) !important; }
        .pms-sidebar-utilities > button { width: 100% !important; min-height: 38px !important; padding: 8px 10px !important; border-radius: 9px !important; }
        .pms-dashboard-main {
          min-width: 0;
          width: 100%;
          margin-left: 0 !important;
          position: relative;
          isolation: isolate;
          overflow: hidden;
          background: var(--pms-page-bg) !important;
          transition: background 900ms ease, color 600ms ease;
        }
        .pms-dashboard-content { position: relative; z-index: 2; }
        .pms-dashboard-main::before,
        .pms-dashboard-main::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0;
          transition: opacity 700ms ease;
        }

        /* Continuous time ambience — no weather API. */
        .pms-dashboard-root[data-room-ops-active="true"] .pms-dashboard-main::before {
          opacity: .76;
          background:
            radial-gradient(circle at 86% 5%, var(--pms-glow-a), transparent 27%),
            radial-gradient(circle at 14% 12%, var(--pms-glow-b), transparent 31%);
          animation: pmsAmbientDrift 18s ease-in-out infinite alternate;
        }

        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="late_night"] .pms-dashboard-main::after,
        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="night"] .pms-dashboard-main::after,
        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="pre_dawn"] .pms-dashboard-main::after {
          opacity: .46;
          background-image:
            radial-gradient(circle, rgba(255,255,255,.70) 0 1px, transparent 1.4px),
            radial-gradient(circle, rgba(147,197,253,.45) 0 1px, transparent 1.4px);
          background-size: 58px 58px, 93px 93px;
          background-position: 12px 7px, 33px 21px;
          animation: pmsStarDrift 28s linear infinite alternate;
        }

        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="dawn"] .pms-dashboard-main::after,
        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="sunrise"] .pms-dashboard-main::after,
        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="golden_hour"] .pms-dashboard-main::after,
        .pms-dashboard-root[data-room-ops-active="true"][data-pms-time-phase="sunset"] .pms-dashboard-main::after {
          opacity: .34;
          background:
            linear-gradient(115deg, transparent 0 34%, rgba(255,255,255,.20) 45%, transparent 58%),
            radial-gradient(circle at 50% 0%, rgba(255,255,255,.18), transparent 38%);
          animation: pmsSkyShimmer 13s ease-in-out infinite alternate;
        }

        @keyframes pmsAmbientDrift {
          from { transform: translate3d(-1.5%,0,0) scale(1); }
          to { transform: translate3d(1.5%,1%,0) scale(1.025); }
        }

        @keyframes pmsStarDrift {
          from { transform: translate3d(0,0,0); }
          to { transform: translate3d(14px,8px,0); }
        }

        @keyframes pmsSkyShimmer {
          from { transform: translate3d(-2%,0,0); opacity: .24; }
          to { transform: translate3d(2%,0,0); opacity: .40; }
        }

        @media (prefers-reduced-motion: reduce) {
          .pms-dashboard-main::before,
          .pms-dashboard-main::after,
          .pms-stat-card,
          .pms-room-card {
            animation: none !important;
            transition: none !important;
          }
        }
        .pms-stats-grid { width: 100%; }
        .pms-controls-row { width: 100%; }
        .pms-floor-grid { width: 100%; }
        .pms-stat-card { box-shadow: 0 8px 24px rgba(15,23,42,.06); transition: transform .18s ease, box-shadow .18s ease; }
        .pms-stat-card:hover { transform: translateY(-1px); box-shadow: 0 11px 28px rgba(15,23,42,.09); }
        .pms-guest-search-card { box-shadow: 0 9px 28px rgba(15,23,42,.055) !important; }
        .pms-floor-section { box-shadow: 0 9px 28px rgba(15,23,42,.055); }
        .pms-room-card { box-shadow: 0 6px 18px rgba(15,23,42,.05); transition: transform .18s ease, box-shadow .18s ease; }
        .pms-room-card:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(15,23,42,.08); }

        @media (max-width: 900px) {
          .pms-compact-menu-panel { position: fixed; top: 58px; right: 10px; left: 10px; width: auto; max-height: calc(100vh - 74px); }
          .pms-sidebar-nav { width: 100% !important; }
          .pms-sidebar-nav > button { min-width: 0 !important; }
          .pms-sidebar-utilities { width: 100% !important; }
          .pms-dashboard-main { padding: 14px 12px 24px !important; }
          .pms-dashboard-header { padding-bottom: 10px !important; align-items: flex-start !important; }
          .pms-stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 8px !important; }
          .pms-controls-row { display: block !important; }
          .pms-floor-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 8px !important; padding: 9px !important; }
          .pms-search-actions { grid-template-columns: 1fr !important; }
        }

        @media (max-width: 560px) {
          .pms-dashboard-main { padding: 12px 10px 22px !important; }
          .pms-dashboard-header h1 { font-size: 22px !important; }
          .pms-dashboard-header { gap: 8px !important; }
          .pms-stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .pms-stat-card { min-height: 68px !important; padding: 10px !important; }
          .pms-controls-row { grid-template-columns: 1fr !important; }
          .pms-live-board-card { grid-column: auto; }
          .pms-dropdown-body {
            position: fixed !important;
            left: 10px !important;
            right: 10px !important;
            top: 72px !important;
            width: auto !important;
            max-width: none !important;
            max-height: calc(100vh - 92px) !important;
            overflow-y: auto !important;
            z-index: 9999 !important;
          }
          .pms-dropdown.service .pms-dropdown-body { left: 10px !important; right: 10px !important; }
          .pms-floor-section { margin-top: 12px !important; border-radius: 14px !important; }
          .pms-floor-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 7px !important; padding: 8px !important; }
          .pms-room-card { min-height: 188px !important; padding: 11px !important; border-radius: 13px !important; }
          .pms-room-number { font-size: 20px !important; }
          .pms-ready-center { font-size: 25px !important; }
        }

        @media (max-width: 380px) {
          .pms-floor-grid { grid-template-columns: 1fr !important; }
          .pms-stats-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
        }
      `}</style>
      <div className="pms-dashboard-shell" style={{ display: "flex", minHeight: "100vh" }}>


        {servicePanelOpen && (
          <div
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setServicePanelOpen(false);
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              background: "rgba(7,18,31,.58)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 16,
            }}
          >
            <div
              style={{
                width: "min(620px, 100%)",
                maxHeight: "88vh",
                overflowY: "auto",
                background: "linear-gradient(180deg,#fffdf8 0%,#ffffff 100%)",
                borderRadius: 20,
                boxShadow: "0 28px 90px rgba(7,18,31,.34)",
                border: "1px solid #d9c38a",
                padding: 18,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#102a43" }}>Service Rooms</div>
                  <div style={{ marginTop: 3, fontSize: 11, color: "#6b7280" }}>Add, Clear / Undo aur Delete — same purana logic.</div>
                </div>
                <button type="button" onClick={() => setServicePanelOpen(false)} style={{ width: 38, height: 38, border: "1px solid #e5e7eb", borderRadius: 11, background: "#ffffff", fontSize: 21, cursor: "pointer" }}>×</button>
              </div>

              <div style={{ padding: 14, borderRadius: 15, background: "#f8fafc", border: "1px solid #e5e7eb", marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#334155", marginBottom: 7 }}>Add Service Room / Area</div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8 }}>
                  <input
                    value={serviceRoomInput}
                    onChange={(event) => setServiceRoomInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addServiceRoom();
                      }
                    }}
                    placeholder="Store / Linen / 108"
                    style={{ ...inputStyle, minWidth: 0 }}
                  />
                  <button type="button" onClick={() => addServiceRoom()} style={{ border: 0, borderRadius: 11, padding: "0 18px", minHeight: 42, background: "linear-gradient(135deg,#173f5f,#102a43)", color: "#fff", fontWeight: 900, cursor: "pointer" }}>
                    Add Service
                  </button>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                {serviceRooms.length === 0 ? (
                  <div style={{ padding: 18, textAlign: "center", color: "#64748b", border: "1px dashed #d6d3d1", borderRadius: 14 }}>
                    Abhi koi service room nahi hai.
                  </div>
                ) : (
                  serviceRooms.map((item) => {
                    const cleared = item.status === "cleared";
                    return (
                      <div key={item.room} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 14, border: cleared ? "1px solid #86efac" : "1px solid #ddd6fe", background: cleared ? "#f0fdf4" : "#faf5ff" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 950, color: "#172033", overflow: "hidden", textOverflow: "ellipsis" }}>{item.room}</div>
                          <div style={{ marginTop: 2, fontSize: 10, fontWeight: 850, color: cleared ? "#15803d" : "#7c3aed" }}>{cleared ? "Clear ✓" : "Not Clear"}</div>
                        </div>
                        <button type="button" onClick={() => toggleServiceRoom(item.room)} style={{ border: 0, borderRadius: 9, padding: "8px 11px", background: cleared ? "#d97706" : "#16a34a", color: "#fff", fontWeight: 900, fontSize: 11, cursor: "pointer" }}>
                          {cleared ? "Undo" : "Clear"}
                        </button>
                        <button type="button" onClick={() => deleteServiceRoom(item.room)} style={{ border: 0, borderRadius: 9, padding: "8px 11px", background: "#dc2626", color: "#fff", fontWeight: 900, fontSize: 11, cursor: "pointer" }}>
                          Delete
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {roomSetupOpen && (
          <div
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setRoomSetupOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(15,23,42,.48)",
              display: "grid",
              placeItems: "center",
              padding: 16,
            }}
          >
            <div
              style={{
                width: "min(920px, 100%)",
                maxHeight: "88vh",
                overflowY: "auto",
                background: "#ffffff",
                borderRadius: 18,
                boxShadow: "0 24px 80px rgba(15,23,42,.30)",
                border: "1px solid #e2e8f0",
                padding: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#0f172a" }}>⚙ Room Setup</div>
                  <div style={{ marginTop: 3, fontSize: 11, color: "#64748b" }}>
                    Rooms add karo aur har room ki category set karo — Deluxe, Executive, Premium, Suite, Classic ya hotel ka koi bhi custom naam.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setRoomSetupOpen(false)}
                  style={{ width: 38, height: 38, border: 0, borderRadius: 10, background: "#f1f5f9", fontSize: 22, cursor: "pointer" }}
                >
                  ×
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
                <InputCard title="Add Single Room">
                  <input value={singleRoom} onChange={(e) => setSingleRoom(e.target.value)} placeholder="Example: 101 / A1" style={inputStyle} />
                  <input
                    value={singleRoomCategory}
                    onChange={(e) => setSingleRoomCategory(e.target.value)}
                    placeholder="Category: Deluxe / Premium / Classic..."
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                  <button onClick={addSingleVacantRoom} style={{ ...fullButton, background: "#2563eb" }}>Add Room</button>
                </InputCard>

                <InputCard title="Add Floor Rooms">
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={floorLetter} onChange={(e) => setFloorLetter(e.target.value)} placeholder="A / B / C" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                    <input value={floorRoomCount} onChange={(e) => setFloorRoomCount(e.target.value)} type="number" min="1" placeholder="Count" style={{ ...inputStyle, width: 90 }} />
                  </div>
                  <input
                    value={floorRoomCategory}
                    onChange={(e) => setFloorRoomCategory(e.target.value)}
                    placeholder="Same category for generated rooms"
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                  <button onClick={addFloorRooms} style={{ ...fullButton, background: "#0f766e" }}>Generate</button>
                </InputCard>
              </div>

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a" }}>Room Category Mapping</div>
                <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>
                  Existing rooms ki category yahan kabhi bhi change kar sakte ho. Blank save karoge to category remove ho jayegi.
                </div>

                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {getRoomSetupRows().map((item) => {
                    const setupRoom = normalizeRoom(getRoomValue(item));
                    const savedCategory = getRoomCategory(item);
                    const draftCategory = Object.prototype.hasOwnProperty.call(roomCategoryDrafts, setupRoom)
                      ? roomCategoryDrafts[setupRoom]
                      : savedCategory;

                    return (
                      <div
                        key={`room-category-${setupRoom}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "70px minmax(70px, 110px) minmax(0, 1fr) auto auto",
                          gap: 8,
                          alignItems: "center",
                          padding: 9,
                          borderRadius: 11,
                          border: "1px solid #e2e8f0",
                          background: "#f8fafc",
                        }}
                      >
                        <strong style={{ color: "#0f172a" }}>{setupRoom}</strong>
                        <span style={{ fontSize: 10, fontWeight: 850, color: "#64748b" }}>{item.setupStatus}</span>
                        <input
                          value={draftCategory}
                          onChange={(event) =>
                            setRoomCategoryDrafts((current) => ({
                              ...current,
                              [setupRoom]: event.target.value,
                            }))
                          }
                          placeholder="Deluxe / Executive / Premium / Suite / Classic"
                          style={{ ...inputStyle, width: "100%" }}
                        />
                        <button
                          type="button"
                          onClick={() => saveRoomCategory(setupRoom, draftCategory)}
                          style={{
                            border: 0,
                            borderRadius: 9,
                            padding: "9px 12px",
                            background: "#0f766e",
                            color: "#fff",
                            fontWeight: 900,
                            cursor: "pointer",
                          }}
                        >
                          Save
                        </button>
                        <button type="button" disabled={roomActionSaving || item.setupStatus !== 'Ready'}
                          onClick={() => handleRemoveReadyRoom(setupRoom)}
                          style={{border: 0, borderRadius: 9, padding: '9px 12px', background: item.setupStatus === 'Ready' ? '#dc2626' : '#e2e8f0',
                            color: item.setupStatus === 'Ready' ? '#fff' : '#64748b', fontWeight: 800, cursor: item.setupStatus === 'Ready' ? 'pointer' : 'not-allowed'}}>
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="pms-dashboard-main" style={{ flex: 1, minWidth: 0, padding: "18px 20px 28px" }}>
          <div className="pms-dashboard-content" style={{ maxWidth: 1480, margin: "0 auto" }}>
            <header className="pms-dashboard-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "4px 2px 14px" }}>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 850,
                    color:
                      isRoomOperations
                        ? roomOpsPalette.muted
                        : "#64748b",
                  }}
                >
                  MANAGER DASHBOARD
                </div>
                <h1
                  style={{
                    margin: "3px 0 0",
                    fontSize: 24,
                    lineHeight: 1.2,
                    color:
                      isRoomOperations
                        ? roomOpsPalette.title
                        : "#0f172a",
                    textShadow:
                      isRoomOperations &&
                      roomOpsPalette.dark
                        ? "0 2px 14px rgba(0,0,0,.24)"
                        : "none",
                    transition:
                      "color 500ms ease",
                  }}
                >
                  {activeDashboardTab === "room_board"
                    ? "Room Operations"
                    : dashboardNavItems.find(([id]) => id === activeDashboardTab)?.[2] || "Dashboard"}
                </h1>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <div
                  style={{
                    padding: "8px 11px",
                    borderRadius: 10,
                    background:
                      isRoomOperations
                        ? roomOpsPalette.dateBg
                        : "#ffffff",
                    border: `1px solid ${
                      isRoomOperations
                        ? roomOpsPalette.border
                        : "#e2e8f0"
                    }`,
                    fontSize: 12,
                    color:
                      isRoomOperations
                        ? roomOpsPalette.dateText
                        : "#475569",
                    fontWeight: 800,
                    backdropFilter:
                      isRoomOperations
                        ? "blur(12px)"
                        : "none",
                  }}
                >
                  {ambientNow.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </div>

                <div
                  className="pms-compact-menu"
                  onMouseEnter={() => setNavMenuOpen(true)}
                  onMouseLeave={() => setNavMenuOpen(false)}
                >
                  <button
                    type="button"
                    className="pms-menu-trigger"
                    aria-label="Open dashboard menu"
                    aria-expanded={navMenuOpen}
                    title="Dashboard Menu"
                    onClick={() => setNavMenuOpen((current) => !current)}
                  >
                    ⋮
                  </button>

                  {navMenuOpen && (
                    <div className="pms-compact-menu-panel">
                      <nav className="pms-sidebar-nav">
                        {dashboardNavItems.map(([id, icon, label]) => {
                          const active = activeDashboardTab === id;
                          const badge = id === "room_board" ? totalBoardRooms : 0;

                          return (
                            <button
                              key={id}
                              onClick={() => {
                                setActiveDashboardTab(id);
                                setNavMenuOpen(false);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 9,
                                border: active ? "1px solid rgba(255,255,255,.24)" : "1px solid transparent",
                                background: active ? "#f59e0b" : "transparent",
                                color: "#ffffff",
                                cursor: "pointer",
                                fontWeight: active ? 900 : 750,
                                textAlign: "left",
                              }}
                            >
                              <span style={{ width: 18, textAlign: "center", opacity: active ? 1 : .8 }}>{icon}</span>
                              <span style={{ flex: 1, fontSize: 12 }}>{label}</span>
                              {badge > 0 && (
                                <span
                                  style={{
                                    minWidth: 20,
                                    padding: "2px 6px",
                                    borderRadius: 999,
                                    background: active ? "rgba(15,23,42,.2)" : "rgba(255,255,255,.14)",
                                    fontSize: 10,
                                    textAlign: "center",
                                  }}
                                >
                                  {badge}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </nav>

                      <div className="pms-sidebar-utilities">
                        <button
                          onClick={() => {
                            setNavMenuOpen(false);
                            setRoomSetupOpen(true);
                          }}
                          style={sidebarUtilityButton("#475569")}
                        >
                          ⚙ Room Setup
                        </button>

                        {currentFirebaseUserId === ADMIN_UID && (
                          <button
                            onClick={() => {
                              setNavMenuOpen(false);
                              handleResetBoard();
                            }}
                            style={sidebarUtilityButton("#334155")}
                          >
                            Reset Board
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setNavMenuOpen(false);
                            handleLogout();
                          }}
                          style={sidebarUtilityButton("#dc2626")}
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </header>

        <ManagerDashboardTabPanel
          id="room_board"
          activeTab={activeDashboardTab}
        >
          <div
            className="pms-stats-grid"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(135px, 1fr))",
              gap: 10,
              width: "100%",
            }}
          >
            <PmsStatCard label="All Rooms" value={totalBoardRooms} tone="#334155" background="#ffffff"  dark={roomOpsPalette.dark} />
            <PmsStatCard label="Ready" value={vacantRooms.length} tone="#15803d" background="#ecfdf5"  dark={roomOpsPalette.dark} />
            <PmsStatCard label="Occupied" value={occupiedRooms.length} tone="#1d4ed8" background="#eff6ff"  dark={roomOpsPalette.dark} />
            <PmsStatCard label="Dirty / Checkout" value={checkoutRooms.length} tone="#be123c" background="#fff1f2"  dark={roomOpsPalette.dark} />
            <PmsStatCard label="Service / Blocked" value={serviceRooms.length} tone="#6d28d9" background="#f5f3ff"  dark={roomOpsPalette.dark} />
            <button
              type="button"
              onClick={() => {
                setSelectedCheckInRoom("");
                setCheckInPrefillProfile(null);
                setCheckInPrefillName("");
                setQuickCheckInOpen(true);
              }}
              className="pms-stat-card"
              style={{
                minHeight: 72,
                padding: "11px 14px",
                borderRadius: 14,
                border: `1px solid ${roomOpsPalette.dark ? "#315f9f" : "#bfdbfe"}`,
                background: roomOpsPalette.dark
                  ? "linear-gradient(135deg,#10243f 0%,#142d4b 100%)"
                  : "#ffffff",
                color: roomOpsPalette.dark ? "#bfdbfe" : "#1d4ed8",
                cursor: "pointer",
                fontWeight: 950,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
              }}
            >
              <svg
                aria-hidden="true"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2.2" />
                <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 12, letterSpacing: ".03em" }}>QUICK CHECK-IN</span>
            </button>

            <button
              type="button"
              onClick={() => setServicePanelOpen(true)}
              className="pms-stat-card"
              style={{
                minHeight: 72,
                padding: "11px 14px",
                borderRadius: 14,
                border: `1px solid ${roomOpsPalette.dark ? "#80652b" : "#eadfc8"}`,
                background: roomOpsPalette.dark ? "linear-gradient(135deg,#31270f 0%,#3a2d11 100%)" : "linear-gradient(135deg,#fffdfa 0%,#fff9ef 100%)",
                color: roomOpsPalette.dark ? "#f2cf78" : "#8a6118",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: ".04em",
                  fontWeight: 950,
                  color: roomOpsPalette.dark ? "#f2cf78" : "#8a6118",
                }}
              >
                SERVICE ROOM
              </div>

              <div
                aria-hidden="true"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: roomOpsPalette.dark ? "rgba(242,207,120,.12)" : "#fbefd7",
                  border: `1px solid ${roomOpsPalette.dark ? "#80652b" : "#f1dfbb"}`,
                  color: roomOpsPalette.dark ? "#f2cf78" : "#8b641c",
                  flex: "0 0 auto",
                }}
              >
                <svg
                  width="27"
                  height="27"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11 27h24l-3 22H14L11 27Z" stroke="currentColor" strokeWidth="3.3" strokeLinejoin="round" />
                  <path d="M15 27c1.6-6.2 6.5-10 11-10 4.2 0 8.1 2.4 10 6.2" stroke="currentColor" strokeWidth="3.3" strokeLinecap="round" />
                  <path d="M16 32h18" stroke="currentColor" strokeWidth="3.3" strokeLinecap="round" />
                  <path d="M46 8v28" stroke="currentColor" strokeWidth="3.3" strokeLinecap="round" />
                  <path d="M40 38c0-4.1 2.7-7 6-7s6 2.9 6 7" stroke="currentColor" strokeWidth="3.3" strokeLinecap="round" />
                  <path d="M39 39l-2 12c4.8 3.6 11.6 5.1 18 2.8L53 39H39Z" stroke="currentColor" strokeWidth="3.3" strokeLinejoin="round" />
                  <path d="M42 42l-1 8M46 42v9M50 42l1 8" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
                </svg>
              </div>
            </button>
            <FoodOrdersPanel hotelId={hotelId} enabled={authReady && authAuthorized} stayRecords={latestStaysRef.current.hotelId === hotelId ? latestStaysRef.current.records : new Map()} dark={roomOpsPalette.dark} />
          </div>

          {boardLoading ? (
            <div style={{ marginTop: 18, padding: 30, textAlign: "center", background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", color: "#64748b" }}>Loading room board...</div>
          ) : (
            renderPmsFloorBoard()
          )}

        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="guest_crm"
          activeTab={activeDashboardTab}
        >
          <GuestHistoryPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="pending_payments"
          activeTab={activeDashboardTab}
        >
          <PendingPaymentsPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="bookings"
          activeTab={activeDashboardTab}
        >
          {hotelId !== JANAKPURI_HOTEL_ID && <AdvanceBookingsPanel hotelId={hotelId} bookings={bookings}
            onCheckIn={booking=>openGuestCheckIn(booking.room,{name:booking.name,booking})} />}
          {hotelId === JANAKPURI_HOTEL_ID && <JanakpuriBookingsPanel bookings={bookings}
            onCheckIn={(booking,claimId)=>openGuestCheckIn(booking.room,{name:booking.name,booking,claimId})} />}

          {(hotelId !== JANAKPURI_HOTEL_ID || bookings.some(b=>b.slotBookingVersion !== 1)) && <>
          <div style={{ marginTop: 8 }}>
            <h2 style={{ marginTop: 0 }}>
              Bookings for this Hotel
            </h2>

            <p
              style={{
                color: "#64748b",
                marginTop: 4,
              }}
            >
              All website bookings for this hotel.
            </p>
          </div>

        <div style={{ marginTop: 30 }}>
          <h3>Bookings for this Hotel</h3>

          {loading ? (
            <p>Loading...</p>
          ) : bookings.length === 0 ? (
            <p>No bookings found.</p>
          ) : (
            bookings.filter(booking => booking.slotBookingVersion !== 1 && booking.bookingAdvanceVersion !== 1).map((booking) => {
              const checkedIn =
                isCheckedIn(booking);

              const freeBooking =
                isFreeStay(booking);

              const finalPrice =
                booking.finalPrice ??
                booking.price ??
                0;

              return (
                <div
                  key={booking.id}
                  style={{
                    marginTop: 14,
                    padding: 15,
                    border:
                      "1px solid #e5e7eb",
                    borderRadius: 15,
                    background: "#fafafa",
                  }}
                >
                  <p>
                    <b>Name:</b>{" "}
                    {booking.name || "-"}
                  </p>

                  <p>
                    <b>Phone:</b>{" "}
                    {booking.phoneNumber ||
                      "-"}
                  </p>

                  <p>
                    <b>Date:</b>{" "}
                    {booking.date || "-"}
                  </p>

                  <p>
                    <b>Time:</b>{" "}
                    {booking.time || "-"}
                  </p>

                  <p>
                    <b>Plan:</b>{" "}
                    {booking.planTitle ||
                      "-"}{" "}
                    {booking.planHours
                      ? `• ${booking.planHours} hrs`
                      : ""}
                  </p>

                  <p>
                    <b>Price:</b>{" "}
                    {freeBooking ? (
                      <span
                        style={{
                          color: "#059669",
                          fontWeight: 900,
                        }}
                      >
                        FREE 🎁
                      </span>
                    ) : booking.spinUsed ? (
                      <>
                        <span
                          style={{
                            marginRight: 8,
                            color: "#777",
                            textDecoration:
                              "line-through",
                          }}
                        >
                          ₹
                          {booking.originalPrice ||
                            0}
                        </span>

                        <span
                          style={{
                            color: "#16a34a",
                            fontWeight: 900,
                          }}
                        >
                          ₹{finalPrice}
                        </span>
                      </>
                    ) : (
                      <strong>
                        ₹{finalPrice}
                      </strong>
                    )}
                  </p>

                  {booking.spinUsed && (
                    <div
                      style={{
                        marginTop: 9,
                        padding: 10,
                        maxWidth: 380,
                        borderRadius: 12,
                        background: "#f5f3ff",
                        border:
                          "1px solid #ddd6fe",
                      }}
                    >
                      <div
                        style={{
                          color: "#7c3aed",
                          fontWeight: 900,
                        }}
                      >
                        🎡 Spin Offer:{" "}
                        {booking.spinDiscountLabel ||
                          "-"}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                        }}
                      >
                        Discount: ₹
                        {booking.discountAmount ||
                          0}{" "}
                        • Final: ₹
                        {finalPrice}
                      </div>
                    </div>
                  )}

                  {booking.checkInCode &&
                    !checkedIn && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: 10,
                          maxWidth: 330,
                          borderRadius: 12,
                          background: "#fff7ed",
                          border:
                            "1px solid #fed7aa",
                          color: "#9a3412",
                          fontWeight: 800,
                        }}
                      >
                        🔐 Guest Check-In Code
                        Required
                      </div>
                    )}

                  {booking.checkInCodeVerified &&
                    checkedIn && (
                      <div
                        style={{
                          marginTop: 9,
                          color: "#15803d",
                          fontSize: 13,
                          fontWeight: 900,
                        }}
                      >
                        🔐 Check-In Code
                        Verified ✅
                      </div>
                    )}

                  {booking.bookingPointGiven && (
                    <div
                      style={{
                        marginTop: 8,
                        color: "#2563eb",
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      ⭐ Guest Booking Point
                      Given: +1
                    </div>
                  )}

                  {booking.referrerSpinGiven && (
                    <div
                      style={{
                        marginTop: 5,
                        color: "#7c3aed",
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      🎡 Referrer Spin Point
                      Given: +1
                    </div>
                  )}

                  <p
                    style={{
                      marginTop: 10,
                    }}
                  >
                    <b>Status:</b>{" "}

                    {checkedIn ? (
                      <span
                        style={{
                          color: "#15803d",
                          fontWeight: 900,
                        }}
                      >
                        Checked In ✅
                      </span>
                    ) : freeBooking ? (
                      <span
                        style={{
                          color: "#059669",
                          fontWeight: 900,
                        }}
                      >
                        Free Stay Confirmed 🎁
                      </span>
                    ) : booking.status ===
                      "pending" ? (
                      <span
                        style={{
                          color: "#f59e0b",
                          fontWeight: 900,
                        }}
                      >
                        Pending ⏳
                      </span>
                    ) : booking.status ===
                      "paid_requested" ? (
                      <span
                        style={{
                          color: "#059669",
                          fontWeight: 900,
                        }}
                      >
                        Payment Sent ✅
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#2563eb",
                          fontWeight: 900,
                        }}
                      >
                        {booking.status ||
                          "-"}
                      </span>
                    )}
                  </p>

                  {!checkedIn && (
                    <button
                      onClick={() =>
                        handleBookingCheckIn(
                          booking
                        )
                      }
                      disabled={
                        checkInLoadingId ===
                        booking.id
                      }
                      style={{
                        marginTop: 8,
                        padding:
                          "10px 15px",
                        border: "none",
                        borderRadius: 10,
                        background:
                          checkInLoadingId ===
                          booking.id
                            ? "#94a3b8"
                            : "#16a34a",
                        color: "#ffffff",
                        cursor:
                          checkInLoadingId ===
                          booking.id
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: 900,
                      }}
                    >
                      {checkInLoadingId ===
                      booking.id
                        ? "Verifying..."
                        : "Verify Code & Check In ✅"}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>


                  </>}
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="housekeeping"
          activeTab={activeDashboardTab}
        >
          <HousekeepingPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
            onOpenRoom={handleOpenHousekeepingRoom}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="maintenance"
          activeTab={activeDashboardTab}
        >
          <MaintenancePanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
            onSendRoomToCleaning={handleSendRoomToCleaning}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="booking_diary"
          activeTab={activeDashboardTab}
        >
          <BookingDiaryPanel
            hotelId={hotelId}
            hotelName={
              hotelName || hotelId
            }
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="day_book"
          activeTab={activeDashboardTab}
        >
          <DayBookPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="expenses"
          activeTab={activeDashboardTab}
        >
          <ExpensePanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="reports"
          activeTab={activeDashboardTab}
        >
          <ReportsPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
            totalRoomCount={
              new Set([...vacantRooms, ...occupiedRooms, ...checkoutRooms, ...serviceRooms].map(getRoomValue).map(normalizeRoom)).size
            }
          />
        </ManagerDashboardTabPanel>

        <ManagerDashboardTabPanel
          id="audit_logs"
          activeTab={activeDashboardTab}
        >
          <AuditLogPanel
            hotelId={hotelId}
            hotelName={hotelName || hotelId}
          />
        </ManagerDashboardTabPanel>
          </div>
        </main>
      </div>

      {selectedOccupiedRoom && (
        <OccupiedRoomModal
          stay={selectedOccupiedRoom}
          saving={roomActionSaving}
          hotelId={hotelId}
          hotelName={hotelName}
          onClose={() => !roomActionSaving && setSelectedOccupiedRoom(null)}
          onAddEntry={handleAddRoomEntry}
          onContinueStay={handleContinueStay}
          onCheckout={handleRoomCheckout}
          onRoomShift={handleRoomShift}
          onAddGuest={handleAddGuestToStay}
          availableRooms={vacantRooms}
        />
      )}

      {selectedCleaningRoom && (
        <RoomCleaningModal roomData={selectedCleaningRoom} hotelId={hotelId} hotelName={hotelName}
          saving={cleaningActionSaving} onClose={() => !cleaningActionSaving && setSelectedCleaningRoom(null)}
          onSaveProgress={handleSaveCleaningProgress} onMarkVacant={handleMarkRoomVacant}
          onVerifyLegacyEntry={handleVerifyLegacyCleaningEntry}
          onCreateMaintenance={handleCreateMaintenanceTicket} />
      )}

      {quickCheckInOpen && (
        <GuestCheckInModal
          room=""
          availableRooms={vacantRooms}
          roomSelectionMode
          hotelId={hotelId}
          hotelName={hotelName || hotelId}
          saving={walkInSaving}
          initialProfile={null}
          initialGuestName=""
          onQuickSaveName={async (name, roomValue) => {
            const targetRoom = normalizeRoom(roomValue);
            if (!targetRoom) {
              return alert("Pehle Ready Room select karo.");
            }

            if (!await quickOccupyRoom(targetRoom, name)) return;
            setQuickCheckInOpen(false);
            setCheckInPrefillProfile(null);
            setCheckInPrefillName("");
          }}
          onClose={() => {
            if (!walkInSaving) {
              setQuickCheckInOpen(false);
              setCheckInPrefillProfile(null);
              setCheckInPrefillName("");
            }
          }}
          onSave={handleWalkInCheckIn}
        />
      )}

      {selectedCheckInRoom && (
        <GuestCheckInModal
          room={selectedCheckInRoom}
          reservation={checkInRoomContext?.booking || null}
          hotelId={hotelId}
          hotelName={hotelName || hotelId}
          saving={walkInSaving}
          initialProfile={checkInPrefillProfile}
          initialGuestName={checkInPrefillName}
          onQuickSaveName={async (name) => {
            if (!await quickOccupyRoom(selectedCheckInRoom, name)) return;
            setSelectedCheckInRoom("");
            setCheckInPrefillProfile(null);
            setCheckInPrefillName("");
          }}
          onClose={() => {
            if (!walkInSaving) {
              setSelectedCheckInRoom("");
              setCheckInPrefillProfile(null);
              setCheckInPrefillName("");
            }
          }}
          onSave={data => handleWalkInCheckIn(data, checkInRoomContext)}
        />
      )}

      {checkoutSuccessData && (
        <CheckoutSuccessModal
          stay={checkoutSuccessData}
          hotelId={hotelId}
          hotelName={
            hotelName ||
            checkoutSuccessData.hotelName ||
            hotelId
          }
          onClose={() => setCheckoutSuccessData(null)}
        />
      )}
    </div>
  );
}

function PmsStatCard({ label, value, tone, background, dark = false }) {
  const displayTone = dark
    ? tone === "#15803d"
      ? "#6ee7b7"
      : tone === "#1d4ed8"
      ? "#93c5fd"
      : tone === "#be123c"
      ? "#fda4af"
      : tone === "#6d28d9"
      ? "#c4b5fd"
      : "#f8fafc"
    : tone;

  return (
    <div
      style={{
        padding: "11px 12px",
        minWidth: 0,
        minHeight: 72,
        borderRadius: 14,
        background: dark ? "rgba(10,24,40,.78)" : background,
        border: dark ? "1px solid rgba(148,163,184,.24)" : "1px solid #e2e8f0",
        boxShadow: "0 3px 10px rgba(15,23,42,.04)",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          lineHeight: 1.2,
          fontWeight: 900,
          letterSpacing: ".035em",
          textTransform: "uppercase",
          color: "#64748b",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 24,
          lineHeight: 1,
          fontWeight: 950,
          color: displayTone,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 99, background: color }} />{label}</span>;
}

function StatCard({
  label,
  value,
  background,
  color,
}) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 13,
        background,
        color,
        textAlign: "center",
        border:
          "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 25,
          fontWeight: 900,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function InputCard({
  title,
  children,
}) {
  return (
    <div
      style={{
        padding: 15,
        borderRadius: 16,
        background: "#f8fbff",
        border: "1px solid #e5e7eb",
        boxShadow:
          "0 4px 14px rgba(0,0,0,0.04)",
      }}
    >
      <h4 style={{ marginTop: 0 }}>
        {title}
      </h4>

      {children}
    </div>
  );
}

function getOccupiedDisplayBalance(
  item = {}
) {
  if (
    !item ||
    typeof item !== "object"
  ) {
    return 0;
  }

  const roomRent =
    Math.max(
      0,
      Number(
        item.tariff || 0
      )
    );

  const continuations =
    Array.isArray(
      item.continuations
    )
      ? item.continuations
      : [];

  const cnuTariffTotal =
    continuations.reduce(
      (sum, row) =>
        sum +
        Math.max(
          0,
          Number(
            row?.tariff || 0
          )
        ),
      0
    );

  const originalTariff =
    Math.max(
      0,
      roomRent -
        cnuTariffTotal
    );

  const checkInReceived =
    Math.max(
      0,
      Number(
        item.advance || 0
      )
    );

  const inferredExcess =
    Math.max(
      0,
      checkInReceived -
        originalTariff
    );

  const excessCredit =
    Math.max(
      0,
      Number(
        item.excessAdvanceCredit ??
          item.excessAdvanceReceived ??
          inferredExcess
      )
    );

  const storedPaid =
    Math.max(
      0,
      Number(
        item.paidTotal ??
          item.advance ??
          0
      )
    );

  const allocatedPaid =
    item.paidAllocationNormalized ===
    true
      ? storedPaid
      : Math.max(
          0,
          storedPaid -
            excessCredit
        );

  const gross =
    roomRent +
    Math.max(
      0,
      Number(
        item.foodTotal || 0
      )
    ) +
    Math.max(
      0,
      Number(
        item.laundryTotal || 0
      )
    ) +
    Math.max(
      0,
      Number(
        item.extraTotal || 0
      )
    );

  return Math.max(
    0,
    Math.round(
      (gross -
        allocatedPaid +
        Number.EPSILON) *
        100
    ) / 100
  );
}

function PendingDetailsTimer({
  startedAt,
}) {
  const [
    now,
    setNow,
  ] = useState(
    Date.now()
  );

  useEffect(() => {
    const timer =
      window.setInterval(
        () =>
          setNow(
            Date.now()
          ),
        1000
      );

    return () =>
      window.clearInterval(
        timer
      );
  }, []);

  const startMs =
    new Date(
      startedAt || ""
    ).getTime();

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return null;
  }

  const totalSeconds =
    Math.max(
      0,
      Math.floor(
        (now - startMs) /
          1000
      )
    );

  const hours =
    Math.floor(
      totalSeconds / 3600
    );

  const minutes =
    Math.floor(
      (totalSeconds %
        3600) /
        60
    );

  const seconds =
    totalSeconds % 60;

  const pad = (value) =>
    String(value).padStart(
      2,
      "0"
    );

  return (
    <div
      title="Room occupied / blocked since"
      style={{
        marginTop: 4,
        display:
          "inline-flex",
        alignItems:
          "center",
        gap: 4,
        padding:
          "3px 7px",
        borderRadius: 999,
        background:
          "#fef3c7",
        color: "#92400e",
        fontSize: 9,
        fontWeight: 900,
        fontVariantNumeric:
          "tabular-nums",
      }}
    >
      ⏱ {pad(hours)}:
      {pad(minutes)}:
      {pad(seconds)}
    </div>
  );
}

function CheckoutElapsedTimer({
  startedAt,
}) {
  const [
    now,
    setNow,
  ] = useState(
    Date.now()
  );

  useEffect(() => {
    const timer =
      window.setInterval(
        () => {
          setNow(
            Date.now()
          );
        },
        1000
      );

    return () =>
      window.clearInterval(
        timer
      );
  }, []);

  const startMs =
    new Date(
      startedAt
    ).getTime();

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return null;
  }

  const totalSeconds =
    Math.max(
      0,
      Math.floor(
        (now - startMs) /
          1000
      )
    );

  const hours =
    Math.floor(
      totalSeconds / 3600
    );

  const minutes =
    Math.floor(
      (totalSeconds % 3600) /
        60
    );

  const seconds =
    totalSeconds % 60;

  const pad = (value) =>
    String(value).padStart(
      2,
      "0"
    );

  return (
    <div
      title={`Checkout at ${new Date(
        startMs
      ).toLocaleString(
        "en-IN"
      )}`}
      style={{
        marginTop: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 7px",
        borderRadius: 999,
        background: "#dbeafe",
        color: "#1d4ed8",
        fontSize: 10,
        fontWeight: 900,
        fontVariantNumeric:
          "tabular-nums",
      }}
    >
      ⏱ {pad(hours)}:
      {pad(minutes)}:
      {pad(seconds)}
    </div>
  );
}

function BoardColumn({
  title,
  background,
  border,
  color,
  children,
}) {
  return (
    <div
      style={{
        minWidth: 0,
        padding: 14,
        borderRadius: 16,
        background,
        border: `1px solid ${border}`,
      }}
    >
      <h4
        style={{
          marginTop: 0,
          color,
        }}
      >
        {title}
      </h4>

      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 10,
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const fullButton = {
  width: "100%",
  marginTop: 10,
  padding: 10,
  border: "none",
  borderRadius: 10,
  color: "#ffffff",
  cursor: "pointer",
  fontWeight: 800,
};

const pmsTileButton = (background) => ({
  flex: "1 1 70px",
  minWidth: 0,
  minHeight: 32,
  padding: "6px 6px",
  border: "none",
  borderRadius: 8,
  background,
  color: "#ffffff",
  cursor: "pointer",
  fontSize: 10,
  lineHeight: 1.12,
  fontWeight: 900,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textAlign: "center",
  boxSizing: "border-box",
});

const pmsDropdownStyle = {
  minWidth: 185,
  flex: "0 1 225px",
  padding: "12px 14px",
  borderRadius: 14,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxSizing: "border-box",
  position: "relative",
  zIndex: 30,
};

const pmsDropdownSummaryStyle = {
  cursor: "pointer",
  fontWeight: 900,
  fontSize: 12,
  whiteSpace: "nowrap",
};

const pmsDropdownBodyStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  marginTop: 0,
  width: 300,
  maxWidth: "min(82vw, 340px)",
  display: "grid",
  gap: 10,
  padding: 10,
  borderRadius: 16,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 16px 36px rgba(15,23,42,.16)",
  boxSizing: "border-box",
  zIndex: 200,
};

const serviceMiniButton = (background) => ({
  padding: "7px 9px",
  border: "none",
  borderRadius: 8,
  background,
  color: "#ffffff",
  cursor: "pointer",
  fontSize: 10,
  fontWeight: 900,
  whiteSpace: "nowrap",
});

const sidebarUtilityButton = (background) => ({
  width: "100%",
  padding: "9px 10px",
  border: "1px solid rgba(255,255,255,.12)",
  borderRadius: 9,
  background,
  color: "#ffffff",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 850,
});

const headerButton = (background) => ({
  padding: "10px 14px",
  border: "none",
  borderRadius: 10,
  background,
  color: "#ffffff",
  cursor: "pointer",
  fontWeight: 800,
});

const actionButton = (background) => ({
  padding: "7px 10px",
  border: "none",
  borderRadius: 8,
  background,
  color: "#ffffff",
  cursor: "pointer",
  fontWeight: 800,
  whiteSpace: "nowrap",
});

export default HotelManagerDashboard;
